<template>
    <div class="page-gutter" style="margin-top: 10px; margin-bottom: 40px">
        <!-- IA reorg: the "Storalytic Intelligence Flow" bar is retired from the
             app (it duplicated the tab row and its orienting purpose is gone).
             Kept in the codebase for the sales deck only, per spec §5. -->

        <!-- IA reorg: the above-tabs hero (ReviewNarrativeCard) is retired. Today
             owns Allen's narrative (incl. the store-trend prose it used to carry,
             spec §3.1); the trend NUMBER lives in Explore's at-a-glance strip.
             The period + opening-hours picker moved OUT of the global chrome and
             INTO the analytical tabs (Explore/Zones) — see the conditional
             ControlHead just under the tab nav. Actions + Today are fixed 30d. -->

        <!-- Tabs -->
        <MDBTabs v-model="activeTab">
            <MDBTabNav tabsClasses="mb-3 stora-tabs-inkbar" style="padding-top: 20px;">
                <!-- IA reorg: Actions · Zones · Explore — a drill-down flow (what to do
                     → why/where → raw numbers). Actions is the default landing tab; Explore
                     is the de-emphasised reference surface (spec §2). No tier gating —
                     everyone gets everything. -->
                <MDBTabItem tabId="actions-layer">
                    <i class="bi bi-list-check tab-icon"></i><span>{{ $t('explore.tabs.actions') }}</span>
                </MDBTabItem>

                <MDBTabItem tabId="intelligence-layer">
                    <i class="bi bi-grid-3x3-gap tab-icon"></i><span>{{ $t('explore.tabs.zones') }}</span>
                </MDBTabItem>

                <MDBTabItem tabId="data-foundation-layer">
                    <i class="bi bi-compass tab-icon"></i><span>{{ $t('explore.tabs.explore') }}</span>
                </MDBTabItem>
            </MDBTabNav>

            <!-- Shared analytical picker — shown ONLY on Explore + Zones (the tabs
                 whose data it drives). Hidden on Actions (fixed 30-day basis). -->
            <ControlHead
                v-if="activeTab === 'data-foundation-layer' || activeTab === 'intelligence-layer'"
                class="in-tab-controls"
            >
                <duration-dropdown
                    :dateRangeMode="dateRangeMode"
                    :selectedDuration="selectedDuration"
                    @updateDuration="handleUpdateDuration">
                </duration-dropdown>
                <dropdown-opening-hours-mode
                    :openingHoursMode="selectedOpeningHoursMode"
                    :disabledModes="disabledOpeningHourModes"
                    @updateOpeningHoursMode="handleUpdateOpeningHoursMode">
                </dropdown-opening-hours-mode>
            </ControlHead>
            <!-- Zone identity 6.7: the opening hours were changed today. -->
            <ChangeNotice
                v-if="(activeTab === 'data-foundation-layer' || activeTab === 'intelligence-layer')
                      && settingChanged(changesToday.register, 'opening_hours')"
                :compact="true" :text="NOTICES.settings.opening_hours" />

            <MDBTabContent>
                <!-- PRIORITIZATION pane retired in the IA reorg: the climb gauges
                     moved to Today's Store health, the opportunity ranking to
                     Actions, and the per-zone deep analytics to Zones. -->

                <!-- INTELLIGENCE -->
                <!-- ZONES (was Intelligence) — cross-zone map + per-zone deep dive. -->
                <MDBTabPane tabId="intelligence-layer">
                    <!-- 6.7: the € in the Zones tab follow a money-settings change tonight. -->
                    <ChangeNotice v-if="activeTab === 'intelligence-layer' && settingChanged(changesToday.register, 'money')"
                        :compact="true" :text="NOTICES.settings.money" />
                    <!-- Weather signature tile (curated; self-hides unless the store has
                         a measured store-level signal). Jumps to the Explore deep-dive. -->
                    <WeatherSignatureTile
                        v-if="activeTab === 'intelligence-layer'"
                        :signature="weatherSignature"
                        :store-name="storeName"
                        @open="activeTab = 'data-foundation-layer'; exploreSubTab = 'sec-weather'"
                    />
                    <div v-if="activeTab==='intelligence-layer'" :class="{ 'group-disabled': !isProductZonePresent() }">
                        <div class="card-container-product-intelligence">
                            <!-- Zones overview: the strategic-positioning quadrant (the
                                 cross-zone map) on the left, and a role KEY on the right
                                 that fills the space + makes the map actionable. Pattern
                                 matrix dropped (its signal is on the zone cards). -->
                            <div class="zones-overview">
                                <div class="zov-chart">
                                    <StrategicPositioningChart
                                        :fixed-widgets-data="fixedWidgetsData"
                                        :is-active="activeTab === 'intelligence-layer'"
                                        :segment-key="companySector"
                                        :zone-overrides="zoneOverrides"
                                        :hide-legend="true"
                                    />
                                </div>
                                <aside class="zov-roles">
                                    <div class="zov-roles-title">{{ $t('explore.roles.title') }}</div>
                                    <div v-for="r in zoneRolesGrouped" :key="r.label" class="role-group">
                                        <div class="role-head" :style="{ color: r.color }">
                                            <span class="role-ico">{{ r.icon }}</span>{{ r.displayLabel }}
                                            <span class="role-count">{{ r.zones.length }}</span>
                                        </div>
                                        <div class="role-meaning">{{ r.meaning }}</div>
                                        <div class="role-zones">
                                            <button
                                                v-for="z in r.zones"
                                                :key="z.name"
                                                type="button"
                                                class="role-zonerow"
                                                :class="{ active: (zoneFocus || heroFocusedZone) === z.name }"
                                                :style="{ borderLeftColor: r.color }"
                                                @click="zoneFocus = z.name"
                                            >
                                                <span class="rz-top">
                                                    <span class="rz-name">{{ z.name }}</span>
                                                    <span v-if="z.eurText" class="rz-eur">{{ z.eurText }}</span>
                                                    <i class="bi bi-chevron-right rz-caret"></i>
                                                </span>
                                                <span class="rz-pattern" :style="{ background: z.patternBg, color: z.patternColor }">{{ z.patternIcon }} {{ z.patternChip }}</span>
                                                <span class="rz-stat">{{ $t('explore.roles.stat', { eng: z.engPct, conv: z.convPct, n: fmt(z.visitors) }) }}</span>
                                            </button>
                                        </div>
                                    </div>
                                </aside>
                            </div>
                            <!-- Funnel only — the role cards on the right are the zone
                                 browser (they carry the grid's info now), so the grid is
                                 retired. Clicking a role card sets zoneFocus → this funnel. -->
                            <product-zone-deep-dive
                                browser-mode="none"
                                :priorities="topPriorities"
                                :store-total-visitors="storeTotalVisitors"
                                :focused-zone="zoneFocus || heroFocusedZone"
                                :tripwire-dwell-map="tripwireDwellMap"
                                :segment-key="companySector"
                                :zone-overrides="zoneOverrides"
                            />
                        </div>
                    </div>
                </MDBTabPane>
                
                <!-- DATA FOUNDATION -->
                <MDBTabPane tabId="data-foundation-layer">
                    <div v-if="activeTab==='data-foundation-layer'" class="data-foundation-pane">

                        <!-- Explore at-a-glance: one headline per PRESENT domain (store +
                             checkout + product), so the strip summarises the whole tab. No
                             title/subtitle — the tab names it and the UI is self-evident.
                             Product/Service are comparison domains; their headline is the
                             in-section chart, not a scalar. -->
                        <div class="explore-glance" :aria-label="$t('explore.glance.aria')">
                            <div v-if="isEntranceZonePresent() || isCombinedEntranceExitZonePresent()" class="eg-tile">
                                <span class="eg-val">{{ fmt(storeTotalVisitors) }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.visitors') }}</span>
                            </div>
                            <div v-if="(isEntranceZonePresent() || isCombinedEntranceExitZonePresent()) && averageVisitDuration" class="eg-tile">
                                <span class="eg-val">{{ formattedDurationTime(averageVisitDuration) }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.visitLength') }}</span>
                            </div>
                            <div v-if="(isEntranceZonePresent() || isCombinedEntranceExitZonePresent()) && formattedBusiestHour" class="eg-tile">
                                <span class="eg-val">{{ formattedBusiestHour }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.busiestHour') }}</span>
                            </div>
                            <div v-if="isCheckoutWaitingZonePresent() && mostTypicalWaitingTime" class="eg-tile">
                                <span class="eg-val">{{ formattedDurationTime(mostTypicalWaitingTime) }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.typicalWait') }}</span>
                            </div>
                            <div v-if="isCheckoutInteractionZonePresent() && mostTypicalInteractionTime" class="eg-tile">
                                <span class="eg-val">{{ formattedDurationTime(mostTypicalInteractionTime) }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.typicalCheckout') }}</span>
                            </div>
                            <!-- Sits with the checkout figures it belongs to, not at the
                                 end of the strip. Absent entirely when no checkout has a
                                 limit set or none was exceeded — no zero, no empty state. -->
                            <div v-if="checkoutUnattendedRate" class="eg-tile">
                                <span class="eg-val">{{ $t('explore.glance.oneIn', { n: checkoutUnattendedRate }) }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.emptyCheckout') }}</span>
                            </div>
                            <div v-if="exploreEurPerVisitor != null" class="eg-tile">
                                <span class="eg-val">{{ eurPerVisitorText }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.eurPerVisitor') }}</span>
                            </div>
                            <div v-if="exploreEngagedPct != null" class="eg-tile">
                                <span class="eg-val">{{ pctRaw(Math.round(exploreEngagedPct * 100)) }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.engaged') }}</span>
                            </div>
                            <div v-if="trendInfo" class="eg-tile">
                                <span class="eg-val" :style="{ color: trendInfo.color || '#2b3a46' }">{{ trendInfo.short }}</span>
                                <span class="eg-lbl">{{ $t('explore.glance.visitorTrend') }}</span>
                            </div>
                        </div>

                        <ZoneTypeNav
                            :items="zoneNavItems"
                            :active="exploreSubTab"
                            @navigate="exploreSubTab = $event"
                        />

                        <!-- Small title for the active sub-tab so the charts aren't naked
                             (the section headers themselves are hidden — see :hide-head). -->
                        <h3 v-if="activeZoneItem" class="explore-subtitle">
                            <i :class="['bi', activeZoneItem.icon]" :style="{ color: activeZoneItem.accent }"></i>
                            {{ activeZoneItem.title }}
                        </h3>

                        <Section
                            id="sec-store-visitors"
                            v-show="exploreSubTab === 'sec-store-visitors'"
                            :title="$t('explore.section.storeVisitors')"
                            icon="bi-shop"
                                                       accent="var(--stora-green)"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.4: a zone changed type to or from an entrance type
                                 today — store footfall itself moves tonight. -->
                            <ChangeNotice v-if="entranceTypeChange(changesToday.register)"
                                :text="NOTICES.storeValues" />
                            <template #summary>
                                <span v-if="zoneStats.entrance.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.entrance.polygon) }}
                                </span>
                                <span v-if="zoneStats.entrance.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.entrance.tripwire) }}
                                </span>
                            </template>

                            <div
                                v-if="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                class="section-empty-state"
                            >
                                <i class="bi bi-door-open empty-ico"></i>
                                <div class="empty-text">
                                    <strong>{{ $t('explore.empty.noEntrance') }}</strong>
                                    <span>{{ $t('explore.empty.noEntranceHint') }}</span>
                                </div>
                            </div>

                            <template v-else>
                            <ChartGrid>
                                <ChartPanel
                                    :title="$t('explore.chart.weekdayHour')"
                                    icon="bi-grid-3x3-gap"
                                    :height="400"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="!heatmapData?.heatmap_matrix || !heatmapData.heatmap_matrix.flat().some(v => v > 0)"
                                    accent="#36910d"
                                >
                                    <heatmap-dashboard
                                    :heatmapData="heatmapData"
                                    :selectedDuration="selectedDuration"
                                    :is-active="activeTab === 'data-foundation-layer'"
                                    :height="340"
                                    />
                                </ChartPanel>

                                <ChartPanel
                                    :title="$t('explore.chart.visitorsTrend')"
                                    icon="bi-activity"
                                    :height="400"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="!entranceChartData || !Object.keys(entranceChartData).length"
                                    accent="#36910d"
                                >
                                    <charts-dashboard-entrance
                                    :chartData="entranceChartData"
                                    :selectedDuration="selectedDuration"
                                    :is-active="activeTab === 'data-foundation-layer'"
                                    :height="340"
                                    />
                                </ChartPanel>
                            </ChartGrid>

                            <KpiGrid style="padding-top:20px">
                                <!-- Total Visitors -->
                                <KpiCard
                                    :label="$t('explore.kpi.totalVisitors')"
                                    :value="fmt(storeTotalVisitors)"
                                    icon="bi-people"
                                    accent="#36910d"
                                    :loading="false"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(storeTotalVisitors)"
                                >
                                    <template #badge>
                                        <span v-if="storeDataIntegrity"
                                              class="integrity-badge"
                                              :title="$t('explore.integrity.title', { detail: storeDataIntegrity.detail })">
                                            <i class="bi bi-exclamation-triangle-fill"></i> {{ $t('explore.integrity.badge') }}
                                        </span>
                                    </template>
                                </KpiCard>

                                <!-- Avg / Day -->
                                <KpiCard
                                    :label="$t('explore.kpi.avgPerDay')"
                                    :value="fmt(averageValue)"
                                    :suffix="$t('explore.kpi.avgSuffix')"
                                    icon="bi-graph-up"
                                    accent="#36910d"
                                    :loading="isLoadingAverage"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(averageValue)"
                                >
                                    <template #meta>{{ $t('explore.meta.overDays', effectiveDurationDays) }}</template>
                                </KpiCard>

                                <!-- <Weekday>'s Average Visitors -->
                                <KpiCard
                                    :label="weekdayAverageLabel"
                                    :value="fmt(averageValueSameWeekday)"
                                    icon="bi-calendar-week"
                                    accent="#36910d"
                                    :loading="isLoadingAverageSameWeekday"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(averageValueSameWeekday)"
                                />

                                <!-- Visitor Trend (custom value slot, but same typography) -->
                                <KpiCard
                                    :label="$t('explore.kpi.visitorTrend')"
                                    :icon="trendInfo?.icon || 'bi-activity'"
                                    :accent="trendInfo?.color || '#999'"
                                    size="sm"
                                    :loading="isLoadingTrend"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="!trendInfo"
                                >
                                    <!-- single bold word, smaller -->
                                    <template #value>
                                        <span class="trend-short">{{ trendInfo?.short }}</span>
                                    </template>

                                    <!-- subdued, one line if possible -->
                                    <template #meta>
                                        <span class="trend-sentence">{{ trendText(trendInfo?.sentence) }}</span>
                                    </template>
                                </KpiCard>

                                <!-- Busiest Hour (avg) -->
                                <KpiCard
                                    :label="$t('explore.kpi.busiestHourAvg')"
                                    :value="formattedBusiestHour"
                                    icon="bi-clock-history"
                                    accent="#36910d"
                                    :loading="isLoadingBusiestHour"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(busiestHour)"
                                    :muted="!highestAverageHourlyCount"
                                >
                                    <template #meta>{{ $t('explore.meta.visitors', { n: fmt(highestAverageHourlyCount) }) }}</template>
                                </KpiCard>

                                <!-- Top Day -->
                                <KpiCard
                                    :label="$t('explore.kpi.bestDay')"
                                    :value="formattedHighestTotalDay?.formattedDate"
                                    icon="bi-calendar3"
                                    accent="#36910d"
                                    :loading="isLoadingHighestDay"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(formattedHighestTotalDay?.formattedDate)"
                                >
                                    <template #meta>
                                        <strong>{{ fmt(highestTotalDayCount) }}</strong> {{ $t('explore.meta.visitorsWord') }}
                                        <span v-if="bestDayLiftPct != null" class="best-day-lift">
                                            · {{ $t('explore.meta.vsDailyAvg', { sign: bestDayLiftPct >= 0 ? '+' : '', pct: bestDayLiftPct }) }}
                                        </span>
                                    </template>
                                </KpiCard>

                                <!-- Top Visitor Hour -->
                                <KpiCard
                                    :label="$t('explore.kpi.topVisitorHour')"
                                    icon="bi-clock"
                                    accent="#36910d"
                                    :loading="isLoadingHighestHour"
                                    :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(formattedHighestTotalHour?.formattedDate)"
                                >
                                    <template #value>
                                    {{ formattedHighestTotalHour?.formattedDate }} | {{ formattedHighestTotalHour?.formattedTime }}
                                    </template>
                                    <template #meta>{{ $t('explore.meta.visitors', { n: fmt(highestTotalHourCount) }) }}</template>
                                </KpiCard>

                                <!-- Average Visit Duration (uses same states; custom empty text when exits missing) -->
                                <!-- value via slot for full control -->
                                <KpiCard
                                    v-if="!isCardEmpty(averageVisitDuration)"
                                    :label="$t('explore.kpi.avgVisitDuration')"
                                    icon="bi-stopwatch"
                                    accent="#36910d"
                                    :value="''"
                                    :loading="isLoadingleastUsedCheckout"
                                    :noZones="!isExitZonePresent() && !isCombinedEntranceExitZonePresent()"
                                    :empty="isCardEmpty(averageVisitDuration)"
                                    :noZonesText="$t('explore.noZones.exit')"
                                >
                                    <template #value>
                                    <!-- If you have a formatted duration, show it here; otherwise fallback -->
                                    <span v-if="averageVisitDuration">
                                        {{ formattedDurationTime(averageVisitDuration) }}
                                    </span>
                                    <span v-else>{{ $t('explore.kpi.noExitCamera') }}</span>
                                    </template>
                                </KpiCard>

                                <!-- Est. Average Store Dwell — single Combined EE tripwire only.
                                     Backend only populates this when there is exactly one active
                                     entry/exit zone, it is a Combined EE with tripwires, and the
                                     zone has UseTripwireForDwell=true. In that configuration the
                                     per-zone Little's-Law W = L/λ result IS the store dwell, so
                                     it's safe to surface here. -->
                                <KpiCard
                                    v-if="storeDwellEstimateSeconds > 0"
                                    :label="$t('explore.kpi.storeVisitLengthEst')"
                                    icon="bi-stopwatch"
                                    accent="#36910d"
                                    tripwire
                                >
                                    <template #value>
                                        {{ formattedDurationTime(storeDwellEstimateSeconds) }}
                                        <span v-if="storeDwellConfidence"
                                              :class="['conf-chip', `conf-${storeDwellConfidence}`]">{{ confidenceLabel(storeDwellConfidence) }}</span>
                                        <span v-if="biasChipForValue(storeDwellBiasPct)"
                                              :class="biasChipForValue(storeDwellBiasPct).cls"
>{{ biasChipForValue(storeDwellBiasPct).label }}</span>
                                    </template>
                                    <template #meta>{{ $t('explore.meta.estFromCrossings') }}</template>
                                </KpiCard>
                            </KpiGrid>
                            </template>
                        </Section>

                        <!-- Checkout Waiting Zone Data -->
                        <Section
                            v-if="isCheckoutWaitingZonePresent()"
                            id="sec-checkout-waiting"
                            v-show="exploreSubTab === 'sec-checkout-waiting'"
                            :title="$t('explore.section.checkoutWaiting')"
                            icon="bi-hourglass-split"
                                                       accent="#AD3935"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([4])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <template #summary>
                                <span v-if="zoneStats.waiting.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.waiting.polygon) }}
                                </span>
                                <span v-if="zoneStats.waiting.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.waiting.tripwire) }}
                                </span>
                                <span v-if="!zoneStats.waiting.polygon && !zoneStats.waiting.tripwire">{{ $t('explore.summary.noWaitingZones') }}</span>
                            </template>
                            <KpiGrid style="padding-top:20px">
                                <!-- Checkout Queue — leads the section; only renders
                                     when the backend ships checkout_queue. -->
                                <KpiCard
                                    v-if="checkoutQueue"
                                    :label="$t('explore.kpi.checkoutQueue')"
                                    icon="bi-people"
                                    accent="#AD3935"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="!checkoutQueue"
                                >
                                    <template #value>{{ queueVerdict }}</template>
                                    <template #meta>{{ $t('explore.meta.queueShare', { pct: Math.round(queuePct) }) }}</template>
                                </KpiCard>

                                <!-- Average Waiting Time -->
                                <KpiCard
                                    :label="$t('explore.kpi.avgWait')"
                                    icon="bi-stopwatch"
                                    accent="#AD3935"
                                    :loading="isLoadingaverageWaitingTime"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="isCardEmpty(averageWaitingTime)"
                                >
                                    <template #value>{{ averageWaitingTime ? formattedDurationTime(averageWaitingTime) : '' }}</template>
                                    <template #meta>{{ $t('explore.meta.realWaits', { n: realWaitCount }) }}</template>
                                </KpiCard>

                                <!-- Typical Waiting Time -->
                                <KpiCard
                                    :label="$t('explore.kpi.typicalWait')"
                                    icon="bi-activity"
                                    accent="#AD3935"
                                    :loading="isLoadingmostTypicalWaitingTime"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="isCardEmpty(mostTypicalWaitingTime)"
                                >
                                    <template #value>
                                        {{ mostTypicalWaitingTime ? formattedDurationTime(mostTypicalWaitingTime) : '' }}
                                    </template>
                                    <template #meta>
                                        {{ $t('explore.meta.realWaits', { n: realWaitCount }) }}
                                    </template>
                                </KpiCard>

                                <!-- Highest Waiting Time -->
                                <KpiCard
                                    :label="$t('explore.kpi.longestWait')"
                                    icon="bi-graph-up-arrow"
                                    accent="#AD3935"
                                    :loading="isLoadingmaxWaitingDuration"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="isCardEmpty(maxWaitingDuration)"
                                >
                                    <template #value>
                                        {{ maxWaitingDuration ? formattedDurationTime(maxWaitingDuration) : '' }}
                                    </template>
                                    <template #meta>
                                        {{ $t('explore.meta.atZone', { zone: maxWaitingZoneName }) }}
                                    </template>
                                </KpiCard>

                                <!-- Real Wait Rate -->
                                <KpiCard
                                    :label="$t('explore.kpi.pausedAtCheckout')"
                                    icon="bi-people"
                                    accent="#AD3935"
                                    :loading="isLoadingRealWaitRate"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="isCardEmpty(realWaitRate)"
                                >
                                    <template #value>{{ pctRaw(realWaitRate) }}</template>
                                    <template #meta>{{ $t('explore.meta.sharePaused') }}</template>
                                </KpiCard>

                                <!-- Average Time in Zone -->
                                <KpiCard
                                    :label="$t('explore.kpi.avgTimeInZone')"
                                    icon="bi-clock-history"
                                    :value="averageTimeInZoneWaiting ? formattedDurationTime(averageTimeInZoneWaiting) : ''"
                                    accent="#AD3935"
                                    :loading="isLoadingaverageTimeInZoneWaiting"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="isCardEmpty(averageTimeInZoneWaiting)"
                                />

                                <!-- Fastest Waiting Line -->
                                <KpiCard
                                    :label="$t('explore.kpi.fastestLine')"
                                    icon="bi-speedometer2"
                                    accent="#AD3935"
                                    :loading="isLoadingfastestCheckoutZone"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="isCardEmpty(fastestCheckoutZone)"
                                >
                                    <template #value>
                                        {{ fastestCheckoutZone }}
                                    </template>
                                </KpiCard>

                                <!-- Slowest Waiting Line -->
                                <KpiCard
                                    :label="$t('explore.kpi.slowestLine')"
                                    icon="bi-hourglass-split"
                                    accent="#AD3935"
                                    :loading="isLoadingslowestCheckoutZone"
                                    :noZones="!isCheckoutWaitingZonePresent()"
                                    :noZonesText="$t('explore.noZones.waiting')"
                                    :empty="isCardEmpty(slowestCheckoutZone)"
                                >
                                    <template #value>
                                        {{ slowestCheckoutZone }}
                                    </template>
                                </KpiCard>
                            </KpiGrid>
                        </Section>

                        <!-- Checkout Interaction Zone Intelligence -->
                        <Section
                            v-if="isCheckoutInteractionZonePresent()"
                            id="sec-checkout-interaction"
                            v-show="exploreSubTab === 'sec-checkout-interaction'"
                            :title="$t('explore.section.checkoutInteraction')"
                            icon="bi-credit-card-2-front"
                                                       accent="#D3A741"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([5, 999])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <!-- 6.7: an alert threshold of a zone here was changed today. -->
                            <ChangeNotice v-if="thresholdChangedIn([5, 999])" :text="NOTICES.settings.thresholds" />
                            <template #summary>
                                <span v-if="zoneStats.interaction.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.interaction.polygon) }}
                                </span>
                                <span v-if="zoneStats.interaction.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.interaction.tripwire) }}
                                </span>
                                <span v-if="!zoneStats.interaction.polygon && !zoneStats.interaction.tripwire">{{ $t('explore.summary.noInteractionZones') }}</span>
                            </template>
                            <KpiGrid style="padding-top:20px">
                                <!-- Average Interaction Time -->
                                <KpiCard
                                    :label="$t('explore.kpi.avgInteraction')"
                                    icon="bi-stopwatch"
                                    accent="#D3A741"
                                    :loading="isLoadingaverageInteractionTime"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(averageInteractionTime)"
                                >
                                    <template #value>{{ averageInteractionTime ? formattedDurationTime(averageInteractionTime) : '' }}</template>
                                    <template #meta>{{ $t('explore.meta.realTransactions', { n: realTransactionCount }) }}</template>
                                </KpiCard>

                                <!-- Typical Interaction Time -->
                                <KpiCard
                                    :label="$t('explore.kpi.typicalInteraction')"
                                    icon="bi-activity"
                                    accent="#D3A741"
                                    :loading="isLoadingmostTypicalInteractionTime"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(mostTypicalInteractionTime)"
                                >
                                    <template #value>{{ mostTypicalInteractionTime ? formattedDurationTime(mostTypicalInteractionTime) : '' }}</template>
                                    <template #meta>
                                        {{ $t('explore.meta.realTransactions', { n: realTransactionCount }) }}
                                    </template>
                                </KpiCard>

                                <!-- Highest Interaction Time -->
                                <KpiCard
                                    :label="$t('explore.kpi.longestInteraction')"
                                    icon="bi-graph-up-arrow"
                                    accent="#D3A741"
                                    :loading="isLoadingmaxInteractionDuration"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(maxInteractionDuration)"
                                >
                                    <template #value>{{ maxInteractionDuration ? formattedDurationTime(maxInteractionDuration) : '' }}</template>
                                    <template #meta>{{ $t('explore.meta.atZone', { zone: maxInteractionZoneName }) }}</template>
                                </KpiCard>

                                <!-- Real Transaction Rate -->
                                <KpiCard
                                    :label="$t('explore.kpi.realTransactionRate')"
                                    icon="bi-people"
                                    accent="#D3A741"
                                    :loading="isLoadingRealTransactionRate"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(realTransactionRate)"
                                >
                                    <template #value>{{ pctRaw(realTransactionRate) }}</template>
                                    <template #meta>{{ $t('explore.meta.transacted') }}</template>
                                </KpiCard>

                                <!-- Counter unattended (docs §28.6): customers who reached a counter
                                     with nobody behind it. Sits with the other outcome figures rather
                                     than with the durations — it is a count of people, not a time. -->
                                <CounterUnattendedCard
                                    v-if="countersByService.checkout.length"
                                    :counters="countersByService.checkout"
                                    accent="#D3A741"
                                />

                                <!-- Average Time in Zone -->
                                <KpiCard
                                    :label="$t('explore.kpi.avgTimeInZone')"
                                    icon="bi-clock-history"
                                    :value="averageTimeInZoneInteraction ? formattedDurationTime(averageTimeInZoneInteraction) : ''"
                                    accent="#D3A741"
                                    :loading="isLoadingaverageTimeInZoneInteraction"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(averageTimeInZoneInteraction)"
                                />

                                <!-- Fastest Interaction Checkout -->
                                <KpiCard
                                    :label="$t('explore.kpi.fastestCheckout')"
                                    icon="bi-speedometer2"
                                    accent="#D3A741"
                                    :loading="isLoadingfastestInteractionZone"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(fastestInteractionZone)"
                                >
                                    <template #value>{{ fastestInteractionZone }}</template>
                                </KpiCard>

                                <!-- Slowest Interaction Checkout -->
                                <KpiCard
                                    :label="$t('explore.kpi.slowestCheckout')"
                                    icon="bi-hourglass-split"
                                    accent="#D3A741"
                                    :loading="isLoadingslowestInteractionZone"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(slowestInteractionZone)"
                                >
                                    <template #value>{{ slowestInteractionZone }}</template>
                                </KpiCard>
                            
                                <!-- Most Used Checkout -->
                                <KpiCard
                                    :label="$t('explore.kpi.mostUsedCheckout')"
                                    icon="bi-check2-circle"
                                    accent="#D3A741"
                                    :loading="isLoadingmostUsedCheckout"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(mostUsedCheckout?.zone_name)"
                                >
                                    <template #value>{{ mostUsedCheckout?.zone_name }}</template>
                                    <template #meta>{{ $t('explore.meta.interactions', { n: fmt(mostUsedCheckout?.visitor_count) }) }}</template>
                                </KpiCard>

                                <!-- Least Used Checkout -->
                                <KpiCard
                                    :label="$t('explore.kpi.leastUsedCheckout')"
                                    icon="bi-dash-circle"
                                    accent="#D3A741"
                                    :loading="isLoadingleastUsedCheckout"
                                    :noZones="!isCheckoutInteractionZonePresent()"
                                    :noZonesText="$t('explore.noZones.interaction')"
                                    :empty="isCardEmpty(leastUsedCheckout?.zone_name)"
                                >
                                    <template #value>{{ leastUsedCheckout?.zone_name }}</template>
                                    <template #meta>{{ $t('explore.meta.interactions', { n: fmt(leastUsedCheckout?.visitor_count) }) }}</template>
                                </KpiCard>
                            </KpiGrid>
                            <!-- Throughput — which till gets people through, and when
                                 each one is working. "Real" transactions are the
                                 pipeline's own definition (clear lingerers at the
                                 interaction zone), not raw visits: a few seconds at a
                                 till is somebody walking past. -->
                            <CheckoutTransactionsPanel
                              :data="checkoutTransactions"
                              accent="#D3A741"
                            />


                            <!-- Counter unattended: somebody stood here and
                                 nobody was behind the counter. Different from
                                 queueing, which is the waiting zone. docs §28.6 -->
                            <CounterUnattendedPanel
                                v-if="countersByService.checkout.length"
                                :counters="countersByService.checkout"
                                accent="#D3A741"
                            />
                        </Section>

                        <!-- Product Zone Intelligence -->
                        <Section
                            v-if="showProductSection"
                            id="sec-product"
                            v-show="exploreSubTab === 'sec-product'"
                            :title="$t('explore.section.product')"
                            icon="bi-tag"
                                                       accent="#004F6E"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([6])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <template #summary>
                                <span v-if="zoneStats.product.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.product.polygon) }}
                                </span>
                                <span v-if="zoneStats.product.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.product.tripwire) }}
                                </span>
                            </template>
                            <!-- ========================= POLYGON SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasPolygonProductZones"
                                :label="$t('explore.sub.polygon')"
                                :countText="$t('explore.count.zones', zoneStats.product.polygon)"
                                icon="bi-bounding-box"
                                accent="#004F6E"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#004F6E"
                                        :loading="isLoadingbestProductZoneInAvgDuration"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('explore.noZones.product')"
                                        :empty="!(bestProductZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestProductZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.avg', { d: formattedDurationTime(bestProductZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.topVisits')" icon="bi-people" accent="#004F6E"
                                        :loading="isLoadingbestProductZoneInCounts"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('explore.noZones.product')"
                                        :empty="!(bestProductZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestProductZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(bestProductZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.lowestVisits')" icon="bi-arrow-down" accent="#004F6E"
                                        :loading="isLoadingworstProductZoneInCounts"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('explore.noZones.product')"
                                        :empty="!(worstVisitorsFromRowsProductZone?.visitors > 0)">
                                        <template #value>{{ worstVisitorsFromRowsProductZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(worstVisitorsFromRowsProductZone?.visitors) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.mostClearLingerers')" icon="bi-person-check" accent="#004F6E"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('explore.noZones.product')"
                                        :empty="!(topClearLingerersProductZone?.clears > 0)">
                                        <template #value>{{ topClearLingerersProductZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.clearLingerers', { n: fmt(topClearLingerersProductZone?.clears) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.highestEngagement')" icon="bi-emoji-heart-eyes" accent="#004F6E"
                                        :noZones="!isProductZonePresent()"
                                        :empty="highestEngagementZoneProductZone.visitors === 0">
                                        <template #value>{{ highestEngagementZoneProductZone.zone }}</template>
                                        <template #meta>
                                            {{ fmt(highestEngagementZoneProductZone.clears) }} / {{ fmt(highestEngagementZoneProductZone.visitors) }}
                                            ({{ fmtPct(highestEngagementZoneProductZone.ratio) }})
                                        </template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.topEngagementPotential')" icon="bi-cash-coin" accent="#004F6E"
                                        :noZones="!isProductZonePresent()" :noZonesText="$t('explore.noZones.product')"
                                        :empty="!(topSalesPotentialProductZone?.engSales > 0)"
                                        :emptyText="$t('explore.kpi.noEngagementPotential')">
                                        <template #value>{{ topSalesPotentialProductZone?.zone }}</template>
                                        <template #meta>{{ asMoney(topSalesPotentialProductZone?.engSales) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#004F6E" size="sm"
                                        :noZones="!isProductZonePresent()"
                                        :empty="productZoneNames.length === 0">
                                        <template #value>{{ zoneCoverageTextProductZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zeroVisitZones')" icon="bi-x-circle" accent="#004F6E" size="sm"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!zeroVisitorZonesProductZone.length">
                                        <template #value>{{ zeroVisitorZonesProductZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroVisitorZonesProductZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.visitorsBy', { zone: $t('explore.zoneNoun.product') })" icon="bi-bar-chart" :height="340"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!visitorsByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in visitorsByProductZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.avgDwellBy', { zone: $t('explore.zoneNoun.product') })" icon="bi-stopwatch" :height="340"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!avgDwellByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in avgDwellByProductZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.totalTimeBy', { zone: $t('explore.zoneNoun.product') })" icon="bi-bar-chart-line" :height="340"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!totalDurationRowsProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...totalDurationRowsProductZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxTotalDurationProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.durationMixBy', { zone: $t('explore.zoneNoun.product') })" icon="bi-grid" :height="360"
                                        :noZones="!isProductZonePresent()"
                                        :empty="!percentageByDurationRangeInProductZone || !Object.keys(percentageByDurationRangeInProductZone).length"
                                        accent="#004F6E">
                                        <ChartsFixedWidgetsPDDPercentageByDuration
                                            :percentageByDurationRangeInZone="percentageByDurationRangeInProductZone"
                                            :zonePresent="isProductZonePresent()"
                                            :active="activeTab === 'data-foundation-layer'" />
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.funnelBy', { kind: $t('explore.zoneKind.product'), zone: $t('explore.zoneNoun.product') })" icon="bi-diagram-3"
                                        :height="Math.max(260, 56 + productZoneFunnelRows.length * 48)"
                                        :empty="!productZoneFunnelRows.length" accent="#004F6E">
                                        <div class="funnelz-table">
                                            <div class="funnelz-row funnelz-head">
                                                <div>{{ $t('explore.funnel.zone') }}</div>
                                                <div class="num">{{ $t('explore.funnel.visits') }}</div>
                                                <div class="num">{{ $t('explore.funnel.walkBys') }}</div>
                                                <div class="num">{{ $t('explore.funnel.shortLingerers') }}</div>
                                                <div class="num">{{ $t('explore.funnel.clearLingerers') }}</div>
                                            </div>
                                            <div v-for="r in productZoneFunnelRows" :key="r.zone" class="funnelz-row">
                                                <div class="zone-name">{{ r.zone }}</div>
                                                <div class="num">{{ fmt(r.visitors) }}</div>
                                                <template v-if="r.strategyInsufficient">
                                                    <div class="merge-3 insufficient-msg">{{ $t('explore.funnel.insufficient') }}</div>
                                                </template>
                                                <template v-else>
                                                    <div class="num"><span>{{ fmt(r.walkbys.count) }}</span><small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.shorts.count) }}</span><small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.clears.count) }}</span><small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small></div>
                                                </template>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- ========================= TRIPWIRE SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasTripwireProductZones"
                                :label="$t('explore.sub.tripwire')"
                                :countText="$t('explore.count.zones', zoneStats.product.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#004F6E"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#004F6E"
                                        :empty="!(bestProductZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestProductZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(bestProductZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#004F6E"
                                        :empty="!(lowestProductZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestProductZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(lowestProductZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#004F6E" size="sm"
                                        :empty="!crossingsByProductZone.length">
                                        <template #value>{{ crossingCoverageTextProductZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#004F6E" size="sm"
                                        :empty="!zeroCrossingZonesProductZone.length">
                                        <template #value>{{ zeroCrossingZonesProductZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesProductZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>

                                    <KpiCard v-if="hasEstimatedProductZones"
                                        :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#004F6E"
                                        :empty="!(bestProductZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestProductZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.product[bestProductZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.product[bestProductZoneByEstAvgDwell?.zone_name]}`]"
>{{ confidenceLabel(estDwellConfidence.product[bestProductZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('product', bestProductZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('product', bestProductZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('product', bestProductZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestProductZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.crossingsBy', { zone: $t('explore.zoneNoun.product') })" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByProductZone?.some(r => r.crossings > 0)" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByProductZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedProductZones"
                                        :title="$t('explore.chart.estAvgDwellBy', { zone: $t('explore.zoneNoun.product') })" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByProductZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.product[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.product[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.product[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('product', row.zone)" :class="biasChipFor('product', row.zone).cls">{{ biasChipFor('product', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedProductZones"
                                        :title="$t('explore.chart.estTotalTimeBy', { zone: $t('explore.zoneNoun.product') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByProductZone?.length" accent="#004F6E">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByProductZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.product[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.product[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.product[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('product', row.zone)" :class="biasChipFor('product', row.zone).cls">{{ biasChipFor('product', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimeProductZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>
                            <!-- Service response — "and did anyone come to them?"
                                 Explore, because it is a raw per-zone-type measurement;
                                 THIS section, because Explore is already sectioned by
                                 zone type. Number, then when it happened (consolidated
                                 across every measured zone of the type), then which
                                 zones. Renders nothing while `pending`.
                                 docs/reference/service_response_measurement.md §9 -->
                            <ServiceResponseTypePanel
                              :data="serviceResponseByType['6']"
                              :timeSpan="serviceResponsePeriodLabel"
                              accent="#004F6E"
                            />

                        </Section>

                        <!-- Demo Zone Intelligence -->
                        <Section
                            v-if="showDemoSection"
                            id="sec-demo"
                            v-show="exploreSubTab === 'sec-demo'"
                            :title="$t('explore.section.demo')"
                            icon="bi-easel"
                                                       accent="#62B5D9"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([7])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <template #summary>
                                <span v-if="zoneStats.demo.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.demo.polygon) }}
                                </span>
                                <span v-if="zoneStats.demo.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.demo.tripwire) }}
                                </span>
                            </template>
                            <!-- ========================= POLYGON SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasPolygonDemoZones"
                                :label="$t('explore.sub.polygon')"
                                :countText="$t('explore.count.zones', zoneStats.demo.polygon)"
                                icon="bi-bounding-box"
                                accent="#62B5D9"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#62B5D9"
                                        :loading="isLoadingbestDemoZoneInAvgDuration"
                                        :noZones="!isDemoZonePresent()" :noZonesText="$t('explore.noZones.demo')"
                                        :empty="!(bestDemoZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestDemoZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.avg', { d: formattedDurationTime(bestDemoZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.topVisits')" icon="bi-people" accent="#62B5D9"
                                        :loading="isLoadingbestDemoZoneInCounts"
                                        :noZones="!isDemoZonePresent()" :noZonesText="$t('explore.noZones.demo')"
                                        :empty="!(bestDemoZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestDemoZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(bestDemoZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.lowestVisits')" icon="bi-person-dash" accent="#62B5D9"
                                        :loading="isLoadingworstDemoZoneInCounts"
                                        :noZones="!isDemoZonePresent()" :noZonesText="$t('explore.noZones.demo')"
                                        :empty="!(worstVisitorsFromRowsDemoZone?.visitors > 0)">
                                        <template #value>{{ worstVisitorsFromRowsDemoZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(worstVisitorsFromRowsDemoZone?.visitors) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.mostClearLingerers')" icon="bi-person-check" accent="#62B5D9"
                                        :noZones="!isDemoZonePresent()" :noZonesText="$t('explore.noZones.demo')"
                                        :empty="!(topClearLingerersDemoZone?.clears > 0)">
                                        <template #value>{{ topClearLingerersDemoZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.clearLingerers', { n: fmt(topClearLingerersDemoZone?.clears) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.highestEngagement')" icon="bi-emoji-heart-eyes" accent="#62B5D9"
                                        :noZones="!isDemoZonePresent()"
                                        :empty="highestEngagementZoneDemoZone.visitors === 0">
                                        <template #value>{{ highestEngagementZoneDemoZone.zone }}</template>
                                        <template #meta>
                                            {{ fmt(highestEngagementZoneDemoZone.clears) }} / {{ fmt(highestEngagementZoneDemoZone.visitors) }}
                                            ({{ fmtPct(highestEngagementZoneDemoZone.ratio) }})
                                        </template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#62B5D9" size="sm"
                                        :noZones="!isDemoZonePresent()"
                                        :empty="demoZoneNames.length === 0">
                                        <template #value>{{ zoneCoverageTextDemoZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zeroVisitZones')" icon="bi-x-circle" accent="#62B5D9" size="sm"
                                        :noZones="!isDemoZonePresent()"
                                        :empty="!zeroVisitorZonesDemoZone.length">
                                        <template #value>{{ zeroVisitorZonesDemoZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroVisitorZonesDemoZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.visitorsBy', { zone: $t('explore.zoneNoun.demo') })" icon="bi-bar-chart" :height="340"
                                        :noZones="!isDemoZonePresent()"
                                        :empty="!visitorsByDemoZone?.length" accent="#62B5D9">
                                        <div class="barlist">
                                            <div v-for="row in visitorsByDemoZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsDemoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.avgDwellBy', { zone: $t('explore.zoneNoun.demo') })" icon="bi-stopwatch" :height="340"
                                        :noZones="!isDemoZonePresent()"
                                        :empty="!avgDwellByDemoZone?.length" accent="#62B5D9">
                                        <div class="barlist">
                                            <div v-for="row in avgDwellByDemoZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecDemoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.totalTimeBy', { zone: $t('explore.zoneNoun.demo') })" icon="bi-bar-chart-line" :height="340"
                                        :noZones="!isDemoZonePresent()"
                                        :empty="!totalDurationRowsDemoZone?.length" accent="#62B5D9">
                                        <div class="barlist">
                                            <div v-for="row in [...totalDurationRowsDemoZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxTotalDurationDemoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.durationMixBy', { zone: $t('explore.zoneNoun.demo') })" icon="bi-grid" :height="360"
                                        :noZones="!isDemoZonePresent()"
                                        :empty="!percentageByDurationRangeInDemoZone || !Object.keys(percentageByDurationRangeInDemoZone).length"
                                        accent="#62B5D9">
                                        <ChartsFixedWidgetsPDDPercentageByDuration
                                            :percentageByDurationRangeInZone="percentageByDurationRangeInDemoZone"
                                            :zonePresent="isDemoZonePresent()"
                                            :active="activeTab === 'data-foundation-layer'" />
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.funnelBy', { kind: $t('explore.zoneKind.demo'), zone: $t('explore.zoneNoun.demo') })" icon="bi-diagram-3"
                                        :height="Math.max(260, 56 + demoZoneFunnelRows.length * 48)"
                                        :empty="!demoZoneFunnelRows.length" accent="#62B5D9">
                                        <div class="funnelz-table">
                                            <div class="funnelz-row funnelz-head">
                                                <div>{{ $t('explore.funnel.zone') }}</div>
                                                <div class="num">{{ $t('explore.funnel.visits') }}</div>
                                                <div class="num">{{ $t('explore.funnel.walkBys') }}</div>
                                                <div class="num">{{ $t('explore.funnel.shortLingerers') }}</div>
                                                <div class="num">{{ $t('explore.funnel.clearLingerers') }}</div>
                                            </div>
                                            <div v-for="r in demoZoneFunnelRows" :key="r.zone" class="funnelz-row">
                                                <div class="zone-name">{{ r.zone }}</div>
                                                <div class="num">{{ fmt(r.visitors) }}</div>
                                                <template v-if="r.strategyInsufficient">
                                                    <div class="merge-3 insufficient-msg">{{ $t('explore.funnel.insufficient') }}</div>
                                                </template>
                                                <template v-else>
                                                    <div class="num"><span>{{ fmt(r.walkbys.count) }}</span><small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.shorts.count) }}</span><small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.clears.count) }}</span><small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small></div>
                                                </template>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- ========================= TRIPWIRE SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasTripwireDemoZones"
                                :label="$t('explore.sub.tripwire')"
                                :countText="$t('explore.count.zones', zoneStats.demo.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#62B5D9"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#62B5D9"
                                        :empty="!(bestDemoZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestDemoZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(bestDemoZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#62B5D9"
                                        :empty="!(lowestDemoZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestDemoZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(lowestDemoZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#62B5D9" size="sm"
                                        :empty="!crossingsByDemoZone.length">
                                        <template #value>{{ crossingCoverageTextDemoZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#62B5D9" size="sm"
                                        :empty="!zeroCrossingZonesDemoZone.length">
                                        <template #value>{{ zeroCrossingZonesDemoZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesDemoZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>

                                    <KpiCard v-if="hasEstimatedDemoZones"
                                        :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#62B5D9"
                                        :empty="!(bestDemoZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestDemoZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.demo[bestDemoZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.demo[bestDemoZoneByEstAvgDwell?.zone_name]}`]"
>{{ confidenceLabel(estDwellConfidence.demo[bestDemoZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('demo', bestDemoZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('demo', bestDemoZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('demo', bestDemoZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestDemoZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.crossingsBy', { zone: $t('explore.zoneNoun.demo') })" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByDemoZone?.some(r => r.crossings > 0)" accent="#62B5D9">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByDemoZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsDemoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedDemoZones"
                                        :title="$t('explore.chart.estAvgDwellBy', { zone: $t('explore.zoneNoun.demo') })" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByDemoZone?.length" accent="#62B5D9">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByDemoZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.demo[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.demo[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.demo[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('demo', row.zone)" :class="biasChipFor('demo', row.zone).cls">{{ biasChipFor('demo', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecDemoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedDemoZones"
                                        :title="$t('explore.chart.estTotalTimeBy', { zone: $t('explore.zoneNoun.demo') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByDemoZone?.length" accent="#62B5D9">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByDemoZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.demo[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.demo[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.demo[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('demo', row.zone)" :class="biasChipFor('demo', row.zone).cls">{{ biasChipFor('demo', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimeDemoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>
                            <!-- Service response — "and did anyone come to them?"
                                 Explore, because it is a raw per-zone-type measurement;
                                 THIS section, because Explore is already sectioned by
                                 zone type. Number, then when it happened (consolidated
                                 across every measured zone of the type), then which
                                 zones. Renders nothing while `pending`.
                                 docs/reference/service_response_measurement.md §9 -->
                            <ServiceResponseTypePanel
                              :data="serviceResponseByType['7']"
                              :timeSpan="serviceResponsePeriodLabel"
                              accent="#62B5D9"
                            />

                        </Section>

                        <!-- Display Zone Intelligence -->
                        <Section
                            v-if="showDisplaySection"
                            id="sec-display"
                            v-show="exploreSubTab === 'sec-display'"
                            :title="$t('explore.section.display')"
                            icon="bi-image"
                                                       accent="#110D93"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([8])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <template #summary>
                                <span v-if="zoneStats.display.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.display.polygon) }}
                                </span>
                                <span v-if="zoneStats.display.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.display.tripwire) }}
                                </span>
                            </template>

                            <!-- ========================= POLYGON SUB-PANEL =========================
                                 Direct-measurement metrics from in-zone dwell. Auto-hidden when there
                                 are no polygon-bearing Display zones. -->
                            <SubPanel
                                v-if="hasPolygonDisplayZones"
                                :label="$t('explore.sub.polygon')"
                                :countText="$t('explore.count.zones', zoneStats.display.polygon)"
                                icon="bi-bounding-box"
                                accent="#110D93"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#110D93"
                                        :loading="isLoadingbestDisplayZoneInAvgDuration"
                                        :noZones="!isDisplayZonePresent()" :noZonesText="$t('explore.noZones.display')"
                                        :empty="!(bestDisplayZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestDisplayZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.avg', { d: formattedDurationTime(bestDisplayZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.topVisits')" icon="bi-people" accent="#110D93"
                                        :loading="isLoadingbestDisplayZoneInCounts"
                                        :noZones="!isDisplayZonePresent()" :noZonesText="$t('explore.noZones.display')"
                                        :empty="!(bestDisplayZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestDisplayZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(bestDisplayZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.lowestVisits')" icon="bi-person-dash" accent="#110D93"
                                        :loading="isLoadingworstDisplayZoneInCounts"
                                        :noZones="!isDisplayZonePresent()" :noZonesText="$t('explore.noZones.display')"
                                        :empty="!(worstVisitorsFromRowsDisplayZone?.visitors > 0)">
                                        <template #value>{{ worstVisitorsFromRowsDisplayZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(worstVisitorsFromRowsDisplayZone?.visitors) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.mostClearLingerers')" icon="bi-person-check" accent="#110D93"
                                        :noZones="!isDisplayZonePresent()" :noZonesText="$t('explore.noZones.display')"
                                        :empty="!(topClearLingerersDisplayZone?.clears > 0)">
                                        <template #value>{{ topClearLingerersDisplayZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.clearLingerers', { n: fmt(topClearLingerersDisplayZone?.clears) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.highestEngagement')" icon="bi-emoji-heart-eyes" accent="#110D93"
                                        :noZones="!isDisplayZonePresent()"
                                        :empty="highestEngagementZoneDisplayZone.visitors === 0">
                                        <template #value>{{ highestEngagementZoneDisplayZone.zone }}</template>
                                        <template #meta>
                                            {{ fmt(highestEngagementZoneDisplayZone.clears) }} / {{ fmt(highestEngagementZoneDisplayZone.visitors) }}
                                            ({{ fmtPct(highestEngagementZoneDisplayZone.ratio) }})
                                        </template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#110D93" size="sm"
                                        :noZones="!isDisplayZonePresent()"
                                        :empty="displayZoneNames.length === 0">
                                        <template #value>{{ zoneCoverageTextDisplayZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zeroVisitZones')" icon="bi-x-circle" accent="#110D93" size="sm"
                                        :noZones="!isDisplayZonePresent()"
                                        :empty="!zeroVisitorZonesDisplayZone.length">
                                        <template #value>{{ zeroVisitorZonesDisplayZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroVisitorZonesDisplayZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.visitorsBy', { zone: $t('explore.zoneNoun.display') })" icon="bi-bar-chart" :height="340"
                                        :noZones="!isDisplayZonePresent()"
                                        :empty="!visitorsByDisplayZone?.length" accent="#110D93">
                                        <div class="barlist">
                                            <div v-for="row in visitorsByDisplayZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsDisplayZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.avgDwellBy', { zone: $t('explore.zoneNoun.display') })" icon="bi-stopwatch" :height="340"
                                        :noZones="!isDisplayZonePresent()"
                                        :empty="!avgDwellByDisplayZone?.length" accent="#110D93">
                                        <div class="barlist">
                                            <div v-for="row in avgDwellByDisplayZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecDisplayZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.totalTimeBy', { zone: $t('explore.zoneNoun.display') })" icon="bi-bar-chart-line" :height="340"
                                        :noZones="!isDisplayZonePresent()"
                                        :empty="!totalDurationRowsDisplayZone?.length" accent="#110D93">
                                        <div class="barlist">
                                            <div v-for="row in [...totalDurationRowsDisplayZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxTotalDurationDisplayZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.durationMixBy', { zone: $t('explore.zoneNoun.display') })" icon="bi-grid" :height="360"
                                        :noZones="!isDisplayZonePresent()"
                                        :empty="!percentageByDurationRangeInDisplayZone || !Object.keys(percentageByDurationRangeInDisplayZone).length"
                                        accent="#110D93">
                                        <ChartsFixedWidgetsPDDPercentageByDuration
                                            :percentageByDurationRangeInZone="percentageByDurationRangeInDisplayZone"
                                            :zonePresent="isDisplayZonePresent()"
                                            :active="activeTab === 'data-foundation-layer'" />
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.funnelBy', { kind: $t('explore.zoneKind.display'), zone: $t('explore.zoneNoun.display') })" icon="bi-diagram-3"
                                        :height="Math.max(260, 56 + displayZoneFunnelRows.length * 48)"
                                        :empty="!displayZoneFunnelRows.length" accent="#110D93">
                                        <div class="funnelz-table">
                                            <div class="funnelz-row funnelz-head">
                                                <div>{{ $t('explore.funnel.zone') }}</div>
                                                <div class="num">{{ $t('explore.funnel.visits') }}</div>
                                                <div class="num">{{ $t('explore.funnel.walkBys') }}</div>
                                                <div class="num">{{ $t('explore.funnel.shortLingerers') }}</div>
                                                <div class="num">{{ $t('explore.funnel.clearLingerers') }}</div>
                                            </div>
                                            <div v-for="r in displayZoneFunnelRows" :key="r.zone" class="funnelz-row">
                                                <div class="zone-name">{{ r.zone }}</div>
                                                <div class="num">{{ fmt(r.visitors) }}</div>
                                                <template v-if="r.strategyInsufficient">
                                                    <div class="merge-3 insufficient-msg">{{ $t('explore.funnel.insufficient') }}</div>
                                                </template>
                                                <template v-else>
                                                    <div class="num"><span>{{ fmt(r.walkbys.count) }}</span><small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.shorts.count) }}</span><small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.clears.count) }}</span><small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small></div>
                                                </template>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- ========================= TRIPWIRE SUB-PANEL =========================
                                 Tripwire crossings + Mode C (Little's-Law) dwell estimates. Auto-hidden
                                 when no Display zones carry tripwires. The dashed frame around the whole
                                 sub-panel carries the "estimated/derived" signal, so individual cards no
                                 longer need their own tripwire badge. -->
                            <SubPanel
                                v-if="hasTripwireDisplayZones"
                                :label="$t('explore.sub.tripwire')"
                                :countText="$t('explore.count.zones', zoneStats.display.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#110D93"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#110D93"
                                        :empty="!(bestDisplayZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestDisplayZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(bestDisplayZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#110D93"
                                        :empty="!(lowestDisplayZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestDisplayZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(lowestDisplayZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#110D93" size="sm"
                                        :empty="!crossingsByDisplayZone.length">
                                        <template #value>{{ crossingCoverageTextDisplayZone }}</template>
                                    </KpiCard>

                                    <KpiCard :label="$t('explore.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#110D93" size="sm"
                                        :empty="!zeroCrossingZonesDisplayZone.length">
                                        <template #value>{{ zeroCrossingZonesDisplayZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesDisplayZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>

                                    <KpiCard v-if="hasEstimatedDisplayZones"
                                        :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#110D93"
                                        :empty="!(bestDisplayZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestDisplayZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.display[bestDisplayZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.display[bestDisplayZoneByEstAvgDwell?.zone_name]}`]"
>{{ confidenceLabel(estDwellConfidence.display[bestDisplayZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('display', bestDisplayZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('display', bestDisplayZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('display', bestDisplayZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestDisplayZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.crossingsBy', { zone: $t('explore.zoneNoun.display') })" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByDisplayZone?.some(r => r.crossings > 0)" accent="#110D93">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByDisplayZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsDisplayZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedDisplayZones"
                                        :title="$t('explore.chart.estAvgDwellBy', { zone: $t('explore.zoneNoun.display') })" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByDisplayZone?.length" accent="#110D93">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByDisplayZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.display[row.zone]"
                                                          :class="['conf-chip', `conf-${estDwellConfidence.display[row.zone]}`]"
>{{ confidenceLabel(estDwellConfidence.display[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('display', row.zone)" :class="biasChipFor('display', row.zone).cls">{{ biasChipFor('display', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecDisplayZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedDisplayZones"
                                        :title="$t('explore.chart.estTotalTimeBy', { zone: $t('explore.zoneNoun.display') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByDisplayZone?.length" accent="#110D93">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByDisplayZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.display[row.zone]"
                                                          :class="['conf-chip', `conf-${estDwellConfidence.display[row.zone]}`]"
>{{ confidenceLabel(estDwellConfidence.display[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('display', row.zone)" :class="biasChipFor('display', row.zone).cls">{{ biasChipFor('display', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimeDisplayZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>
                        </Section>

                        <!-- Info Zone Intelligence (Phase 8.3.d) -->
                        <Section
                            v-if="showInfoSection"
                            id="sec-info"
                            v-show="exploreSubTab === 'sec-info'"
                            :title="$t('explore.section.info')"
                            icon="bi-info-circle"
                                                       accent="#0D9488"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([9, 12, 15])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <!-- 6.7: an alert threshold of a zone here was changed today. -->
                            <ChangeNotice v-if="thresholdChangedIn([9, 12, 15])" :text="NOTICES.settings.thresholds" />
                            <template #summary>
                                <span v-if="zoneStats.info.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.info.polygon) }}
                                </span>
                                <span v-if="zoneStats.info.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.info.tripwire) }}
                                </span>
                            </template>
                            <!-- ========================= POLYGON SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasPolygonInfoZones"
                                :label="$t('explore.sub.polygon')"
                                :countText="$t('explore.count.zones', zoneStats.info.polygon)"
                                icon="bi-bounding-box"
                                accent="#0D9488"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#0D9488"
                                        :noZones="!isInfoZonePresent()" :noZonesText="$t('explore.noZones.info')"
                                        :empty="!(bestInfoZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestInfoZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.avg', { d: formattedDurationTime(bestInfoZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.topVisits')" icon="bi-people" accent="#0D9488"
                                        :noZones="!isInfoZonePresent()" :noZonesText="$t('explore.noZones.info')"
                                        :empty="!(bestInfoZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestInfoZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(bestInfoZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.lowestVisits')" icon="bi-arrow-down" accent="#0D9488"
                                        :noZones="!isInfoZonePresent()" :noZonesText="$t('explore.noZones.info')"
                                        :empty="!(worstVisitorsFromRowsInfoZone?.visitors > 0)">
                                        <template #value>{{ worstVisitorsFromRowsInfoZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(worstVisitorsFromRowsInfoZone?.visitors) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.mostClearLingerers')" icon="bi-person-check" accent="#0D9488"
                                        :noZones="!isInfoZonePresent()" :noZonesText="$t('explore.noZones.info')"
                                        :empty="!(topClearLingerersInfoZone?.clears > 0)">
                                        <template #value>{{ topClearLingerersInfoZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.clearLingerers', { n: fmt(topClearLingerersInfoZone?.clears) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.highestEngagement')" icon="bi-emoji-heart-eyes" accent="#0D9488"
                                        :noZones="!isInfoZonePresent()"
                                        :empty="highestEngagementZoneInfoZone.visitors === 0">
                                        <template #value>{{ highestEngagementZoneInfoZone.zone }}</template>
                                        <template #meta>
                                            {{ fmt(highestEngagementZoneInfoZone.clears) }} / {{ fmt(highestEngagementZoneInfoZone.visitors) }}
                                            ({{ fmtPct(highestEngagementZoneInfoZone.ratio) }})
                                        </template>
                                    </KpiCard>

                                    <!-- Counter unattended (docs §28.6): customers who reached a counter
                                         with nobody behind it. Sits with the other outcome figures rather
                                         than with the durations — it is a count of people, not a time. -->
                                    <CounterUnattendedCard
                                        v-if="countersByService.info.length"
                                        :counters="countersByService.info"
                                        accent="#0D9488"
                                    />
                                    <KpiCard :label="$t('explore.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#0D9488" size="sm"
                                        :noZones="!isInfoZonePresent()"
                                        :empty="(zoneCoverageTextInfoZone || '').startsWith('0/0')">
                                        <template #value>{{ zoneCoverageTextInfoZone }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.zeroVisitZones')" icon="bi-x-circle" accent="#0D9488" size="sm"
                                        :noZones="!isInfoZonePresent()"
                                        :empty="!zeroVisitorZonesInfoZone.length">
                                        <template #value>{{ zeroVisitorZonesInfoZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroVisitorZonesInfoZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.visitorsBy', { zone: $t('explore.zoneNoun.info') })" icon="bi-bar-chart" :height="340"
                                        :empty="!visitorsByInfoZone?.length" accent="#0D9488">
                                        <div class="barlist">
                                            <div v-for="row in visitorsByInfoZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsInfoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                    <ChartPanel :title="$t('explore.chart.avgDwellBy', { zone: $t('explore.zoneNoun.info') })" icon="bi-stopwatch" :height="340"
                                        :empty="!avgDwellByInfoZone?.length" accent="#0D9488">
                                        <div class="barlist">
                                            <div v-for="row in avgDwellByInfoZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecInfoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.totalTimeBy', { zone: $t('explore.zoneNoun.info') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!totalDurationRowsInfoZone?.length" accent="#0D9488">
                                        <div class="barlist">
                                            <div v-for="row in [...totalDurationRowsInfoZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxTotalDurationInfoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                    <ChartPanel :title="$t('explore.chart.durationMixBy', { zone: $t('explore.zoneNoun.info') })" icon="bi-grid" :height="360"
                                        :empty="!percentageByDurationRangeInInfoZone || !Object.keys(percentageByDurationRangeInInfoZone).length" accent="#0D9488">
                                        <ChartsFixedWidgetsPDDPercentageByDuration :percentageByDurationRangeInZone="percentageByDurationRangeInInfoZone" :zonePresent="isInfoZonePresent()" :active="activeTab === 'data-foundation-layer'" />
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.funnelBy', { kind: $t('explore.zoneKind.info'), zone: $t('explore.zoneNoun.info') })" icon="bi-diagram-3"
                                        :height="Math.max(260, 56 + infoZoneFunnelRows.length * 48)"
                                        :empty="!infoZoneFunnelRows.length" accent="#0D9488">
                                        <div class="funnelz-table">
                                            <div class="funnelz-row funnelz-head">
                                                <div>{{ $t('explore.funnel.zone') }}</div>
                                                <div class="num">{{ $t('explore.funnel.visits') }}</div>
                                                <div class="num">{{ $t('explore.funnel.walkBys') }}</div>
                                                <div class="num">{{ $t('explore.funnel.shortLingerers') }}</div>
                                                <div class="num">{{ $t('explore.funnel.clearLingerers') }}</div>
                                            </div>
                                            <div v-for="r in infoZoneFunnelRows" :key="r.zone" class="funnelz-row">
                                                <div class="zone-name">{{ r.zone }}</div>
                                                <div class="num">{{ fmt(r.visitors) }}</div>
                                                <template v-if="r.strategyInsufficient">
                                                    <div class="merge-3 insufficient-msg">{{ $t('explore.funnel.insufficient') }}</div>
                                                </template>
                                                <template v-else>
                                                    <div class="num"><span>{{ fmt(r.walkbys.count) }}</span><small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.shorts.count) }}</span><small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.clears.count) }}</span><small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small></div>
                                                </template>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- ========================= TRIPWIRE SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasTripwireInfoZones"
                                :label="$t('explore.sub.tripwire')"
                                :countText="$t('explore.count.zones', zoneStats.info.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#0D9488"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#0D9488"
                                        :empty="!(bestInfoZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestInfoZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(bestInfoZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#0D9488"
                                        :empty="!(lowestInfoZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestInfoZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(lowestInfoZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#0D9488" size="sm"
                                        :empty="!crossingsByInfoZone.length">
                                        <template #value>{{ crossingCoverageTextInfoZone }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#0D9488" size="sm"
                                        :empty="!zeroCrossingZonesInfoZone.length">
                                        <template #value>{{ zeroCrossingZonesInfoZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesInfoZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                    <KpiCard v-if="hasEstimatedInfoZones" :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#0D9488"
                                        :empty="!(bestInfoZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestInfoZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.info[bestInfoZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.info[bestInfoZoneByEstAvgDwell?.zone_name]}`]"
>{{ confidenceLabel(estDwellConfidence.info[bestInfoZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('info', bestInfoZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('info', bestInfoZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('info', bestInfoZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestInfoZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.crossingsBy', { zone: $t('explore.zoneNoun.info') })" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByInfoZone?.some(r => r.crossings > 0)" accent="#0D9488">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByInfoZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsInfoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedInfoZones" :title="$t('explore.chart.estAvgDwellBy', { zone: $t('explore.zoneNoun.info') })" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByInfoZone?.length" accent="#0D9488">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByInfoZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.info[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.info[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.info[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('info', row.zone)" :class="biasChipFor('info', row.zone).cls">{{ biasChipFor('info', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecInfoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedInfoZones" :title="$t('explore.chart.estTotalTimeBy', { zone: $t('explore.zoneNoun.info') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByInfoZone?.length" accent="#0D9488">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByInfoZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.info[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.info[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.info[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('info', row.zone)" :class="biasChipFor('info', row.zone).cls">{{ biasChipFor('info', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimeInfoZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- Counter unattended: somebody stood here and
                                 nobody was behind the counter. Different from
                                 queueing, which is the waiting zone. docs §28.6 -->
                            <CounterUnattendedPanel
                                v-if="countersByService.info.length"
                                :counters="countersByService.info"
                                accent="#0D9488"
                            />
                        </Section>

                        <!-- Service Zone Intelligence (Phase 8.3.d) -->
                        <Section
                            v-if="showServiceSection"
                            id="sec-service"
                            v-show="exploreSubTab === 'sec-service'"
                            :title="$t('explore.section.service')"
                            icon="bi-headset"
                                                       accent="#D97706"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([10, 13, 16])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <!-- 6.7: an alert threshold of a zone here was changed today. -->
                            <ChangeNotice v-if="thresholdChangedIn([10, 13, 16])" :text="NOTICES.settings.thresholds" />
                            <template #summary>
                                <span v-if="zoneStats.service.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.service.polygon) }}
                                </span>
                                <span v-if="zoneStats.service.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.service.tripwire) }}
                                </span>
                            </template>
                            <!-- ========================= POLYGON SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasPolygonServiceZones"
                                :label="$t('explore.sub.polygon')"
                                :countText="$t('explore.count.zones', zoneStats.service.polygon)"
                                icon="bi-bounding-box"
                                accent="#D97706"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#D97706"
                                        :noZones="!isServiceZonePresent()" :noZonesText="$t('explore.noZones.service')"
                                        :empty="!(bestServiceZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestServiceZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.avg', { d: formattedDurationTime(bestServiceZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.topVisits')" icon="bi-people" accent="#D97706"
                                        :noZones="!isServiceZonePresent()" :noZonesText="$t('explore.noZones.service')"
                                        :empty="!(bestServiceZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestServiceZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(bestServiceZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.lowestVisits')" icon="bi-arrow-down" accent="#D97706"
                                        :noZones="!isServiceZonePresent()" :noZonesText="$t('explore.noZones.service')"
                                        :empty="!(worstVisitorsFromRowsServiceZone?.visitors > 0)">
                                        <template #value>{{ worstVisitorsFromRowsServiceZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(worstVisitorsFromRowsServiceZone?.visitors) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.mostClearLingerers')" icon="bi-person-check" accent="#D97706"
                                        :noZones="!isServiceZonePresent()" :noZonesText="$t('explore.noZones.service')"
                                        :empty="!(topClearLingerersServiceZone?.clears > 0)">
                                        <template #value>{{ topClearLingerersServiceZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.clearLingerers', { n: fmt(topClearLingerersServiceZone?.clears) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.highestEngagement')" icon="bi-emoji-heart-eyes" accent="#D97706"
                                        :noZones="!isServiceZonePresent()"
                                        :empty="highestEngagementZoneServiceZone.visitors === 0">
                                        <template #value>{{ highestEngagementZoneServiceZone.zone }}</template>
                                        <template #meta>
                                            {{ fmt(highestEngagementZoneServiceZone.clears) }} / {{ fmt(highestEngagementZoneServiceZone.visitors) }}
                                            ({{ fmtPct(highestEngagementZoneServiceZone.ratio) }})
                                        </template>
                                    </KpiCard>

                                    <!-- Counter unattended (docs §28.6): customers who reached a counter
                                         with nobody behind it. Sits with the other outcome figures rather
                                         than with the durations — it is a count of people, not a time. -->
                                    <CounterUnattendedCard
                                        v-if="countersByService.service.length"
                                        :counters="countersByService.service"
                                        accent="#D97706"
                                    />
                                    <KpiCard :label="$t('explore.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#D97706" size="sm"
                                        :noZones="!isServiceZonePresent()"
                                        :empty="(zoneCoverageTextServiceZone || '').startsWith('0/0')">
                                        <template #value>{{ zoneCoverageTextServiceZone }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.zeroVisitZones')" icon="bi-x-circle" accent="#D97706" size="sm"
                                        :noZones="!isServiceZonePresent()"
                                        :empty="!zeroVisitorZonesServiceZone.length">
                                        <template #value>{{ zeroVisitorZonesServiceZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroVisitorZonesServiceZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.visitorsBy', { zone: $t('explore.zoneNoun.service') })" icon="bi-bar-chart" :height="340"
                                        :empty="!visitorsByServiceZone?.length" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in visitorsByServiceZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                    <ChartPanel :title="$t('explore.chart.avgDwellBy', { zone: $t('explore.zoneNoun.service') })" icon="bi-stopwatch" :height="340"
                                        :empty="!avgDwellByServiceZone?.length" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in avgDwellByServiceZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.totalTimeBy', { zone: $t('explore.zoneNoun.service') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!totalDurationRowsServiceZone?.length" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in [...totalDurationRowsServiceZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxTotalDurationServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                    <ChartPanel :title="$t('explore.chart.durationMixBy', { zone: $t('explore.zoneNoun.service') })" icon="bi-grid" :height="360"
                                        :empty="!percentageByDurationRangeInServiceZone || !Object.keys(percentageByDurationRangeInServiceZone).length" accent="#D97706">
                                        <ChartsFixedWidgetsPDDPercentageByDuration :percentageByDurationRangeInZone="percentageByDurationRangeInServiceZone" :zonePresent="isServiceZonePresent()" :active="activeTab === 'data-foundation-layer'" />
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.funnelBy', { kind: $t('explore.zoneKind.service'), zone: $t('explore.zoneNoun.service') })" icon="bi-diagram-3"
                                        :height="Math.max(260, 56 + serviceZoneFunnelRows.length * 48)"
                                        :empty="!serviceZoneFunnelRows.length" accent="#D97706">
                                        <div class="funnelz-table">
                                            <div class="funnelz-row funnelz-head">
                                                <div>{{ $t('explore.funnel.zone') }}</div>
                                                <div class="num">{{ $t('explore.funnel.visits') }}</div>
                                                <div class="num">{{ $t('explore.funnel.walkBys') }}</div>
                                                <div class="num">{{ $t('explore.funnel.shortLingerers') }}</div>
                                                <div class="num">{{ $t('explore.funnel.clearLingerers') }}</div>
                                            </div>
                                            <div v-for="r in serviceZoneFunnelRows" :key="r.zone" class="funnelz-row">
                                                <div class="zone-name">{{ r.zone }}</div>
                                                <div class="num">{{ fmt(r.visitors) }}</div>
                                                <template v-if="r.strategyInsufficient">
                                                    <div class="merge-3 insufficient-msg">{{ $t('explore.funnel.insufficient') }}</div>
                                                </template>
                                                <template v-else>
                                                    <div class="num"><span>{{ fmt(r.walkbys.count) }}</span><small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.shorts.count) }}</span><small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.clears.count) }}</span><small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small></div>
                                                </template>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- ========================= TRIPWIRE SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasTripwireServiceZones"
                                :label="$t('explore.sub.tripwire')"
                                :countText="$t('explore.count.zones', zoneStats.service.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#D97706"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#D97706"
                                        :empty="!(bestServiceZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestServiceZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(bestServiceZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#D97706"
                                        :empty="!(lowestServiceZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestServiceZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(lowestServiceZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#D97706" size="sm"
                                        :empty="!crossingsByServiceZone.length">
                                        <template #value>{{ crossingCoverageTextServiceZone }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#D97706" size="sm"
                                        :empty="!zeroCrossingZonesServiceZone.length">
                                        <template #value>{{ zeroCrossingZonesServiceZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesServiceZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                    <KpiCard v-if="hasEstimatedServiceZones" :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#D97706"
                                        :empty="!(bestServiceZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestServiceZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.service[bestServiceZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.service[bestServiceZoneByEstAvgDwell?.zone_name]}`]"
>{{ confidenceLabel(estDwellConfidence.service[bestServiceZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('service', bestServiceZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('service', bestServiceZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('service', bestServiceZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestServiceZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.crossingsBy', { zone: $t('explore.zoneNoun.service') })" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByServiceZone?.some(r => r.crossings > 0)" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByServiceZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedServiceZones" :title="$t('explore.chart.estAvgDwellBy', { zone: $t('explore.zoneNoun.service') })" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByServiceZone?.length" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByServiceZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.service[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.service[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.service[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('service', row.zone)" :class="biasChipFor('service', row.zone).cls">{{ biasChipFor('service', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedServiceZones" :title="$t('explore.chart.estTotalTimeBy', { zone: $t('explore.zoneNoun.service') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByServiceZone?.length" accent="#D97706">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByServiceZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.service[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.service[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.service[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('service', row.zone)" :class="biasChipFor('service', row.zone).cls">{{ biasChipFor('service', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimeServiceZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- Counter unattended: somebody stood here and
                                 nobody was behind the counter. Different from
                                 queueing, which is the waiting zone. docs §28.6 -->
                            <CounterUnattendedPanel
                                v-if="countersByService.service.length"
                                :counters="countersByService.service"
                                accent="#D97706"
                            />
                        </Section>

                        <!-- Pick Up Zone Intelligence (Phase 8.3.d) -->
                        <Section
                            v-if="showPickUpSection"
                            id="sec-pickup"
                            v-show="exploreSubTab === 'sec-pickup'"
                            :title="$t('explore.section.pickup')"
                            icon="bi-bag-check"
                                                       accent="#7E22CE"
                            :collapsible="false"
                            :hide-head="true"
                        >
                            <!-- Zone identity 6.2: a zone became this type today. -->
                            <ChangeNotice v-for="z in movedIntoTypes([11, 14, 17])" :key="z.name"
                                :text="NOTICES.movedInto(z.name, ZONE_TYPE_NAMES[z.to])" />
                            <!-- 6.7: an alert threshold of a zone here was changed today. -->
                            <ChangeNotice v-if="thresholdChangedIn([11, 14, 17])" :text="NOTICES.settings.thresholds" />
                            <template #summary>
                                <span v-if="zoneStats.pickup.polygon" class="sum-pill sum-zones">
                                    <i class="bi bi-bounding-box"></i>{{ $t('explore.count.zones', zoneStats.pickup.polygon) }}
                                </span>
                                <span v-if="zoneStats.pickup.tripwire" class="sum-pill sum-tripwire">
                                    <i class="bi bi-arrow-left-right"></i>{{ $t('explore.count.tripwires', zoneStats.pickup.tripwire) }}
                                </span>
                            </template>
                            <!-- ========================= POLYGON SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasPolygonPickUpZones"
                                :label="$t('explore.sub.polygon')"
                                :countText="$t('explore.count.zones', zoneStats.pickup.polygon)"
                                icon="bi-bounding-box"
                                accent="#7E22CE"
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#7E22CE"
                                        :noZones="!isPickUpZonePresent()" :noZonesText="$t('explore.noZones.pickup')"
                                        :empty="!(bestPickUpZoneByAvgDurationPoly?.avg_duration > 0)">
                                        <template #value>{{ bestPickUpZoneByAvgDurationPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.avg', { d: formattedDurationTime(bestPickUpZoneByAvgDurationPoly?.avg_duration || 0) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.topVisits')" icon="bi-people" accent="#7E22CE"
                                        :noZones="!isPickUpZonePresent()" :noZonesText="$t('explore.noZones.pickup')"
                                        :empty="!(bestPickUpZoneByVisitorsPoly?.visitor_count > 0)">
                                        <template #value>{{ bestPickUpZoneByVisitorsPoly?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(bestPickUpZoneByVisitorsPoly?.visitor_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.lowestVisits')" icon="bi-arrow-down" accent="#7E22CE"
                                        :noZones="!isPickUpZonePresent()" :noZonesText="$t('explore.noZones.pickup')"
                                        :empty="!(worstVisitorsFromRowsPickUpZone?.visitors > 0)">
                                        <template #value>{{ worstVisitorsFromRowsPickUpZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.visits', { n: fmt(worstVisitorsFromRowsPickUpZone?.visitors) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.mostClearLingerers')" icon="bi-person-check" accent="#7E22CE"
                                        :noZones="!isPickUpZonePresent()" :noZonesText="$t('explore.noZones.pickup')"
                                        :empty="!(topClearLingerersPickUpZone?.clears > 0)">
                                        <template #value>{{ topClearLingerersPickUpZone?.zone }}</template>
                                        <template #meta>{{ $t('explore.meta.clearLingerers', { n: fmt(topClearLingerersPickUpZone?.clears) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.highestEngagement')" icon="bi-emoji-heart-eyes" accent="#7E22CE"
                                        :noZones="!isPickUpZonePresent()"
                                        :empty="highestEngagementZonePickUpZone.visitors === 0">
                                        <template #value>{{ highestEngagementZonePickUpZone.zone }}</template>
                                        <template #meta>
                                            {{ fmt(highestEngagementZonePickUpZone.clears) }} / {{ fmt(highestEngagementZonePickUpZone.visitors) }}
                                            ({{ fmtPct(highestEngagementZonePickUpZone.ratio) }})
                                        </template>
                                    </KpiCard>

                                    <!-- Counter unattended (docs §28.6): customers who reached a counter
                                         with nobody behind it. Sits with the other outcome figures rather
                                         than with the durations — it is a count of people, not a time. -->
                                    <CounterUnattendedCard
                                        v-if="countersByService.pickup.length"
                                        :counters="countersByService.pickup"
                                        accent="#7E22CE"
                                    />
                                    <KpiCard :label="$t('explore.kpi.zoneCoverage')" icon="bi-grid-3x3-gap" accent="#7E22CE" size="sm"
                                        :noZones="!isPickUpZonePresent()"
                                        :empty="(zoneCoverageTextPickUpZone || '').startsWith('0/0')">
                                        <template #value>{{ zoneCoverageTextPickUpZone }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.zeroVisitZones')" icon="bi-x-circle" accent="#7E22CE" size="sm"
                                        :noZones="!isPickUpZonePresent()"
                                        :empty="!zeroVisitorZonesPickUpZone.length">
                                        <template #value>{{ zeroVisitorZonesPickUpZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroVisitorZonesPickUpZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.visitorsBy', { zone: $t('explore.zoneNoun.pickup') })" icon="bi-bar-chart" :height="340"
                                        :empty="!visitorsByPickUpZone?.length" accent="#7E22CE">
                                        <div class="barlist">
                                            <div v-for="row in visitorsByPickUpZone.sort((a,b)=>b.visitors-a.visitors)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.visitors/maxVisitorsPickUpZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.visitors) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                    <ChartPanel :title="$t('explore.chart.avgDwellBy', { zone: $t('explore.zoneNoun.pickup') })" icon="bi-stopwatch" :height="340"
                                        :empty="!avgDwellByPickUpZone?.length" accent="#7E22CE">
                                        <div class="barlist">
                                            <div v-for="row in avgDwellByPickUpZone.sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxAvgSecPickUpZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.totalTimeBy', { zone: $t('explore.zoneNoun.pickup') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!totalDurationRowsPickUpZone?.length" accent="#7E22CE">
                                        <div class="barlist">
                                            <div v-for="row in [...totalDurationRowsPickUpZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxTotalDurationPickUpZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                    <ChartPanel :title="$t('explore.chart.durationMixBy', { zone: $t('explore.zoneNoun.pickup') })" icon="bi-grid" :height="360"
                                        :empty="!percentageByDurationRangeInPickUpZone || !Object.keys(percentageByDurationRangeInPickUpZone).length" accent="#7E22CE">
                                        <ChartsFixedWidgetsPDDPercentageByDuration :percentageByDurationRangeInZone="percentageByDurationRangeInPickUpZone" :zonePresent="isPickUpZonePresent()" :active="activeTab === 'data-foundation-layer'" />
                                    </ChartPanel>

                                    <ChartPanel :title="$t('explore.chart.funnelBy', { kind: $t('explore.zoneKind.pickup'), zone: $t('explore.zoneNoun.pickup') })" icon="bi-diagram-3"
                                        :height="Math.max(260, 56 + pickUpZoneFunnelRows.length * 48)"
                                        :empty="!pickUpZoneFunnelRows.length" accent="#7E22CE">
                                        <div class="funnelz-table">
                                            <div class="funnelz-row funnelz-head">
                                                <div>{{ $t('explore.funnel.zone') }}</div>
                                                <div class="num">{{ $t('explore.funnel.visits') }}</div>
                                                <div class="num">{{ $t('explore.funnel.walkBys') }}</div>
                                                <div class="num">{{ $t('explore.funnel.shortLingerers') }}</div>
                                                <div class="num">{{ $t('explore.funnel.clearLingerers') }}</div>
                                            </div>
                                            <div v-for="r in pickUpZoneFunnelRows" :key="r.zone" class="funnelz-row">
                                                <div class="zone-name">{{ r.zone }}</div>
                                                <div class="num">{{ fmt(r.visitors) }}</div>
                                                <template v-if="r.strategyInsufficient">
                                                    <div class="merge-3 insufficient-msg">{{ $t('explore.funnel.insufficient') }}</div>
                                                </template>
                                                <template v-else>
                                                    <div class="num"><span>{{ fmt(r.walkbys.count) }}</span><small v-if="r.walkbys.count != null"> ({{ r.walkbys.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.shorts.count) }}</span><small v-if="r.shorts.count != null"> ({{ r.shorts.pct }})</small></div>
                                                    <div class="num"><span>{{ fmt(r.clears.count) }}</span><small v-if="r.clears.count != null"> ({{ r.clears.pct }})</small></div>
                                                </template>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- ========================= TRIPWIRE SUB-PANEL ========================= -->
                            <SubPanel
                                v-if="hasTripwirePickUpZones"
                                :label="$t('explore.sub.tripwire')"
                                :countText="$t('explore.count.zones', zoneStats.pickup.tripwire)"
                                icon="bi-arrow-left-right"
                                accent="#7E22CE"
                                tripwire
                            >
                                <KpiGrid>
                                    <KpiCard :label="$t('explore.kpi.topCrossings')" icon="bi-arrow-left-right" accent="#7E22CE"
                                        :empty="!(bestPickUpZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ bestPickUpZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(bestPickUpZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.lowestCrossings')" icon="bi-arrow-down" accent="#7E22CE"
                                        :empty="!(lowestPickUpZoneByCrossings?.crossing_count > 0)">
                                        <template #value>{{ lowestPickUpZoneByCrossings?.zone_name }}</template>
                                        <template #meta>{{ $t('explore.meta.crossings', { n: fmt(lowestPickUpZoneByCrossings?.crossing_count) }) }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.crossingCoverage')" icon="bi-grid-3x3-gap" accent="#7E22CE" size="sm"
                                        :empty="!crossingsByPickUpZone.length">
                                        <template #value>{{ crossingCoverageTextPickUpZone }}</template>
                                    </KpiCard>
                                    <KpiCard :label="$t('explore.kpi.zeroCrossingZones')" icon="bi-x-circle" accent="#7E22CE" size="sm"
                                        :empty="!zeroCrossingZonesPickUpZone.length">
                                        <template #value>{{ zeroCrossingZonesPickUpZone.length }}</template>
                                        <template #meta>
                                            <span>
                                                {{ summarizeList(zeroCrossingZonesPickUpZone, 4) }}
                                            </span>
                                        </template>
                                    </KpiCard>
                                    <KpiCard v-if="hasEstimatedPickUpZones" :label="$t('explore.kpi.topAvgDuration')" icon="bi-stopwatch" accent="#7E22CE"
                                        :empty="!(bestPickUpZoneByEstAvgDwell?.avg_duration > 0)">
                                        <template #value>
                                            {{ formattedDurationTime(bestPickUpZoneByEstAvgDwell?.avg_duration || 0) }}
                                            <span v-if="estDwellConfidence.pickup[bestPickUpZoneByEstAvgDwell?.zone_name]"
                                                  :class="['conf-chip', `conf-${estDwellConfidence.pickup[bestPickUpZoneByEstAvgDwell?.zone_name]}`]"
>{{ confidenceLabel(estDwellConfidence.pickup[bestPickUpZoneByEstAvgDwell?.zone_name]) }}</span>
                                            <span v-if="biasChipFor('pickup', bestPickUpZoneByEstAvgDwell?.zone_name)" :class="biasChipFor('pickup', bestPickUpZoneByEstAvgDwell?.zone_name).cls">{{ biasChipFor('pickup', bestPickUpZoneByEstAvgDwell?.zone_name).label }}</span>
                                        </template>
                                        <template #meta>{{ bestPickUpZoneByEstAvgDwell?.zone_name }}</template>
                                    </KpiCard>
                                </KpiGrid>

                                <ChartGrid style="padding-top:16px">
                                    <ChartPanel :title="$t('explore.chart.crossingsBy', { zone: $t('explore.zoneNoun.pickup') })" icon="bi-arrow-left-right" :height="340"
                                        :empty="!crossingsByPickUpZone?.some(r => r.crossings > 0)" accent="#7E22CE">
                                        <div class="barlist">
                                            <div v-for="row in [...crossingsByPickUpZone].sort((a,b)=>b.crossings-a.crossings)" :key="row.zone" class="barlist-row">
                                                <span class="barlist-label">{{ row.zone }}</span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.crossings/maxCrossingsPickUpZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmt(row.crossings) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedPickUpZones" :title="$t('explore.chart.estAvgDwellBy', { zone: $t('explore.zoneNoun.pickup') })" icon="bi-stopwatch" :height="340"
                                        :empty="!estAvgDwellByPickUpZone?.length" accent="#7E22CE">
                                        <div class="barlist">
                                            <div v-for="row in [...estAvgDwellByPickUpZone].sort((a,b)=>b.avgSec-a.avgSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.pickup[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.pickup[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.pickup[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('pickup', row.zone)" :class="biasChipFor('pickup', row.zone).cls">{{ biasChipFor('pickup', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.avgSec/maxEstAvgSecPickUpZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.avgSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>

                                    <ChartPanel v-if="hasEstimatedPickUpZones" :title="$t('explore.chart.estTotalTimeBy', { zone: $t('explore.zoneNoun.pickup') })" icon="bi-bar-chart-line" :height="340"
                                        :empty="!estTotalTimeByPickUpZone?.length" accent="#7E22CE">
                                        <div class="barlist">
                                            <div v-for="row in [...estTotalTimeByPickUpZone].sort((a,b)=>b.totalSec-a.totalSec)" :key="row.zone" class="barlist-row with-chip">
                                                <span class="barlist-label">
                                                    {{ row.zone }}
                                                    <span v-if="estDwellConfidence.pickup[row.zone]" :class="['conf-chip', `conf-${estDwellConfidence.pickup[row.zone]}`]">{{ confidenceLabel(estDwellConfidence.pickup[row.zone]) }}</span>
                                                    <span v-if="biasChipFor('pickup', row.zone)" :class="biasChipFor('pickup', row.zone).cls">{{ biasChipFor('pickup', row.zone).label }}</span>
                                                </span>
                                                <div class="barlist-bar"><div class="barlist-fill" :style="{ width: (row.totalSec/maxEstTotalTimePickUpZone*100)+'%' }"></div></div>
                                                <span class="barlist-value">{{ fmtSec(row.totalSec) }}</span>
                                            </div>
                                        </div>
                                    </ChartPanel>
                                </ChartGrid>
                            </SubPanel>

                            <!-- Counter unattended: somebody stood here and
                                 nobody was behind the counter. Different from
                                 queueing, which is the waiting zone. docs §28.6 -->
                            <CounterUnattendedPanel
                                v-if="countersByService.pickup.length"
                                :counters="countersByService.pickup"
                                accent="#7E22CE"
                            />
                        </Section>

                        <!-- WEATHER (data view; only present for stores with a location) -->
                        <Section v-show="exploreSubTab === 'sec-weather'" :hide-head="true" margin-top="6px">
                            <WeatherStoreCard
                                :signature="weatherSignature"
                                :zones-payload="weatherZones"
                                :store-name="storeName"
                            />
                        </Section>

                    </div>
                </MDBTabPane>

                <!-- ACTIONS (was Guidance) — the full opportunity lifecycle:
                     open → in progress → concluded. -->
                <MDBTabPane tabId="actions-layer">
                    <guidance-tab
                        v-if="activeTab === 'actions-layer'"
                        :opportunities="actionsPriorities"
                        :can-edit="canEditGuidance"
                        :is-active="activeTab === 'actions-layer'"
                        :focus-zone="focusZone"
                        :weather-outlook="weatherSignature && weatherSignature.outlook"
                    />
                </MDBTabPane>
                
            </MDBTabContent>
        </MDBTabs>
    </div>
    <!-- IA reorg: the per-tab "Allen's Insights" summary modal is retired. The store
         read is Today's briefing; on-demand questions go to the docked Ask Allen. -->

</template>

<script>
/* ============================================================
 * Imports
 * ========================================================== */
import HeatmapDashboard from './HeatmapDashboard.vue'
import { trendText } from '@/i18n/trend'
import ChartsDashboardEntrance from './ChartsDashboardEntrance.vue'
import ChartsFixedWidgetsPDDTotalDuration from './ChartsFixedWidgetsPDDTotalDuration.vue'
import ChartsFixedWidgetsPDDPercentageByDuration from './ChartsFixedWidgetsPDDPercentageByDuration.vue'
import ProductZoneDeepDive from './ProductZoneDeepDive.vue'
import GuidanceTab from '@/components/GuidanceTab.vue'
import { useUserStore } from '@/stores/UserStore'

import KpiCard from '@/components/ui/KpiCard.vue'
import ChartPanel from '@/components/ui/ChartPanel.vue'
import ServiceResponseTypePanel from '@/components/ServiceResponseTypePanel.vue'
import CheckoutTransactionsPanel from '@/components/CheckoutTransactionsPanel.vue'

import DurationDropdown from './DurationDropdown.vue'
import DropdownOpeningHoursMode from './DropdownOpeningHoursMode.vue'

// Stora UI shells
import ControlHead from '@/components/ui/ControlHead.vue'
import Section from '@/components/ui/Section.vue'
import SubPanel from '@/components/ui/SubPanel.vue'
import KpiGrid from '@/components/ui/KpiGrid.vue'
import ChartGrid from '@/components/ui/ChartGrid.vue'
import ZoneTypeNav from '@/components/ui/ZoneTypeNav.vue'
import TodayHero from '@/components/ui/TodayHero.vue'
import StrategicPositioningChart from '@/components/StrategicPositioningChart.vue'
import PatternMatrix from '@/components/PatternMatrix.vue'
import WeatherSignatureTile from '@/components/weather/WeatherSignatureTile.vue'
import WeatherStoreCard from '@/components/weather/WeatherStoreCard.vue'
import CounterUnattendedPanel from '@/components/CounterUnattendedPanel.vue'
import CounterUnattendedCard from '@/components/CounterUnattendedCard.vue'
import { computePriorities, buildZoneOverridesMap } from '@/prioritization/priorities'
import { PATTERN_META } from '@/prioritization/settings'
import { setAllenContext } from '@/composables/allenContext'
import {
  composeStrategicPositioning,
  QUADRANT_META,
} from '@/prioritization/strategicPositioning'
import { hasAnyTripwire } from '@/utils/tripwireShape'

import { MDBTabs, MDBTabNav, MDBTabContent, MDBTabItem, MDBTabPane } from 'mdb-vue-ui-kit'

import { getHeatmapByDuration, getEntranceChartDataByDuration, getTopPDDChartData, getAllenInsight, getWeatherInsight, getServiceResponseStore, getCheckoutTransactions, getCounterUnattendedStore } from "@/api/eventsAPI.js"

import { useDetectionZoneStore } from '@/stores/DetectionZone'
import { useZoneDirectoryStore } from '@/stores/ZoneDirectory'
import { useChangesTodayStore } from '@/stores/ChangesToday'
import { NOTICES, ZONE_TYPE_NAMES, movedInto, entranceTypeChange, settingChanged } from '@/zones/changesToday'
import ChangeNotice from '@/components/ui/ChangeNotice.vue'
import { zoneFromLink } from '@/zones/deepLink'
import { useOpeningHoursStore } from '@/stores/OpeningHoursStore'
import { useCompanyStore } from '@/stores/CompanyStore'

import { onMounted, onBeforeUnmount, onUnmounted, ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatEuro, formatPercent, formatDate, formatWeekday } from '@/i18n/format'
import { useRoute } from 'vue-router'

export default {
    name: 'FixedWidgets',

    emits: [
        'selectedDuration',
        'selectedOpeningHoursMode',
    ],

    components: {
        ChangeNotice,
        ServiceResponseTypePanel,
        CheckoutTransactionsPanel,
        CounterUnattendedPanel,
        CounterUnattendedCard,
        HeatmapDashboard,
        ChartsDashboardEntrance,
        ChartsFixedWidgetsPDDTotalDuration,
        ChartsFixedWidgetsPDDPercentageByDuration,
        ProductZoneDeepDive,
        GuidanceTab,
        DurationDropdown,
        DropdownOpeningHoursMode,
        MDBTabs,
        MDBTabNav,
        MDBTabContent,
        MDBTabItem,
        MDBTabPane,
        KpiCard,
        ChartPanel,
        ControlHead,
        Section,
        SubPanel,
        KpiGrid,
        ChartGrid,
        ZoneTypeNav,
        TodayHero,
        StrategicPositioningChart,
        PatternMatrix,
        WeatherSignatureTile,
        WeatherStoreCard,
    },

    props: {
        fixedWidgetsData: {
            type: Object,
            required: true
        },
        fixedWidgetsCompareToData: {
            type: Object,
            required: true
        },
        // Stable 30-day snapshot for Actions — its opportunity suggestions read
        // this, never the picker-driven data, so slicing Zones/Explore leaves the
        // Actions lifecycle untouched.
        fixedWidgetsData30d: {
            type: Object,
            default: () => ({})
        },
    },

    setup(props, { emit }) {
        const { t } = useI18n()
        // English keeps the exact output it always had; only Dutch goes through
        // @/i18n/format (1.234 · 12,5% · € 1.234 · 1 uur 5 min).
        const isEn = () => currentLanguage() === 'en'
        /* -----------------------------
        * App / UI state
        * --------------------------- */
        // IA reorg: the dashboard lands on Actions (the loop hub) — drill down to
        // Zones, then Explore. Today is the app's real landing page. No tiers —
        // everyone gets every tab.
        const activeTab = ref('actions-layer')
        const focusZone = ref('')   // deep-link: zone whose Actions card to scroll to + flash
        const route = useRoute()
        const windowWidth = ref(window.innerWidth)
        const dateRangeMode = ref("duration")
        const selectedDuration  = ref(30)
        // Service response (docs/reference/service_response_measurement.md), keyed by
        // DetectionZoneTypeId so each Explore zone-type section reads its own block.
        const serviceResponseByType = ref({})
        const checkoutTransactions = ref(null)
        const counterUnattended = ref([])

        // Grouped so each Explore zone-type section reads only its own counters. Never
        // merged across services: an info desk and a checkout are different problems
        // with different fixes, and one combined figure would hide which.
        const countersByService = computed(() => {
            const out = { checkout: [], info: [], service: [], pickup: [] }
            for (const c of counterUnattended.value) {
                if (out[c.service]) out[c.service].push(c)
            }
            return out
        })

        // The strip carries this for CHECKOUT only, because checkout is the only
        // service counter the strip already has figures for. Info / Service / Pick Up
        // report in their own Explore sections instead — three more tiles would crowd
        // out the store numbers the strip exists for, and summing them would hide which
        // counter has the problem. docs §28.10
        const checkoutUnattendedRate = computed(() => {
            // Owner's-limit view: 1 in N customers found a checkout unattended LONGER than
            // the limit the shop set (the Checkout Interaction zone's TriggerActionThreshold,
            // via ownerThresholdWaits). Absent when no checkout has a limit set, or none was
            // exceeded — no zero, no tile. Never "waited" — that is the queueing zone.
            const measured = counterUnattended.value.filter(
                c => c.state === 'measured' && c.service === 'checkout'
                     && c.ownerThresholdWaits && c.ownerThresholdWaits.count > 0)
            if (!measured.length) return null
            // Visit-weighted, so a quiet checkout cannot swing the store figure.
            const visits = measured.reduce((n, c) => n + (c.visits || 0), 0)
            const affected = measured.reduce(
                (n, c) => n + (c.ownerThresholdWaits.count || 0), 0)
            if (!visits || !affected) return null
            return Math.max(2, Math.round(visits / affected))
        })

        const serviceResponsePeriodLabel = computed(() => {
            const d = effectiveDurationDays.value
            return d === 1 ? t('explore.period.yesterday') : t('explore.period.lastNDaysLower', { n: d })
        })
        // Redis slot name for the ETL rollup key. Unknown values fall back to the
        // 30-day view rather than requesting a key that cannot exist.
        const serviceResponseSlot = computed(() => ({
            1: 'yesterday', 7: 'past_7_days', 30: 'past_30_days', 90: 'past_90_days',
            365: 'past_365_days', 1000: 'this_week', 2000: 'this_month',
            3000: 'last_week', 4000: 'last_month',
        }[selectedDuration.value] || 'past_30_days'))

        const loadServiceResponse = async () => {
            const r = await getServiceResponseStore(serviceResponseSlot.value)
            serviceResponseByType.value = r || {}
        }

        // Counter unattended (docs §28.6). Every counter, including the ones that
        // cannot be measured yet — the states are the useful part until stores draw
        // their staff zones. (`counterUnattended` is declared above, next to the
        // computed that reads it.)
        const loadCounterUnattended = async () => {
            const r = await getCounterUnattendedStore(
                serviceResponseSlot.value, selectedOpeningHoursMode.value || '')
            counterUnattended.value = (r && r.counters) || []
        }

        const loadCheckoutTransactions = async () => {
            const r = await getCheckoutTransactions(
                serviceResponseSlot.value, selectedOpeningHoursMode.value || '')
            // 'pending' carries no byZone, so the panel renders nothing until the
            // nightly run has produced a figure.
            checkoutTransactions.value = (r && r.byZone) ? r : null
        }

        const selectedOpeningHoursMode = ref('only_regular_opening_hours')
        const disabledOpeningHourModes = ref([])

        /* -----------------------------
        * Stores
        * --------------------------- */
        const detectionZoneStore = useDetectionZoneStore()
        const openingHoursStore = useOpeningHoursStore()
        const companyStore = useCompanyStore()

        /* -----------------------------
        * Weather insight (nightly-precomputed: store signature + per-zone signals)
        * --------------------------- */
        const weatherSignature = ref(null)
        const weatherZones = ref(null)
        const storeName = computed(() => companyStore.company?.CompanyName || '')
        // Weather-enabled iff the route returned data (a configured lat/long location);
        // 'unavailable' -> the Explore Weather tab + Intelligence tile stay hidden.
        const weatherEnabled = computed(() =>
            !!(weatherZones.value && !weatherZones.value.status))
        const loadWeatherInsight = async () => {
            try {
                const [sig, zones] = await Promise.all([
                    getWeatherInsight({ part: 'signature' }),
                    getWeatherInsight({ part: 'zones' }),
                ])
                weatherSignature.value = (sig && !sig.status) ? sig : null
                weatherZones.value = zones || null
            } catch (e) {
                console.error('weather insight load failed', e)
            }
        }

        // companySector: the company-wide retail-sector key configured in
        // Settings → "Company Benchmark — Retail Sector". Drives the
        // starting Emax/Cmax/TWT ceilings everywhere via the :segment-key
        // prop on children. Falls back to 'default' when the admin has
        // not yet picked a sector — child components resolve 'default'
        // to the DEFAULT_SECTOR fallback inside ceilingsRef.
        const companySector = computed(() =>
            companyStore.company?.CompanySettings?.sector || 'default'
        )

        // zoneOverrides: {zoneName → sectorOverride} built once from the
        // detection-zone store snapshot. Lets one zone behave as a
        // different sector ("pharmacy endcap inside our DIY store").
        // Empty object means every zone uses the company sector.
        const zoneOverrides = computed(() =>
            buildZoneOverridesMap(detectionZoneStore)
        )

        // companyStaffCost: optional company-level override for the
        // per-hour staff cost the Capacity gauge uses to monetise
        // wasted capacity / missed throughput. Set in Settings →
        // Checkout Staff Cost. null when not set → gauge falls back
        // to the sector default in CAPACITY_VALUE_SETTINGS.
        const companyStaffCost = computed(() => {
            const v = companyStore.company?.CompanySettings?.staffCostPerHour
            return (typeof v === 'number' && Number.isFinite(v) && v > 0) ? v : null
        })

        const uniqueDetectionZoneTypeIds = ref([])
        const allZones = ref(null)
        const productZoneCount = ref(0)
        const waitingZoneCount = ref(0)
        const interactionLaneCount = ref(0)
        const cashierLaneCount     = ref(0)
        // Service-like zone counts — surfaced in the Service gauge footer.
        const serviceZoneCount = ref(0)
        const infoZoneCount    = ref(0)
        const pickUpZoneCount  = ref(0)

        /* -----------------------------
        * Heatmap / entrance charts
        * --------------------------- */
        const heatmapData = ref([])
        const entranceChartData = ref({})

        /* -----------------------------
        * “Allen’s insights” modal + job tracking
        * --------------------------- */
        const showAllensInsights = ref(false)
        const storeAnalytics = ref("")
        const jobStatus    = ref(null)
        const isMounted = ref(true)
        let currentFetchController = null
        let latestRequestId = 0
        const POLL_INTERVAL_MS = 2000

        /* -----------------------------
        * Store-level metrics
        * --------------------------- */ 
        const currentWeekday = ref()
        const todaysCurrentCount = ref(0)
        const todaysCurrentCombinedCountStoreIn = ref(0)
        const todaysCurrentCombinedCountStoreOut = ref(0)
        const todaysCurrentExit = ref(0)

        const averageVisitDuration = ref(0)
        // Mode C (Little's-Law) store dwell estimate. Backend only populates
        // this when the company has exactly one active entry/exit zone, it is
        // a Combined EE with tripwires, and the zone has opted into
        // UseTripwireForDwell — see bgtaskdatafetching.process_dashboard_data_for_company.
        const storeDwellEstimateSeconds = ref(null)
        const storeDwellConfidence = ref(null)
        const storeDwellBiasPct = ref(null)
        const averageValueSameWeekday = ref(0)
        const averageValue = ref(0)

        const highestTotalHour = ref()
        const highestTotalHourCount = ref(0)
        const highestTotalDay = ref()
        const highestTotalDayCount = ref(0)
        const busiestHour = ref()
        const highestAverageHourlyCount = ref(0)
        const trend = ref("")
        // Structured companion to the trend sentence; absent on payloads
        // exported before the trend estimator was rebuilt.
        const trendDetail = ref(null)
        // Atomic with the data (was a ref+watcher that lagged a tick behind
        // topPriorities on a slot change → reach-€ denominator mismatch flash).
        const storeTotalVisitors = computed(() => Number(props.fixedWidgetsData?.store_total_visitors) || 0)

        // Entrance data-outage caveat: the pipeline stamps `store_data_integrity`
        // on the single-day (yesterday) widget when the entrance camera missed part
        // of an OPEN day, so the count is a partial total. Only present on flagged
        // days, so the badge is invisible in normal operation.
        const storeDataIntegrity = computed(() => {
            const s = props.fixedWidgetsData?.store_data_integrity
            return (s && s.status === 'partial_outage') ? s : null
        })
        
        /* -----------------------------
        * Product/Demo/Display data (PDD)
        * --------------------------- */
        const bestProductZoneByVisitors = ref({})
        const bestProductZoneByAvgDuration = ref({})
        const worstProductZoneByVisitors = ref({})
        const totalDurationPerProductZone = ref({})
        const percentageByDurationRangeInProductZone = ref({})
        const productZoneVisitorsCounts = ref({})
        const productZoneClearLingerersCounts = ref({})
        const productHotcoldByVisitors = ref([])
        const productHotcoldByClearLingerers = ref([])
        const productZoneFunnels = ref({})

        // Intelligence tab is funnel-driven (visitors → engaged → converted) and needs
        // dwell data, which only polygon zones produce. Tripwire-only zones (incl. Mode C
        // dwell-estimated ones) are excluded from polygon-led charts by filtering against
        // the names of polygon-bearing zones derived from the authoritative DetectionZones
        // list (which already excludes IsActive=False rows server-side).
        const _parsePoly = (v) => {
            if (Array.isArray(v)) return v
            if (typeof v === 'string' && v.length) {
                try { return JSON.parse(v) } catch { return null }
            }
            return null
        }
        const _makePolygonZoneNames = (typeId) => computed(() => {
            const zones = allZones.value || []
            return new Set(
                zones
                    .filter(z => z.DetectionZoneTypeId === typeId)
                    .filter(z => {
                        const pts = _parsePoly(z?.DetectionZonePointArray)
                        return Array.isArray(pts) && pts.length >= 3
                    })
                    .map(z => z.DetectionZoneName)
            )
        })
        const polygonProductZoneNames = _makePolygonZoneNames(6)
        const polygonDemoZoneNames    = _makePolygonZoneNames(7)
        const polygonDisplayZoneNames = _makePolygonZoneNames(8)
        const polygonInfoZoneNames    = _makePolygonZoneNames(9)
        const polygonServiceZoneNames = _makePolygonZoneNames(10)
        const polygonPickUpZoneNames  = _makePolygonZoneNames(11)

        const filterByPolygonProductZones = (obj) => {
            const names = polygonProductZoneNames.value
            if (!names.size) return obj
            return Object.fromEntries(
                Object.entries(obj || {}).filter(([zone]) => names.has(zone))
            )
        }
        const filterArrayByPolygonProductZones = (arr) => {
            const names = polygonProductZoneNames.value
            if (!names.size) return arr
            return (arr || []).filter(row => names.has(row?.zone))
        }

        const productZoneVisitorsCountsFiltered = computed(() => filterByPolygonProductZones(productZoneVisitorsCounts.value))
        const productZoneClearLingerersCountsFiltered = computed(() => filterByPolygonProductZones(productZoneClearLingerersCounts.value))
        const productHotcoldByVisitorsFiltered = computed(() => filterArrayByPolygonProductZones(productHotcoldByVisitors.value))
        const productHotcoldByClearLingerersFiltered = computed(() => filterArrayByPolygonProductZones(productHotcoldByClearLingerers.value))
        const productZoneFunnelsFiltered = computed(() => filterByPolygonProductZones(productZoneFunnels.value))

        const bestDemoZoneByVisitors = ref({})
        const bestDemoZoneByAvgDuration = ref({})
        const worstDemoZoneByVisitors = ref({})
        const totalDurationPerDemoZone = ref({})
        const percentageByDurationRangeInDemoZone = ref({})
        const demoZoneVisitorsCounts = ref({})
        const demoZoneClearLingerersCounts = ref({})
        const demoHotcoldByVisitors = ref([])
        const demoHotcoldByClearLingerers = ref([])
        const demoZoneFunnels = ref({})

        const bestDisplayZoneByVisitors = ref({})
        const bestDisplayZoneByAvgDuration = ref({})
        const worstDisplayZoneByVisitors = ref({})
        const totalDurationPerDisplayZone = ref({})
        const percentageByDurationRangeInDisplayZone = ref({})
        const displayZoneVisitorsCounts = ref({})
        const displayZoneClearLingerersCounts = ref({})
        const displayHotcoldByVisitors = ref([])
        const displayHotcoldByClearLingerers = ref([])
        const displayZoneFunnels = ref({})

        // Info / Service / Pick Up dwell zone polygon data (Phase 8.3.d)
        const bestInfoZoneByVisitors = ref({})
        const bestInfoZoneByAvgDuration = ref({})
        const worstInfoZoneByVisitors = ref({})
        const totalDurationPerInfoZone = ref({})
        const percentageByDurationRangeInInfoZone = ref({})
        const infoZoneVisitorsCounts = ref({})
        const infoZoneClearLingerersCounts = ref({})
        const infoHotcoldByVisitors = ref([])
        const infoHotcoldByClearLingerers = ref([])
        const infoZoneFunnels = ref({})

        const bestServiceZoneByVisitors = ref({})
        const bestServiceZoneByAvgDuration = ref({})
        const worstServiceZoneByVisitors = ref({})
        const totalDurationPerServiceZone = ref({})
        const percentageByDurationRangeInServiceZone = ref({})
        const serviceZoneVisitorsCounts = ref({})
        const serviceZoneClearLingerersCounts = ref({})
        const serviceHotcoldByVisitors = ref([])
        const serviceHotcoldByClearLingerers = ref([])
        const serviceZoneFunnels = ref({})

        const bestPickUpZoneByVisitors = ref({})
        const bestPickUpZoneByAvgDuration = ref({})
        const worstPickUpZoneByVisitors = ref({})
        const totalDurationPerPickUpZone = ref({})
        const percentageByDurationRangeInPickUpZone = ref({})
        const pickUpZoneVisitorsCounts = ref({})
        const pickUpZoneClearLingerersCounts = ref({})
        const pickUpHotcoldByVisitors = ref([])
        const pickUpHotcoldByClearLingerers = ref([])
        const pickUpZoneFunnels = ref({})

        /* -----------------------------
        * Tripwire crossings per zone type (Phase 8.3.c — Product/Demo/Display).
        * Backend provides best/lowest crossing zone, per-zone counts, and
        * tripwire/polygon active-zone totals for each type. Used to drive
        * the Crossings KPI cards + bar chart and the polygon-vs-tripwire
        * show/hide logic.
        * --------------------------- */
        const bestProductZoneByCrossings = ref({})
        const lowestProductZoneByCrossings = ref({})
        const productZoneCrossingsCounts = ref({})
        const productZonesWithTripwireTotal = ref(0)
        const productZonesWithPolygonTotal = ref(0)

        const bestDemoZoneByCrossings = ref({})
        const lowestDemoZoneByCrossings = ref({})
        const demoZoneCrossingsCounts = ref({})
        const demoZonesWithTripwireTotal = ref(0)
        const demoZonesWithPolygonTotal = ref(0)

        const bestDisplayZoneByCrossings = ref({})
        const lowestDisplayZoneByCrossings = ref({})
        const displayZoneCrossingsCounts = ref({})
        const displayZonesWithTripwireTotal = ref(0)
        const displayZonesWithPolygonTotal = ref(0)

        const bestInfoZoneByCrossings = ref({})
        const lowestInfoZoneByCrossings = ref({})
        const infoZoneCrossingsCounts = ref({})
        const infoZonesWithTripwireTotal = ref(0)
        const infoZonesWithPolygonTotal = ref(0)

        const bestServiceZoneByCrossings = ref({})
        const lowestServiceZoneByCrossings = ref({})
        const serviceZoneCrossingsCounts = ref({})
        const serviceZonesWithTripwireTotal = ref(0)
        const serviceZonesWithPolygonTotal = ref(0)

        const bestPickUpZoneByCrossings = ref({})
        const lowestPickUpZoneByCrossings = ref({})
        const pickUpZoneCrossingsCounts = ref({})
        const pickUpZonesWithTripwireTotal = ref(0)
        const pickUpZonesWithPolygonTotal = ref(0)

        /* Top charts for PDD carousel */
        const bestProductAvgDur = ref({})
        const bestProductCount = ref({})
        const bestDemoAvgDur = ref({})
        const bestDemoCount = ref({})
        const bestDisplayAvgDur = ref({})
        const bestDisplayCount = ref({})

        /* -----------------------------
        * Checkout/Interaction KPIs
        * --------------------------- */
        const averageWaitingTime = ref(0)
        const averageTimeInZoneWaiting = ref(0)
        const maxWaitingDuration = ref(0)
        const maxWaitingZoneName = ref("")
        // Typical widgets carry the geometric center of the busiest log2-bucket
        // of "real" engagements: typical*.value (single number); the matching
        // *count* ref holds how many real waits/transactions fell in that bucket.
        const mostTypicalWaitingTime = ref(0)
        const amountMostTypicalWaitingTime = ref(0)
        const realWaitRate = ref(0)
        const realWaitCount = ref(0)

        // Checkout queue (line length from waiting-zone occupancy overlap).
        const checkoutQueue = ref(null)
        const queuePct = computed(() => Number(checkoutQueue.value?.pct_time_ge2 ?? 0))
        const queueVerdict = computed(() => {
            const p = queuePct.value
            if (p < 5) return t('explore.queue.rarely')
            if (p < 15) return t('explore.queue.sometimes')
            return t('explore.queue.often')
        })

        const averageInteractionTime = ref(0)
        const averageTimeInZoneInteraction = ref(0)
        const maxInteractionDuration = ref(0)
        const maxInteractionZoneName = ref("")
        const mostTypicalInteractionTime = ref(0)
        const amountMostTypicalInteractionTime = ref(0)
        const realTransactionRate = ref(0)
        const realTransactionCount = ref(0)

        const fastestCheckoutZone = ref("")
        const slowestCheckoutZone = ref("")
        const fastestInteractionZone = ref("")
        const slowestInteractionZone = ref("")
        const mostUsedCheckout = ref("")
        const leastUsedCheckout = ref("")

        /* -----------------------------
        * Loading flags (per widget)
        * --------------------------- */
        const isLoadingAverageSameWeekday = ref(true)
        const isLoadingAverage = ref(true)
        const isLoadingHighestHour = ref(true)
        const isLoadingHighestDay = ref(true)
        const isLoadingBusiestHour = ref(true)
        const isLoadingTrend = ref(true)
        
        const isLoadingbestProductZoneInCounts = ref(true)
        const isLoadingbestProductZoneInAvgDuration = ref(true)
        const isLoadingworstProductZoneInCounts = ref(true)

        const isLoadingbestDemoZoneInCounts = ref(true)
        const isLoadingbestDemoZoneInAvgDuration = ref(true)
        const isLoadingworstDemoZoneInCounts = ref(true)

        const isLoadingbestDisplayZoneInCounts = ref(true)
        const isLoadingbestDisplayZoneInAvgDuration = ref(true)
        const isLoadingworstDisplayZoneInCounts = ref(true)

        const isLoadingaverageWaitingTime = ref(true)
        const isLoadingaverageTimeInZoneWaiting = ref(true)
        const isLoadingmaxWaitingDuration = ref(true)
        const isLoadingmaxWaitingZoneName = ref(true)
        const isLoadingmostTypicalWaitingTime = ref(true)
        const isLoadingRealWaitRate = ref(true)
        const isLoadingaverageInteractionTime = ref(true)
        const isLoadingaverageTimeInZoneInteraction = ref(true)
        const isLoadingmaxInteractionDuration = ref(true)
        const isLoadingmaxInteractionZoneName = ref(true)
        const isLoadingmostTypicalInteractionTime = ref(true)
        const isLoadingRealTransactionRate = ref(true)
        const isLoadingfastestCheckoutZone = ref(true)
        const isLoadingslowestCheckoutZone = ref(true)
        const isLoadingfastestInteractionZone = ref(true)
        const isLoadingslowestInteractionZone = ref(true)
        const isLoadingmostUsedCheckout = ref(true)
        const isLoadingleastUsedCheckout = ref(true)

        /* ==========================================================
        * Formatting helpers
        * ======================================================== */
        const fmt = (n) => (n == null ? '—' : isEn() ? n.toLocaleString() : formatNumber(n, { decimals: Number.isInteger(Number(n)) ? 0 : 1 }))
        
        // currency + safe division helpers
        const asMoney = (n) => 
            n == null 
                ? '—'
                : !isEn() ? formatEuro(n)
                : n.toLocaleString(undefined, {
                    style:'currency',
                    currency:'EUR',
                    maximumFractionDigits:0
                })
        
        const safeDiv = (a,b) => (b && b !== 0) ? (a / b) : 0

        const fmtPct = (p) => (p==null ? '—' : isEn() ? `${(p*100).toFixed(1)}%` : formatPercent(p * 100, { decimals: 1 }))

        const pct = (part, total) => total && part != null
            ? (isEn() ? `${((part / total) * 100).toFixed(1)}%` : formatPercent((part / total) * 100, { decimals: 1 }))
            : '—'

        // A percentage the backend already rounded: "23.4%" in English as always, "23,4%" in Dutch.
        const pctRaw = (v) => (isEn() ? `${v}%` : `${String(v).replace('.', ',')}%`)

        // Little's-law confidence chip ('high' | 'medium' | 'low'): "high confidence" / "hoge zekerheid".
        const confidenceLabel = (conf) => {
            const key = `explore.confidence.${String(conf).toLowerCase()}`
            return ['high', 'medium', 'low'].includes(String(conf).toLowerCase())
                ? t(key) : t('explore.confidence.other', { conf })
        }

        const formattedDurationTime = computed(() => (seconds) => {
            // Round to whole seconds FIRST so the seconds part can never render as 60
            // (e.g. 899.6s -> "15 min 0 sec", not "14 min 60 sec").
            const total = Math.round(Number(seconds) || 0)
            if (total < 60) return `${total} sec`
            if (total < 3600) {
                const m = Math.floor(total / 60)
                const s = total % 60
                return `${m} min ${s} sec`
            }
            const h = Math.floor(total / 3600)
            const m = Math.floor((total % 3600) / 60)
            const s = total % 60
            return `${h} ${isEn() ? 'h' : 'uur'} ${m} min ${s} sec`
        })
        
        const fmtSec = (s) => 
            formattedDurationTime.value ? 
                formattedDurationTime.value(s) 
                : `${Math.round(s)}s`

        // Keep the list readable inside the card
        const summarizeList = (arr, n = 4) => {
            if (!arr?.length) return ''
            if (arr.length <= n) return arr.join(', ')
            const head = arr.slice(0, n).join(', ')
            return t('explore.meta.listMore', { head, n: arr.length - n })
        }

        const hasInsufficientStrategyVisitorCutOff = (f) => {
            const s = String(f?.strategy ?? '')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '_')
            return s === 'insufficient_data'
        }

        /* ==========================================================
        * Presence helpers (which zone-types exist)
        * ======================================================== */
        const isEntranceZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(1)
        }

        const isExitZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(2)
        }

        const isCombinedEntranceExitZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(3)
        }

        const isCheckoutWaitingZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(4)
        }

        const isCheckoutInteractionZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(5)
        }

        const isProductZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(6)
        }

        const isDemoZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(7)
        }

        const isDisplayZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(8)
        }

        const isInfoZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(9)
        }

        const isServiceZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(10)
        }

        const isPickUpZonePresent = () => {
            return uniqueDetectionZoneTypeIds.value.includes(11)
        }

        /* ==========================================================
        * Product derived data (lists, funnels, coverage)
        * ======================================================== */
        const productZoneFunnelsObj = computed(() => 
            props.fixedWidgetsData?.product_zone_funnels || {}
        )

        // zone names: prefer funnels keys, else visitors_counts keys
        const productZoneNames = computed(() => {
            const funnelKeys = Object.keys(productZoneFunnelsObj.value || {})
            if (funnelKeys.length) return funnelKeys
            return Object.keys(productZoneVisitorsCounts.value || {})
        })

        // Polygon-only Visitors / Avg Dwell. Mode C tripwire-estimated zones
        // get their own tripwire-panel charts below; keeping the polygon
        // charts pure prevents mixed measured/estimated rows.
        const visitorsByProductZone = computed(() => {
            const poly = polygonProductZoneNames.value
            return productZoneNames.value
                .filter(z => !poly.size || poly.has(z))
                .map(z => {
                    const f = productZoneFunnelsObj.value?.[z]
                    const visitors = f != null
                        ? Number(f.zone_visitors ?? 0)
                        : Number(productZoneVisitorsCounts.value?.[z] ?? 0)
                    return { zone: z, visitors }
                })
                .filter(r => r.visitors > 0)
        })

        const avgDwellByProductZone = computed(() => {
            const poly = polygonProductZoneNames.value
            return productZoneNames.value
                .filter(z => !poly.size || poly.has(z))
                .map(z => {
                    const total = totalDurationPerProductZone.value?.[z] ?? 0
                    const vis = Number(productZoneVisitorsCounts.value?.[z] ?? 0)
                    return { zone: z, avgSec: safeDiv(total, vis) }
                })
                .filter(r => r.avgSec > 0)
        })

        const clearLingerersByProductZone = computed(() =>
            productZoneNames.value.map(z => {
                const f = productZoneFunnelsObj.value?.[z]
                const clears = f != null
                ? Number(f.clear_lingerers ?? 0)
                : Number(productZoneClearLingerersCounts.value?.[z] ?? 0)
                return { zone: z, clears }
            })
        )

        const funnelsByProductZone = computed(() =>
            productZoneNames.value.map(z => {
                const f = productZoneFunnelsObj.value?.[z]
                if (f != null) {
                const visitors = Number(f.zone_visitors ?? 0)
                const clears   = Number(f.clear_lingerers ?? 0)
                const conv     = Number(f.conversion_rate ?? 0)
                const apv      = Number(f.average_product_value ?? 0)
                const engSales = clears * conv * apv
                return { zone: z, visitors, clears, conv, apv, engSales }
                } else {
                // fallback to previous logic
                const f2   = productZoneFunnels.value?.[z] || {}
                const vis  = Number(productZoneVisitorsCounts.value?.[z] ?? 0)
                const clr  = Number(productZoneClearLingerersCounts.value?.[z] ?? 0)
                const conv = Number(f2.conversion_rate ?? 0)
                const apv  = Number(f2.average_product_value ?? 0)
                const eng  = clr * conv * apv
                return { zone: z, visitors: vis, clears: clr, conv, apv, engSales: eng }
                }
            })
        )

        // simple min–max for bar widths
        const maxVisitorsProductZone = computed(() => 
            Math.max(1, ...visitorsByProductZone.value.map(x=>x.visitors))
        )
        
        const maxAvgSecProductZone = computed(() => 
            Math.max(1, ...avgDwellByProductZone.value.map(x=>x.avgSec))
        )

        const topClearLingerersProductZone = computed(() => 
            [...clearLingerersByProductZone.value].sort((a,b)=>b.clears-a.clears)[0]
        )
        
        const topConversionProductZone = computed(() => 
            [...funnelsByProductZone.value].sort((a,b)=>b.conv-a.conv)[0]
        )

        const topSalesPotentialProductZone = computed(() => 
            [...funnelsByProductZone.value].sort((a,b)=>b.engSales-a.engSales)[0]
        )

        const zonesWithTrafficProductZone = computed(() => {
            const poly = polygonProductZoneNames.value
            return visitorsByProductZone.value
                .filter(x => x.visitors > 0 && (!poly.size || poly.has(x.zone)))
                .length
        })

        const zoneCoverageTextProductZone = computed(() => {
            const total = polygonProductZoneNames.value.size || productZoneNames.value.length
            return t('explore.kpi.coverageTraffic', { n: zonesWithTrafficProductZone.value, total })
        })

        // Table rows for “Product Funnel by Zone”
        const zoneVisitorsProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_visitors_counts || {}
        )
        
        const zoneWalkbysProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_walkbys_counts || {}
        )

        const zoneShortsProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_short_lingerers_counts || {}
        )
        
        const zoneClearsProductZone = computed(() => 
            props.fixedWidgetsData?.product_zone_clear_lingerers_counts || {}
        )
        
        const productZoneFunnelRows = computed(() => {
            const funnels = productZoneFunnelsObj.value || {}
            const polyNames = polygonProductZoneNames.value
            // Funnel/walkby/short/clear breakdown is polygon-only — tripwire-only
            // zones (incl. Mode C) cannot produce these segments and are dropped.
            const funnelKeys = Object.keys(funnels)
                .filter(z => !polyNames.size || polyNames.has(z))

            if (funnelKeys.length) {
                // build rows straight from funnels
                const rows = funnelKeys.map(zone => {
                    const f = funnels[zone] || {}
                    const visitors = Number(f.zone_visitors ?? 0)   // keep 0-visitors rows
                    const walkbys  = Number(f.walk_bys ?? 0)
                    const shorts   = Number(f.short_lingerers ?? 0)
                    const clears   = Number(f.clear_lingerers ?? 0)
                    const insufficient = hasInsufficientStrategyVisitorCutOff(f)
                    return insufficient
                        ? {
                            zone,
                            visitors,
                            strategyInsufficient: true,          // ← flag for template
                            // keep these numeric fields (not rendered)
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                        : {
                            zone,
                            visitors,
                            strategyInsufficient: false,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                })
                
                rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                return rows
            }

            // fallback: merge legacy *_counts (polygon-only)
            const polyNamesFb = polygonProductZoneNames.value
            const keys = new Set([
                ...Object.keys(zoneVisitorsProductZone.value || {}),
                ...Object.keys(zoneWalkbysProductZone.value || {}),
                ...Object.keys(zoneShortsProductZone.value || {}),
                ...Object.keys(zoneClearsProductZone.value || {}),
            ])

            const rows = []
            for (const k of keys) {
                if (polyNamesFb.size && !polyNamesFb.has(k)) continue
                const visitors = Number(zoneVisitorsProductZone.value?.[k] ?? 0)
                const walkbys  = Number(zoneWalkbysProductZone.value?.[k] ?? 0)
                const shorts   = Number(zoneShortsProductZone.value?.[k] ?? 0)
                const clears   = Number(zoneClearsProductZone.value?.[k] ?? 0)
                rows.push({
                    zone: k,
                    visitors,
                    strategyInsufficient: false,                  // ← always false in fallback
                    walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                    shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                    clears:  { count: clears,  pct: pct(clears,  visitors) },
                })
            }

            rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
            return rows
        })

        // Best/worst visitors from the rows (used in KPI cards)
        const bestVisitorsFromRowsProductZone = computed(() => {
            const rows = (productZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rows.length) return null
            rows.sort(
                (a, b) =>
                (b.visitors || 0) - (a.visitors || 0) || a.zone.localeCompare(b.zone)
            )
            return rows[0] // { zone, visitors, walkbys:{}, shorts:{}, clears:{} }
        })

        const worstVisitorsFromRowsProductZone = computed(() => {
            const rowsAll = (productZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rowsAll.length) return null

            const rowsPos = rowsAll.filter(r => (r.visitors || 0) > 0)
            const rows = rowsPos.length ? rowsPos : rowsAll

            rows.sort(
                (a, b) =>
                (a.visitors || 0) - (b.visitors || 0) || a.zone.localeCompare(b.zone)
            );
            return rows[0]
        })

        // Zones with exactly zero visitors (from the funnel rows)
        const zeroVisitorZonesProductZone = computed(() =>
            (productZoneFunnelRows.value || [])
                .filter(r => (r.visitors ?? 0) === 0)
                .map(r => r.zone)
                .sort((a, b) => a.localeCompare(b))
        )

        const highestEngagementZoneProductZone = computed(() => {
            const visitors = productZoneVisitorsCounts.value || {}          // { Zone: count }
            const clears   = productZoneClearLingerersCounts.value || {}    // { Zone: count }

            let best = { zone: '—', ratio: 0, clears: 0, visitors: 0 }
            for (const [zone, v] of Object.entries(visitors)) {
                const c = Number(clears[zone] || 0)
                if (v > 0) {
                const r = c / v
                if (r > best.ratio) best = { zone, ratio: r, clears: c, visitors: v }
                }
            }
            return best
        })

        const _estProduct = _makeEstComputeds({
            typeKey:                'product',
            polygonNamesRef:        polygonProductZoneNames,
            visitorsCountsRef:      productZoneVisitorsCounts,
            totalDurationRef:       totalDurationPerProductZone,
            polygonAvgRowsRef:      avgDwellByProductZone,
            polygonVisitorsRowsRef: visitorsByProductZone,
        })

        // Flat {zoneName: { avgSec, confidence }} map across all zone-types,
        // built from the estimated-dwell rows. Used by ProductZoneDeepDive
        // to render the per-zone dwell callout on tripwire-only zones.
        const tripwireDwellMap = computed(() => {
            const out = {}
            const confidences = estDwellConfidence.value || {}
            const sources = [
                { rowsRef: _estProduct.estAvgDwellByZone, conf: confidences.product },
            ]
            for (const { rowsRef, conf } of sources) {
                for (const row of (rowsRef?.value || [])) {
                    if (!row?.zone) continue
                    out[row.zone] = {
                        avgSec: Number(row.avgSec || 0),
                        confidence: (conf || {})[row.zone] || null,
                    }
                }
            }
            return out
        })

        // True when at least one product zone has a polygon (so engagement /
        // conversion / € efficiency math can run). When false, the hero
        // card and Commercial Efficiency gauge would otherwise paint a
        // misleading "0% / Critical / High leakage" picture even though
        // they couldn't measure anything. Surfaces to TodayHero and
        // StoreEfficiencyGauge so they can render an honest "not
        // measurable" state instead.
        const hasMeasurableProductZones = computed(() =>
            polygonProductZoneNames.value.size > 0
        )
        const totalDurationPerProductZonePoly = _estProduct.totalDurationPerZonePoly
        const totalDurationRowsProductZone    = _estProduct.totalDurationRowsPoly
        const maxTotalDurationProductZone     = _estProduct.maxTotalDurationPoly
        const estVisitorsByProductZone        = _estProduct.estVisitorsByZone
        const estAvgDwellByProductZone        = _estProduct.estAvgDwellByZone
        const estTotalTimeByProductZone       = _estProduct.estTotalTimeByZone
        const maxEstVisitorsProductZone       = _estProduct.maxEstVisitors
        const maxEstAvgSecProductZone         = _estProduct.maxEstAvgSec
        const maxEstTotalTimeProductZone      = _estProduct.maxEstTotalTime
        const hasEstimatedProductZones        = _estProduct.hasEstimated
        const bestProductZoneByAvgDurationPoly = _estProduct.bestByAvgDurationPoly
        const bestProductZoneByVisitorsPoly    = _estProduct.bestByVisitorsPoly
        const bestProductZoneByEstAvgDwell     = _estProduct.bestByEstAvgDwell

        /* ==========================================================
        * Demo derived data (lists, funnels, coverage)
        * ======================================================== */
        const demoZoneFunnelsObj = computed(() =>
            props.fixedWidgetsData?.demo_zone_funnels || {}
        )

        const demoZoneNames = computed(() => {
            const funnelKeys = Object.keys(demoZoneFunnelsObj.value || {})
            if (funnelKeys.length) return funnelKeys
            return Object.keys(demoZoneVisitorsCounts.value || {})
        })

        const visitorsByDemoZone = computed(() => {
            const poly = polygonDemoZoneNames.value
            return demoZoneNames.value
                .filter(z => !poly.size || poly.has(z))
                .map(z => {
                    const f = demoZoneFunnelsObj.value?.[z]
                    const visitors = f != null
                        ? Number(f.zone_visitors ?? 0)
                        : Number(demoZoneVisitorsCounts.value?.[z] ?? 0)
                    return { zone: z, visitors }
                })
                .filter(r => r.visitors > 0)
        })

        const avgDwellByDemoZone = computed(() => {
            const poly = polygonDemoZoneNames.value
            return demoZoneNames.value
                .filter(z => !poly.size || poly.has(z))
                .map(z => {
                    const total = totalDurationPerDemoZone.value?.[z] ?? 0
                    const vis = Number(demoZoneVisitorsCounts.value?.[z] ?? 0)
                    return { zone: z, avgSec: safeDiv(total, vis) }
                })
                .filter(r => r.avgSec > 0)
        })

        const clearLingerersByDemoZone = computed(() =>
            demoZoneNames.value.map(z => {
                const f = demoZoneFunnelsObj.value?.[z]
                const clears = f != null
                ? Number(f.clear_lingerers ?? 0)
                : Number(demoZoneClearLingerersCounts.value?.[z] ?? 0)
                return { zone: z, clears }
            })
        )

        const funnelsByDemoZone = computed(() =>
            demoZoneNames.value.map(z => {
                const f = demoZoneFunnelsObj.value?.[z]
                if (f != null) {
                const visitors = Number(f.zone_visitors ?? 0)
                const clears   = Number(f.clear_lingerers ?? 0)
                const conv     = Number(f.conversion_rate ?? 0)
                const apv      = Number(f.average_product_value ?? 0)
                const engSales = clears * conv * apv
                return { zone: z, visitors, clears, conv, apv, engSales }
                } else {
                // fallback to previous logic
                const f2   = demoZoneFunnels.value?.[z] || {}
                const vis  = Number(demoZoneVisitorsCounts.value?.[z] ?? 0)
                const clr  = Number(demoZoneClearLingerersCounts.value?.[z] ?? 0)
                const conv = Number(f2.conversion_rate ?? 0)
                const apv  = Number(f2.average_product_value ?? 0)
                const eng  = clr * conv * apv
                return { zone: z, visitors: vis, clears: clr, conv, apv, engSales: eng }
                }
            })
        )

        // simple min–max for bar widths
        const maxVisitorsDemoZone = computed(() => 
            Math.max(1, ...visitorsByDemoZone.value.map(x=>x.visitors))
        )
        
        const maxAvgSecDemoZone = computed(() => 
            Math.max(1, ...avgDwellByDemoZone.value.map(x=>x.avgSec))
        )

        const topClearLingerersDemoZone = computed(() => 
            [...clearLingerersByDemoZone.value].sort((a,b)=>b.clears-a.clears)[0]
        )
        
        const topConversionDemoZone = computed(() => 
            [...funnelsByDemoZone.value].sort((a,b)=>b.conv-a.conv)[0]
        )

        const topSalesPotentialDemoZone = computed(() => 
            [...funnelsByDemoZone.value].sort((a,b)=>b.engSales-a.engSales)[0]
        )

        const zonesWithTrafficDemoZone = computed(() => {
            const poly = polygonDemoZoneNames.value
            return visitorsByDemoZone.value
                .filter(x => x.visitors > 0 && (!poly.size || poly.has(x.zone)))
                .length
        })

        const zoneCoverageTextDemoZone = computed(() => {
            const total = polygonDemoZoneNames.value.size || demoZoneNames.value.length
            return t('explore.kpi.coverageTraffic', { n: zonesWithTrafficDemoZone.value, total })
        })

        // Table rows for “Demo Funnel by Zone”
        const zoneVisitorsDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_visitors_counts || {}
        )
        
        const zoneWalkbysDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_walkbys_counts || {}
        )

        const zoneShortsDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_short_lingerers_counts || {}
        )
        
        const zoneClearsDemoZone = computed(() => 
            props.fixedWidgetsData?.demo_zone_clear_lingerers_counts || {}
        )
        
        const demoZoneFunnelRows = computed(() => {
            const funnels = demoZoneFunnelsObj.value || {}
            const polyNames = polygonDemoZoneNames.value
            const funnelKeys = Object.keys(funnels)
                .filter(z => !polyNames.size || polyNames.has(z))

            if (funnelKeys.length) {
                // build rows straight from funnels
                const rows = funnelKeys.map(zone => {
                    const f = funnels[zone] || {}
                    const visitors = Number(f.zone_visitors ?? 0)   // keep 0-visitors rows
                    const walkbys  = Number(f.walk_bys ?? 0)
                    const shorts   = Number(f.short_lingerers ?? 0)
                    const clears   = Number(f.clear_lingerers ?? 0)
                    const insufficient = hasInsufficientStrategyVisitorCutOff(f)
                    return insufficient
                        ? {
                            zone,
                            visitors,
                            strategyInsufficient: true,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                        : {
                            zone,
                            visitors,
                            strategyInsufficient: false,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                })

                rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                return rows
            }

            // fallback: merge legacy *_counts (polygon-only)
            const polyNamesFb = polygonDemoZoneNames.value
            const keys = new Set([
                ...Object.keys(zoneVisitorsDemoZone.value || {}),
                ...Object.keys(zoneWalkbysDemoZone.value || {}),
                ...Object.keys(zoneShortsDemoZone.value || {}),
                ...Object.keys(zoneClearsDemoZone.value || {}),
            ])

            const rows = []
            for (const k of keys) {
                if (polyNamesFb.size && !polyNamesFb.has(k)) continue
                const visitors = Number(zoneVisitorsDemoZone.value?.[k] ?? 0)
                const walkbys  = Number(zoneWalkbysDemoZone.value?.[k] ?? 0)
                const shorts   = Number(zoneShortsDemoZone.value?.[k] ?? 0)
                const clears   = Number(zoneClearsDemoZone.value?.[k] ?? 0)
                rows.push({
                    zone: k,
                    visitors,
                    strategyInsufficient: false,
                    walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                    shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                    clears:  { count: clears,  pct: pct(clears,  visitors) },
                })
            }

            rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
            return rows
        })

        // Best/worst visitors from the rows (used in KPI cards)
        const bestVisitorsFromRowsDemoZone = computed(() => {
            const rows = (demoZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rows.length) return null
            rows.sort(
                (a, b) =>
                (b.visitors || 0) - (a.visitors || 0) || a.zone.localeCompare(b.zone)
            )
            return rows[0] // { zone, visitors, walkbys:{}, shorts:{}, clears:{} }
        })

        const worstVisitorsFromRowsDemoZone = computed(() => {
            const rowsAll = (demoZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rowsAll.length) return null

            const rowsPos = rowsAll.filter(r => (r.visitors || 0) > 0)
            const rows = rowsPos.length ? rowsPos : rowsAll

            rows.sort(
                (a, b) =>
                (a.visitors || 0) - (b.visitors || 0) || a.zone.localeCompare(b.zone)
            );
            return rows[0]
        })

        // Zones with exactly zero visitors (from the funnel rows)
        const zeroVisitorZonesDemoZone = computed(() =>
            (demoZoneFunnelRows.value || [])
                .filter(r => (r.visitors ?? 0) === 0)
                .map(r => r.zone)
                .sort((a, b) => a.localeCompare(b))
        )

        const highestEngagementZoneDemoZone = computed(() => {
            const visitors = demoZoneVisitorsCounts.value || {}          // { Zone: count }
            const clears   = demoZoneClearLingerersCounts.value || {}    // { Zone: count }

            let best = { zone: '—', ratio: 0, clears: 0, visitors: 0 }
            for (const [zone, v] of Object.entries(visitors)) {
                const c = Number(clears[zone] || 0)
                if (v > 0) {
                const r = c / v
                if (r > best.ratio) best = { zone, ratio: r, clears: c, visitors: v }
                }
            }
            return best
        })

        const _estDemo = _makeEstComputeds({
            typeKey:                'demo',
            polygonNamesRef:        polygonDemoZoneNames,
            visitorsCountsRef:      demoZoneVisitorsCounts,
            totalDurationRef:       totalDurationPerDemoZone,
            polygonAvgRowsRef:      avgDwellByDemoZone,
            polygonVisitorsRowsRef: visitorsByDemoZone,
        })
        const totalDurationPerDemoZonePoly = _estDemo.totalDurationPerZonePoly
        const totalDurationRowsDemoZone    = _estDemo.totalDurationRowsPoly
        const maxTotalDurationDemoZone     = _estDemo.maxTotalDurationPoly
        const estVisitorsByDemoZone        = _estDemo.estVisitorsByZone
        const estAvgDwellByDemoZone        = _estDemo.estAvgDwellByZone
        const estTotalTimeByDemoZone       = _estDemo.estTotalTimeByZone
        const maxEstVisitorsDemoZone       = _estDemo.maxEstVisitors
        const maxEstAvgSecDemoZone         = _estDemo.maxEstAvgSec
        const maxEstTotalTimeDemoZone      = _estDemo.maxEstTotalTime
        const hasEstimatedDemoZones        = _estDemo.hasEstimated
        const bestDemoZoneByAvgDurationPoly = _estDemo.bestByAvgDurationPoly
        const bestDemoZoneByVisitorsPoly    = _estDemo.bestByVisitorsPoly
        const bestDemoZoneByEstAvgDwell     = _estDemo.bestByEstAvgDwell

        /* ==========================================================
        * Display derived data (lists, funnels, coverage)
        * ======================================================== */
        const displayZoneFunnelsObj = computed(() => 
            props.fixedWidgetsData?.display_zone_funnels || {}
        )

        const displayZoneNames = computed(() => {
            const funnelKeys = Object.keys(displayZoneFunnelsObj.value || {})
            if (funnelKeys.length) return funnelKeys
            return Object.keys(displayZoneVisitorsCounts.value || {})
        })

        // Polygon-only visitors/dwell. Mode C tripwire-estimated zones are
        // surfaced separately in the tripwire panel below so the polygon
        // charts stay semantically pure (no mixed measured/estimated rows).
        const visitorsByDisplayZone = computed(() => {
            const poly = polygonDisplayZoneNames.value
            return displayZoneNames.value
                .filter(z => !poly.size || poly.has(z))
                .map(z => {
                    const f = displayZoneFunnelsObj.value?.[z]
                    const visitors = f != null
                        ? Number(f.zone_visitors ?? 0)
                        : Number(displayZoneVisitorsCounts.value?.[z] ?? 0)
                    return { zone: z, visitors }
                })
                .filter(r => r.visitors > 0)
        })

        const avgDwellByDisplayZone = computed(() => {
            const poly = polygonDisplayZoneNames.value
            return displayZoneNames.value
                .filter(z => !poly.size || poly.has(z))
                .map(z => {
                    const total = totalDurationPerDisplayZone.value?.[z] ?? 0
                    const vis = Number(displayZoneVisitorsCounts.value?.[z] ?? 0)
                    return { zone: z, avgSec: safeDiv(total, vis) }
                })
                .filter(r => r.avgSec > 0)
        })

        const _estDisplay = _makeEstComputeds({
            typeKey:                'display',
            polygonNamesRef:        polygonDisplayZoneNames,
            visitorsCountsRef:      displayZoneVisitorsCounts,
            totalDurationRef:       totalDurationPerDisplayZone,
            polygonAvgRowsRef:      avgDwellByDisplayZone,
            polygonVisitorsRowsRef: visitorsByDisplayZone,
        })
        const totalDurationPerDisplayZonePoly = _estDisplay.totalDurationPerZonePoly
        const totalDurationRowsDisplayZone    = _estDisplay.totalDurationRowsPoly
        const maxTotalDurationDisplayZone     = _estDisplay.maxTotalDurationPoly
        const estVisitorsByDisplayZone        = _estDisplay.estVisitorsByZone
        const estAvgDwellByDisplayZone        = _estDisplay.estAvgDwellByZone
        const estTotalTimeByDisplayZone       = _estDisplay.estTotalTimeByZone
        const maxEstVisitorsDisplayZone       = _estDisplay.maxEstVisitors
        const maxEstAvgSecDisplayZone         = _estDisplay.maxEstAvgSec
        const maxEstTotalTimeDisplayZone      = _estDisplay.maxEstTotalTime
        const hasEstimatedDisplayZones        = _estDisplay.hasEstimated
        const bestDisplayZoneByAvgDurationPoly = _estDisplay.bestByAvgDurationPoly
        const bestDisplayZoneByVisitorsPoly    = _estDisplay.bestByVisitorsPoly
        const bestDisplayZoneByEstAvgDwell     = _estDisplay.bestByEstAvgDwell

        const clearLingerersByDisplayZone = computed(() =>
            displayZoneNames.value.map(z => {
                const f = displayZoneFunnelsObj.value?.[z]
                const clears = f != null
                ? Number(f.clear_lingerers ?? 0)
                : Number(displayZoneClearLingerersCounts.value?.[z] ?? 0)
                return { zone: z, clears }
            })
        )

        const funnelsByDisplayZone = computed(() =>
            displayZoneNames.value.map(z => {
                const f = displayZoneFunnelsObj.value?.[z]
                if (f != null) {
                const visitors = Number(f.zone_visitors ?? 0)
                const clears   = Number(f.clear_lingerers ?? 0)
                const conv     = Number(f.conversion_rate ?? 0)
                const apv      = Number(f.average_product_value ?? 0)
                const engSales = clears * conv * apv
                return { zone: z, visitors, clears, conv, apv, engSales }
                } else {
                // fallback to previous logic
                const f2   = displayZoneFunnels.value?.[z] || {}
                const vis  = Number(displayZoneVisitorsCounts.value?.[z] ?? 0)
                const clr  = Number(displayZoneClearLingerersCounts.value?.[z] ?? 0)
                const conv = Number(f2.conversion_rate ?? 0)
                const apv  = Number(f2.average_product_value ?? 0)
                const eng  = clr * conv * apv
                return { zone: z, visitors: vis, clears: clr, conv, apv, engSales: eng }
                }
            })
        )

        // simple min–max for bar widths
        const maxVisitorsDisplayZone = computed(() => 
            Math.max(1, ...visitorsByDisplayZone.value.map(x=>x.visitors))
        )
        
        const maxAvgSecDisplayZone = computed(() => 
            Math.max(1, ...avgDwellByDisplayZone.value.map(x=>x.avgSec))
        )

        const topClearLingerersDisplayZone = computed(() => 
            [...clearLingerersByDisplayZone.value].sort((a,b)=>b.clears-a.clears)[0]
        )
        
        const topConversionDisplayZone = computed(() => 
            [...funnelsByDisplayZone.value].sort((a,b)=>b.conv-a.conv)[0]
        )

        const topSalesPotentialDisplayZone = computed(() => 
            [...funnelsByDisplayZone.value].sort((a,b)=>b.engSales-a.engSales)[0]
        )

        const zonesWithTrafficDisplayZone = computed(() => {
            const poly = polygonDisplayZoneNames.value
            return visitorsByDisplayZone.value
                .filter(x => x.visitors > 0 && (!poly.size || poly.has(x.zone)))
                .length
        })

        const zoneCoverageTextDisplayZone = computed(() => {
            const total = polygonDisplayZoneNames.value.size || displayZoneNames.value.length
            return t('explore.kpi.coverageTraffic', { n: zonesWithTrafficDisplayZone.value, total })
        })

        // Table rows for “Display Funnel by Zone”
        const zoneVisitorsDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_visitors_counts || {}
        )
        
        const zoneWalkbysDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_walkbys_counts || {}
        )

        const zoneShortsDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_short_lingerers_counts || {}
        )
        
        const zoneClearsDisplayZone = computed(() => 
            props.fixedWidgetsData?.display_zone_clear_lingerers_counts || {}
        )
        
        const displayZoneFunnelRows = computed(() => {
            const funnels = displayZoneFunnelsObj.value || {}
            const polyNames = polygonDisplayZoneNames.value
            const funnelKeys = Object.keys(funnels)
                .filter(z => !polyNames.size || polyNames.has(z))

            if (funnelKeys.length) {
                // build rows straight from funnels
                const rows = funnelKeys.map(zone => {
                    const f = funnels[zone] || {}
                    const visitors = Number(f.zone_visitors ?? 0)   // keep 0-visitors rows
                    const walkbys  = Number(f.walk_bys ?? 0)
                    const shorts   = Number(f.short_lingerers ?? 0)
                    const clears   = Number(f.clear_lingerers ?? 0)
                    const insufficient = hasInsufficientStrategyVisitorCutOff(f)
                    return insufficient
                        ? {
                            zone,
                            visitors,
                            strategyInsufficient: true,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                        : {
                            zone,
                            visitors,
                            strategyInsufficient: false,
                            walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                            shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                            clears:  { count: clears,  pct: pct(clears,  visitors) },
                        }
                })
                
                rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                return rows
            }

            // fallback: merge legacy *_counts (polygon-only)
            const polyNamesFb = polygonDisplayZoneNames.value
            const keys = new Set([
                ...Object.keys(zoneVisitorsDisplayZone.value || {}),
                ...Object.keys(zoneWalkbysDisplayZone.value || {}),
                ...Object.keys(zoneShortsDisplayZone.value || {}),
                ...Object.keys(zoneClearsDisplayZone.value || {}),
            ])

            const rows = []
            for (const k of keys) {
                if (polyNamesFb.size && !polyNamesFb.has(k)) continue
                const visitors = Number(zoneVisitorsDisplayZone.value?.[k] ?? 0)
                const walkbys  = Number(zoneWalkbysDisplayZone.value?.[k] ?? 0)
                const shorts   = Number(zoneShortsDisplayZone.value?.[k] ?? 0)
                const clears   = Number(zoneClearsDisplayZone.value?.[k] ?? 0)
                rows.push({
                    zone: k,
                    visitors,
                    strategyInsufficient: false,
                    walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                    shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                    clears:  { count: clears,  pct: pct(clears,  visitors) },
                })
            }

            rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
            return rows
        })

        // Best/worst visitors from the rows (used in KPI cards)
        const bestVisitorsFromRowsDisplayZone = computed(() => {
            const rows = (displayZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rows.length) return null
            rows.sort(
                (a, b) =>
                (b.visitors || 0) - (a.visitors || 0) || a.zone.localeCompare(b.zone)
            )
            return rows[0] // { zone, visitors, walkbys:{}, shorts:{}, clears:{} }
        })

        const worstVisitorsFromRowsDisplayZone = computed(() => {
            const rowsAll = (displayZoneFunnelRows.value || [])
                .filter(r => Number.isFinite(r.visitors))
            if (!rowsAll.length) return null

            const rowsPos = rowsAll.filter(r => (r.visitors || 0) > 0)
            const rows = rowsPos.length ? rowsPos : rowsAll

            rows.sort(
                (a, b) =>
                (a.visitors || 0) - (b.visitors || 0) || a.zone.localeCompare(b.zone)
            );
            return rows[0]
        })

        // Zones with exactly zero visitors (from the funnel rows)
        const zeroVisitorZonesDisplayZone = computed(() =>
            (displayZoneFunnelRows.value || [])
                .filter(r => (r.visitors ?? 0) === 0)
                .map(r => r.zone)
                .sort((a, b) => a.localeCompare(b))
        )

        const highestEngagementZoneDisplayZone = computed(() => {
            const visitors = displayZoneVisitorsCounts.value || {}          // { Zone: count }
            const clears   = displayZoneClearLingerersCounts.value || {}    // { Zone: count }

            let best = { zone: '—', ratio: 0, clears: 0, visitors: 0 }
            for (const [zone, v] of Object.entries(visitors)) {
                const c = Number(clears[zone] || 0)
                if (v > 0) {
                const r = c / v
                if (r > best.ratio) best = { zone, ratio: r, clears: c, visitors: v }
                }
            }
            return best
        })

        /* ==========================================================
         * Tripwire-derived helpers per dwell zone type (Phase 8.3.c)
         * Direct computeds — reactive on the per-type refs above.
         * ======================================================== */

        // Product
        const crossingsByProductZone = computed(() =>
            Object.entries(productZoneCrossingsCounts.value || {})
                .map(([zone, n]) => ({ zone, crossings: Number(n || 0) }))
        )
        const maxCrossingsProductZone = computed(() =>
            Math.max(1, ...crossingsByProductZone.value.map(x => x.crossings))
        )
        const zeroCrossingZonesProductZone = computed(() =>
            crossingsByProductZone.value.filter(x => x.crossings === 0).map(x => x.zone).sort((a, b) => a.localeCompare(b))
        )
        const crossingCoverageTextProductZone = computed(() => {
            // Use the crossings array length as denominator — backend already
            // seeds every active tripwire-bearing zone via setdefault(0), so
            // it's self-sufficient and stays correct even when the separate
            // _zones_with_tripwire_total field is missing from older caches.
            const total = crossingsByProductZone.value.length
            const withCrossings = crossingsByProductZone.value.filter(x => x.crossings > 0).length
            return t('explore.kpi.coverageCrossings', { n: withCrossings, total })
        })
        // Backwards-compat: when the backend hasn't yet emitted the new
        // {type}_zones_with_polygon_total / _tripwire_total fields (pre-Phase-
        // 8.3 ETL output), fall back to the legacy isXZonePresent() check so
        // the polygon row stays visible. New ETL runs override this.
        const hasPolygonProductZones = computed(() => {
            const raw = props.fixedWidgetsData?.product_zones_with_polygon_total
            if (raw === undefined) return isProductZonePresent()
            return Number(raw || 0) > 0
        })
        const hasTripwireProductZones = computed(() => {
            const raw = props.fixedWidgetsData?.product_zones_with_tripwire_total
            return raw !== undefined && Number(raw || 0) > 0
        })
        const showProductSection = computed(() => hasPolygonProductZones.value || hasTripwireProductZones.value)

        // Demo
        const crossingsByDemoZone = computed(() =>
            Object.entries(demoZoneCrossingsCounts.value || {})
                .map(([zone, n]) => ({ zone, crossings: Number(n || 0) }))
        )
        const maxCrossingsDemoZone = computed(() =>
            Math.max(1, ...crossingsByDemoZone.value.map(x => x.crossings))
        )
        const zeroCrossingZonesDemoZone = computed(() =>
            crossingsByDemoZone.value.filter(x => x.crossings === 0).map(x => x.zone).sort((a, b) => a.localeCompare(b))
        )
        const crossingCoverageTextDemoZone = computed(() => {
            const total = crossingsByDemoZone.value.length
            const withCrossings = crossingsByDemoZone.value.filter(x => x.crossings > 0).length
            return t('explore.kpi.coverageCrossings', { n: withCrossings, total })
        })
        const hasPolygonDemoZones = computed(() => {
            const raw = props.fixedWidgetsData?.demo_zones_with_polygon_total
            if (raw === undefined) return isDemoZonePresent()
            return Number(raw || 0) > 0
        })
        const hasTripwireDemoZones = computed(() => {
            const raw = props.fixedWidgetsData?.demo_zones_with_tripwire_total
            return raw !== undefined && Number(raw || 0) > 0
        })
        const showDemoSection = computed(() => hasPolygonDemoZones.value || hasTripwireDemoZones.value)

        // Display
        const crossingsByDisplayZone = computed(() =>
            Object.entries(displayZoneCrossingsCounts.value || {})
                .map(([zone, n]) => ({ zone, crossings: Number(n || 0) }))
        )
        const maxCrossingsDisplayZone = computed(() =>
            Math.max(1, ...crossingsByDisplayZone.value.map(x => x.crossings))
        )
        const zeroCrossingZonesDisplayZone = computed(() =>
            crossingsByDisplayZone.value.filter(x => x.crossings === 0).map(x => x.zone).sort((a, b) => a.localeCompare(b))
        )
        const crossingCoverageTextDisplayZone = computed(() => {
            const total = crossingsByDisplayZone.value.length
            const withCrossings = crossingsByDisplayZone.value.filter(x => x.crossings > 0).length
            return t('explore.kpi.coverageCrossings', { n: withCrossings, total })
        })
        const hasPolygonDisplayZones = computed(() => {
            const raw = props.fixedWidgetsData?.display_zones_with_polygon_total
            if (raw === undefined) return isDisplayZonePresent()
            return Number(raw || 0) > 0
        })
        const hasTripwireDisplayZones = computed(() => {
            const raw = props.fixedWidgetsData?.display_zones_with_tripwire_total
            return raw !== undefined && Number(raw || 0) > 0
        })
        const showDisplaySection = computed(() => hasPolygonDisplayZones.value || hasTripwireDisplayZones.value)

        // ===========================================================
        // Phase 8.3.d — Info / Service / Pick Up zones
        // Builder helper: per-type polygon-side derived computeds.
        // ===========================================================
        function _buildDwellZoneTypeComputeds({
            funnelsObjRef,           // Ref<{}>  (e.g. infoZoneFunnelsObj)
            visitorsCountsRef,       // Ref<{}>
            clearLingerersCountsRef, // Ref<{}>
            totalDurationRef,        // Ref<{}>
            walkbysCountsRef,        // Ref<{}>
            shortsCountsRef,         // Ref<{}>
            zoneTypeKey,             // 'info' | 'service' | 'pick_up'
            polygonNamesRef,         // ComputedRef<Set<string>> — polygon-bearing zone names
        }) {
            // Read props.fixedWidgetsData inside each computed (NOT captured via
            // a const) so reactivity tracks the prop's identity changes — when
            // the API response replaces the object, the computed re-runs.

            const zoneFunnelsObj = computed(() => props.fixedWidgetsData?.[`${zoneTypeKey}_zone_funnels`] || {})
            const zoneNames = computed(() => {
                const keys = Object.keys(zoneFunnelsObj.value || {})
                if (keys.length) return keys
                return Object.keys(visitorsCountsRef.value || {})
            })
            const visitorsByZone = computed(() =>
                zoneNames.value
                    .map(z => {
                        const f = zoneFunnelsObj.value?.[z]
                        const visitors = f != null
                            ? Number(f.zone_visitors ?? 0)
                            : Number(visitorsCountsRef.value?.[z] ?? 0)
                        return { zone: z, visitors }
                    })
                    .filter(r => r.visitors > 0)
            )
            const avgDwellByZone = computed(() =>
                zoneNames.value
                    .map(z => {
                        const total = totalDurationRef.value?.[z] ?? 0
                        const vis = Number(visitorsCountsRef.value?.[z] ?? 0)
                        return { zone: z, avgSec: safeDiv(total, vis) }
                    })
                    .filter(r => r.avgSec > 0)
            )
            const clearLingerersByZone = computed(() =>
                zoneNames.value.map(z => {
                    const f = zoneFunnelsObj.value?.[z]
                    const clears = f != null
                        ? Number(f.clear_lingerers ?? 0)
                        : Number(clearLingerersCountsRef.value?.[z] ?? 0)
                    return { zone: z, clears }
                })
            )
            const maxVisitors = computed(() => Math.max(1, ...visitorsByZone.value.map(x => x.visitors)))
            const maxAvgSec   = computed(() => Math.max(1, ...avgDwellByZone.value.map(x => x.avgSec)))

            const topClearLingerers = computed(() =>
                [...clearLingerersByZone.value].sort((a, b) => b.clears - a.clears)[0]
            )

            const zonesWithTraffic = computed(() => {
                const poly = polygonNamesRef?.value
                return visitorsByZone.value
                    .filter(x => x.visitors > 0 && (!poly || !poly.size || poly.has(x.zone)))
                    .length
            })
            const zoneCoverageText = computed(() => {
                const polySize = polygonNamesRef?.value?.size
                const total = polySize || zoneNames.value.length
                return t('explore.kpi.coverageTraffic', { n: zonesWithTraffic.value, total })
            })

            const funnelRows = computed(() => {
                const funnels = zoneFunnelsObj.value || {}
                const polyNames = polygonNamesRef?.value
                const funnelKeys = Object.keys(funnels)
                    .filter(z => !polyNames || !polyNames.size || polyNames.has(z))

                if (funnelKeys.length) {
                    const rows = funnelKeys.map(zone => {
                        const f = funnels[zone] || {}
                        const visitors = Number(f.zone_visitors ?? 0)
                        const walkbys  = Number(f.walk_bys ?? 0)
                        const shorts   = Number(f.short_lingerers ?? 0)
                        const clears   = Number(f.clear_lingerers ?? 0)
                        const insufficient = hasInsufficientStrategyVisitorCutOff(f)
                        return insufficient
                            ? { zone, visitors, strategyInsufficient: true,
                                walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                                shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                                clears:  { count: clears,  pct: pct(clears,  visitors) } }
                            : { zone, visitors, strategyInsufficient: false,
                                walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                                shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                                clears:  { count: clears,  pct: pct(clears,  visitors) } }
                    })
                    rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                    return rows
                }

                // fallback (polygon-only)
                const polyNamesFb = polygonNamesRef?.value
                const keys = new Set([
                    ...Object.keys(visitorsCountsRef.value || {}),
                    ...Object.keys(walkbysCountsRef.value || {}),
                    ...Object.keys(shortsCountsRef.value || {}),
                    ...Object.keys(clearLingerersCountsRef.value || {}),
                ])
                const rows = []
                for (const k of keys) {
                    if (polyNamesFb && polyNamesFb.size && !polyNamesFb.has(k)) continue
                    const visitors = Number(visitorsCountsRef.value?.[k] ?? 0)
                    const walkbys  = Number(walkbysCountsRef.value?.[k] ?? 0)
                    const shorts   = Number(shortsCountsRef.value?.[k] ?? 0)
                    const clears   = Number(clearLingerersCountsRef.value?.[k] ?? 0)
                    rows.push({ zone: k, visitors, strategyInsufficient: false,
                        walkbys: { count: walkbys, pct: pct(walkbys, visitors) },
                        shorts:  { count: shorts,  pct: pct(shorts,  visitors) },
                        clears:  { count: clears,  pct: pct(clears,  visitors) } })
                }
                rows.sort((a,b)=>(b.visitors||0)-(a.visitors||0)||a.zone.localeCompare(b.zone))
                return rows
            })

            const worstVisitorsFromRows = computed(() => {
                const rowsAll = (funnelRows.value || []).filter(r => Number.isFinite(r.visitors))
                if (!rowsAll.length) return null
                const rowsPos = rowsAll.filter(r => (r.visitors || 0) > 0)
                const rows = rowsPos.length ? rowsPos : rowsAll
                rows.sort((a, b) => (a.visitors || 0) - (b.visitors || 0) || a.zone.localeCompare(b.zone))
                return rows[0]
            })
            const zeroVisitorZones = computed(() =>
                (funnelRows.value || []).filter(r => (r.visitors ?? 0) === 0).map(r => r.zone).sort((a, b) => a.localeCompare(b))
            )
            const highestEngagementZone = computed(() => {
                const visitors = visitorsCountsRef.value || {}
                const clears   = clearLingerersCountsRef.value || {}
                let best = { zone: '—', ratio: 0, clears: 0, visitors: 0 }
                for (const [zone, v] of Object.entries(visitors)) {
                    const c = Number(clears[zone] || 0)
                    if (v > 0) {
                        const r = c / v
                        if (r > best.ratio) best = { zone, ratio: r, clears: c, visitors: v }
                    }
                }
                return best
            })

            // Filter polygon Visitors/AvgDwell rows again here so the est
            // factory's polygon-best KPIs work even though visitorsByZone /
            // avgDwellByZone above are already filtered (polyNamesRef is
            // applied inside _makeEstComputeds via the polygonNamesRef arg).
            const polygonVisitorsByZone = computed(() => {
                const poly = polygonNamesRef?.value
                return (visitorsByZone.value || [])
                    .filter(r => !poly || !poly.size || poly.has(r.zone))
            })
            const polygonAvgDwellByZone = computed(() => {
                const poly = polygonNamesRef?.value
                return (avgDwellByZone.value || [])
                    .filter(r => !poly || !poly.size || poly.has(r.zone))
            })

            const _est = _makeEstComputeds({
                typeKey: zoneTypeKey === 'pick_up' ? 'pickup' : zoneTypeKey,
                polygonNamesRef,
                visitorsCountsRef,
                totalDurationRef,
                polygonAvgRowsRef:      polygonAvgDwellByZone,
                polygonVisitorsRowsRef: polygonVisitorsByZone,
            })

            return {
                zoneFunnelsObj, zoneNames, visitorsByZone, avgDwellByZone, clearLingerersByZone,
                maxVisitors, maxAvgSec, topClearLingerers, zonesWithTraffic, zoneCoverageText,
                funnelRows, worstVisitorsFromRows, zeroVisitorZones, highestEngagementZone,
                // Mode C tripwire-side bindings
                totalDurationPerZonePoly:  _est.totalDurationPerZonePoly,
                totalDurationRowsPoly:     _est.totalDurationRowsPoly,
                maxTotalDurationPoly:      _est.maxTotalDurationPoly,
                estVisitorsByZone:         _est.estVisitorsByZone,
                estAvgDwellByZone:         _est.estAvgDwellByZone,
                estTotalTimeByZone:        _est.estTotalTimeByZone,
                maxEstVisitors:            _est.maxEstVisitors,
                maxEstAvgSec:              _est.maxEstAvgSec,
                maxEstTotalTime:           _est.maxEstTotalTime,
                hasEstimated:              _est.hasEstimated,
                bestByAvgDurationPoly:     _est.bestByAvgDurationPoly,
                bestByVisitorsPoly:        _est.bestByVisitorsPoly,
                bestByEstAvgDwell:         _est.bestByEstAvgDwell,
            }
        }

        // Info zone derived computeds (polygon + tripwire)
        const zoneVisitorsInfoZone = computed(() => props.fixedWidgetsData?.info_zone_visitors_counts || {})
        const zoneWalkbysInfoZone  = computed(() => props.fixedWidgetsData?.info_zone_walkbys_counts || {})
        const zoneShortsInfoZone   = computed(() => props.fixedWidgetsData?.info_zone_short_lingerers_counts || {})
        const zoneClearsInfoZone   = computed(() => props.fixedWidgetsData?.info_zone_clear_lingerers_counts || {})
        const _info = _buildDwellZoneTypeComputeds({
            funnelsObjRef:           infoZoneFunnels,
            visitorsCountsRef:       infoZoneVisitorsCounts,
            clearLingerersCountsRef: infoZoneClearLingerersCounts,
            totalDurationRef:        totalDurationPerInfoZone,
            walkbysCountsRef:        zoneWalkbysInfoZone,
            shortsCountsRef:         zoneShortsInfoZone,
            zoneTypeKey:             'info',
            polygonNamesRef:         polygonInfoZoneNames,
        })
        const infoZoneFunnelsObj         = _info.zoneFunnelsObj
        const infoZoneNames              = _info.zoneNames
        const visitorsByInfoZone         = _info.visitorsByZone
        const avgDwellByInfoZone         = _info.avgDwellByZone
        const maxVisitorsInfoZone        = _info.maxVisitors
        const maxAvgSecInfoZone          = _info.maxAvgSec
        const topClearLingerersInfoZone  = _info.topClearLingerers
        const zoneCoverageTextInfoZone   = _info.zoneCoverageText
        const infoZoneFunnelRows         = _info.funnelRows
        const worstVisitorsFromRowsInfoZone = _info.worstVisitorsFromRows
        const zeroVisitorZonesInfoZone   = _info.zeroVisitorZones
        const highestEngagementZoneInfoZone = _info.highestEngagementZone
        const totalDurationPerInfoZonePoly  = _info.totalDurationPerZonePoly
        const totalDurationRowsInfoZone     = _info.totalDurationRowsPoly
        const maxTotalDurationInfoZone      = _info.maxTotalDurationPoly
        const estVisitorsByInfoZone         = _info.estVisitorsByZone
        const estAvgDwellByInfoZone         = _info.estAvgDwellByZone
        const estTotalTimeByInfoZone        = _info.estTotalTimeByZone
        const maxEstVisitorsInfoZone        = _info.maxEstVisitors
        const maxEstAvgSecInfoZone          = _info.maxEstAvgSec
        const maxEstTotalTimeInfoZone       = _info.maxEstTotalTime
        const hasEstimatedInfoZones         = _info.hasEstimated
        const bestInfoZoneByAvgDurationPoly = _info.bestByAvgDurationPoly
        const bestInfoZoneByVisitorsPoly    = _info.bestByVisitorsPoly
        const bestInfoZoneByEstAvgDwell     = _info.bestByEstAvgDwell
        const crossingsByInfoZone = computed(() =>
            Object.entries(infoZoneCrossingsCounts.value || {}).map(([zone, n]) => ({ zone, crossings: Number(n || 0) }))
        )
        const maxCrossingsInfoZone = computed(() => Math.max(1, ...crossingsByInfoZone.value.map(x => x.crossings)))
        const zeroCrossingZonesInfoZone = computed(() =>
            crossingsByInfoZone.value.filter(x => x.crossings === 0).map(x => x.zone).sort((a, b) => a.localeCompare(b))
        )
        const crossingCoverageTextInfoZone = computed(() => {
            const total = crossingsByInfoZone.value.length
            const withCrossings = crossingsByInfoZone.value.filter(x => x.crossings > 0).length
            return t('explore.kpi.coverageCrossings', { n: withCrossings, total })
        })
        const hasPolygonInfoZones = computed(() => {
            const raw = props.fixedWidgetsData?.info_zones_with_polygon_total
            if (raw === undefined) return isInfoZonePresent()
            return Number(raw || 0) > 0
        })
        const hasTripwireInfoZones = computed(() => {
            const raw = props.fixedWidgetsData?.info_zones_with_tripwire_total
            return raw !== undefined && Number(raw || 0) > 0
        })
        const showInfoSection = computed(() => hasPolygonInfoZones.value || hasTripwireInfoZones.value)

        // Service zone derived computeds (polygon + tripwire)
        const zoneVisitorsServiceZone = computed(() => props.fixedWidgetsData?.service_zone_visitors_counts || {})
        const zoneWalkbysServiceZone  = computed(() => props.fixedWidgetsData?.service_zone_walkbys_counts || {})
        const zoneShortsServiceZone   = computed(() => props.fixedWidgetsData?.service_zone_short_lingerers_counts || {})
        const zoneClearsServiceZone   = computed(() => props.fixedWidgetsData?.service_zone_clear_lingerers_counts || {})
        const _service = _buildDwellZoneTypeComputeds({
            funnelsObjRef:           serviceZoneFunnels,
            visitorsCountsRef:       serviceZoneVisitorsCounts,
            clearLingerersCountsRef: serviceZoneClearLingerersCounts,
            totalDurationRef:        totalDurationPerServiceZone,
            walkbysCountsRef:        zoneWalkbysServiceZone,
            shortsCountsRef:         zoneShortsServiceZone,
            zoneTypeKey:             'service',
            polygonNamesRef:         polygonServiceZoneNames,
        })
        const serviceZoneFunnelsObj         = _service.zoneFunnelsObj
        const serviceZoneNames              = _service.zoneNames
        const visitorsByServiceZone         = _service.visitorsByZone
        const avgDwellByServiceZone         = _service.avgDwellByZone
        const maxVisitorsServiceZone        = _service.maxVisitors
        const maxAvgSecServiceZone          = _service.maxAvgSec
        const topClearLingerersServiceZone  = _service.topClearLingerers
        const zoneCoverageTextServiceZone   = _service.zoneCoverageText
        const serviceZoneFunnelRows         = _service.funnelRows
        const worstVisitorsFromRowsServiceZone = _service.worstVisitorsFromRows
        const zeroVisitorZonesServiceZone   = _service.zeroVisitorZones
        const highestEngagementZoneServiceZone = _service.highestEngagementZone
        const totalDurationPerServiceZonePoly  = _service.totalDurationPerZonePoly
        const totalDurationRowsServiceZone     = _service.totalDurationRowsPoly
        const maxTotalDurationServiceZone      = _service.maxTotalDurationPoly
        const estVisitorsByServiceZone         = _service.estVisitorsByZone
        const estAvgDwellByServiceZone         = _service.estAvgDwellByZone
        const estTotalTimeByServiceZone        = _service.estTotalTimeByZone
        const maxEstVisitorsServiceZone        = _service.maxEstVisitors
        const maxEstAvgSecServiceZone          = _service.maxEstAvgSec
        const maxEstTotalTimeServiceZone       = _service.maxEstTotalTime
        const hasEstimatedServiceZones         = _service.hasEstimated
        const bestServiceZoneByAvgDurationPoly = _service.bestByAvgDurationPoly
        const bestServiceZoneByVisitorsPoly    = _service.bestByVisitorsPoly
        const bestServiceZoneByEstAvgDwell     = _service.bestByEstAvgDwell
        const crossingsByServiceZone = computed(() =>
            Object.entries(serviceZoneCrossingsCounts.value || {}).map(([zone, n]) => ({ zone, crossings: Number(n || 0) }))
        )
        const maxCrossingsServiceZone = computed(() => Math.max(1, ...crossingsByServiceZone.value.map(x => x.crossings)))
        const zeroCrossingZonesServiceZone = computed(() =>
            crossingsByServiceZone.value.filter(x => x.crossings === 0).map(x => x.zone).sort((a, b) => a.localeCompare(b))
        )
        const crossingCoverageTextServiceZone = computed(() => {
            const total = crossingsByServiceZone.value.length
            const withCrossings = crossingsByServiceZone.value.filter(x => x.crossings > 0).length
            return t('explore.kpi.coverageCrossings', { n: withCrossings, total })
        })
        const hasPolygonServiceZones = computed(() => {
            const raw = props.fixedWidgetsData?.service_zones_with_polygon_total
            if (raw === undefined) return isServiceZonePresent()
            return Number(raw || 0) > 0
        })
        const hasTripwireServiceZones = computed(() => {
            const raw = props.fixedWidgetsData?.service_zones_with_tripwire_total
            return raw !== undefined && Number(raw || 0) > 0
        })
        const showServiceSection = computed(() => hasPolygonServiceZones.value || hasTripwireServiceZones.value)

        // Pick Up zone derived computeds (polygon + tripwire)
        const zoneVisitorsPickUpZone = computed(() => props.fixedWidgetsData?.pick_up_zone_visitors_counts || {})
        const zoneWalkbysPickUpZone  = computed(() => props.fixedWidgetsData?.pick_up_zone_walkbys_counts || {})
        const zoneShortsPickUpZone   = computed(() => props.fixedWidgetsData?.pick_up_zone_short_lingerers_counts || {})
        const zoneClearsPickUpZone   = computed(() => props.fixedWidgetsData?.pick_up_zone_clear_lingerers_counts || {})
        const _pickup = _buildDwellZoneTypeComputeds({
            funnelsObjRef:           pickUpZoneFunnels,
            visitorsCountsRef:       pickUpZoneVisitorsCounts,
            clearLingerersCountsRef: pickUpZoneClearLingerersCounts,
            totalDurationRef:        totalDurationPerPickUpZone,
            walkbysCountsRef:        zoneWalkbysPickUpZone,
            shortsCountsRef:         zoneShortsPickUpZone,
            zoneTypeKey:             'pick_up',
            polygonNamesRef:         polygonPickUpZoneNames,
        })
        const pickUpZoneFunnelsObj         = _pickup.zoneFunnelsObj
        const pickUpZoneNames              = _pickup.zoneNames
        const visitorsByPickUpZone         = _pickup.visitorsByZone
        const avgDwellByPickUpZone         = _pickup.avgDwellByZone
        const maxVisitorsPickUpZone        = _pickup.maxVisitors
        const maxAvgSecPickUpZone          = _pickup.maxAvgSec
        const topClearLingerersPickUpZone  = _pickup.topClearLingerers
        const zoneCoverageTextPickUpZone   = _pickup.zoneCoverageText
        const pickUpZoneFunnelRows         = _pickup.funnelRows
        const worstVisitorsFromRowsPickUpZone = _pickup.worstVisitorsFromRows
        const zeroVisitorZonesPickUpZone   = _pickup.zeroVisitorZones
        const highestEngagementZonePickUpZone = _pickup.highestEngagementZone
        const totalDurationPerPickUpZonePoly  = _pickup.totalDurationPerZonePoly
        const totalDurationRowsPickUpZone     = _pickup.totalDurationRowsPoly
        const maxTotalDurationPickUpZone      = _pickup.maxTotalDurationPoly
        const estVisitorsByPickUpZone         = _pickup.estVisitorsByZone
        const estAvgDwellByPickUpZone         = _pickup.estAvgDwellByZone
        const estTotalTimeByPickUpZone        = _pickup.estTotalTimeByZone
        const maxEstVisitorsPickUpZone        = _pickup.maxEstVisitors
        const maxEstAvgSecPickUpZone          = _pickup.maxEstAvgSec
        const maxEstTotalTimePickUpZone       = _pickup.maxEstTotalTime
        const hasEstimatedPickUpZones         = _pickup.hasEstimated
        const bestPickUpZoneByAvgDurationPoly = _pickup.bestByAvgDurationPoly
        const bestPickUpZoneByVisitorsPoly    = _pickup.bestByVisitorsPoly
        const bestPickUpZoneByEstAvgDwell     = _pickup.bestByEstAvgDwell
        const crossingsByPickUpZone = computed(() =>
            Object.entries(pickUpZoneCrossingsCounts.value || {}).map(([zone, n]) => ({ zone, crossings: Number(n || 0) }))
        )
        const maxCrossingsPickUpZone = computed(() => Math.max(1, ...crossingsByPickUpZone.value.map(x => x.crossings)))
        const zeroCrossingZonesPickUpZone = computed(() =>
            crossingsByPickUpZone.value.filter(x => x.crossings === 0).map(x => x.zone).sort((a, b) => a.localeCompare(b))
        )
        const crossingCoverageTextPickUpZone = computed(() => {
            const total = crossingsByPickUpZone.value.length
            const withCrossings = crossingsByPickUpZone.value.filter(x => x.crossings > 0).length
            return t('explore.kpi.coverageCrossings', { n: withCrossings, total })
        })
        const hasPolygonPickUpZones = computed(() => {
            const raw = props.fixedWidgetsData?.pick_up_zones_with_polygon_total
            if (raw === undefined) return isPickUpZonePresent()
            return Number(raw || 0) > 0
        })
        const hasTripwirePickUpZones = computed(() => {
            const raw = props.fixedWidgetsData?.pick_up_zones_with_tripwire_total
            return raw !== undefined && Number(raw || 0) > 0
        })
        const showPickUpSection = computed(() => hasPolygonPickUpZones.value || hasTripwirePickUpZones.value)

        /* ==========================================================
         * Per-section zone counts (polygon + tripwire) for summary pills
         * ======================================================== */
        // Priorities feed the Today hero; computed top-level so the hero can
        // render even when the Prioritization tab hasn't been visited yet.
        // Guidance writes are admin-only (server-enforced too); non-admins get a
        // read-only view of the tab.
        const userStore = useUserStore()
        const canEditGuidance = computed(() => !!userStore.isAdminInCurrentCompany)

        // Zone identity 6.2: zones that became one of these types TODAY — listed under the
        // new type already, figures from tonight (a note at the top of its section).
        const changesToday = useChangesTodayStore()
        changesToday.ensureLoaded(userStore.currentCompany?.CompanyId ?? null)
        const movedIntoTypes = (typeIds) => typeIds.flatMap((t) => movedInto(changesToday.register, t))
        // 6.7: did a zone of these types get a new alert threshold today?
        const thresholdChangedIn = (typeIds) => {
            const keys = ((changesToday.register.settings || {}).thresholds || {}).zones || []
            return keys.length > 0 && (detectionZoneStore.detectionzones || []).some((z) => z
                && typeIds.includes(z.DetectionZoneTypeId) && keys.map(String).includes(String(z.ZoneKey)))
        }

        // Allen's per-zone recommendations — shown as the "what to do" on the Prio
        // cards. Acting on them (track / snooze / dismiss) lives in the Guidance tab.
        const guidanceRecommendations = ref({})
        const loadGuidanceContext = async () => {
            try {
                guidanceRecommendations.value =
                    (await companyStore.fetchGuidanceRecommendations().catch(() => ({}))) || {}
            } catch (e) { /* leave empty — Prio shows 'recommendation appears after the next update' */ }
        }
        onMounted(loadGuidanceContext)

        const topPriorities = computed(() =>
            computePriorities(
                props.fixedWidgetsData,
                props.fixedWidgetsCompareToData,
                companySector.value,
                zoneOverrides.value,
                props.fixedWidgetsData?._cohort ?? null,
                // Real day count, NOT the raw selectedDuration — the named ranges
                // (this/last week/month) are sentinels (1000/2000/3000/4000), so
                // passing them straight in would make eurScale = 30/2000 ≈ 0.015.
                // effectiveDurationDays resolves sentinels → elapsed/calendar days
                // and passes the fixed windows (7/30/365) through unchanged.
                dataPeriodDays.value,   // data-aligned days → €/mo scale never leads the data (no flash)
            )
        )

        // Store-level product-funnel stats for Explore's at-a-glance (spec §5:
        // "€/visitor, %-engaged store stats → KEEP → Explore"). Aggregates across
        // product zones (picker-driven, like the rest of Explore). Ratios, so
        // period-independent.
        const exploreProductVisitors = computed(() =>
            (topPriorities.value || []).reduce((a, z) => a + (z.visitors || 0), 0)
        )
        const exploreEngagedPct = computed(() => {
            const v = exploreProductVisitors.value
            if (!(v > 0)) return null
            const engaged = (topPriorities.value || []).reduce((a, z) => a + (z.visitors || 0) * (z.engagementRate || 0), 0)
            return engaged / v
        })
        const eurPerVisitorText = computed(() => {
            const v = exploreEurPerVisitor.value
            if (v == null) return ''
            return isEn() ? `€${v.toFixed(2)}` : formatEuro(v, { decimals: 2 })
        })
        const exploreEurPerVisitor = computed(() => {
            const v = storeTotalVisitors.value || exploreProductVisitors.value || 0
            if (!(v > 0)) return null
            const realized = (topPriorities.value || []).reduce(
                (a, z) => a + (z.visitors || 0) * (z.engagementRate || 0) * (z.conversionRate || 0) * (z.avgValue || 0), 0
            )
            return realized / v
        })

        // Actions' opportunities — computed from the STABLE 30-day snapshot, fully
        // decoupled from the picker (period 30 → €/mo scale = 1). Slicing Zones or
        // Explore never shifts Allen's suggestions. Falls back to the picker-driven
        // list only until the 30d snapshot has landed.
        const actionsPriorities = computed(() => {
            const d30 = props.fixedWidgetsData30d
            if (!d30 || !Object.keys(d30).length) return topPriorities.value
            return computePriorities(
                d30,
                null,
                companySector.value,
                zoneOverrides.value,
                d30?._cohort ?? null,
                30,
            )
        })

        // Per-zone strategic-role lookup from the Intelligence tab's
        // positioning composer, shaped for chip rendering ({icon,label,color}
        // per zone). Passed into TodayHero so the opportunity line shows
        // a strategic-role chip next to the zone name. Uses the company
        // sector + per-zone overrides so the composer reads the right
        // ceiling for each zone (including pharmacy-overrides etc.).
        const strategicByZone = computed(() => {
            const sp = composeStrategicPositioning(props.fixedWidgetsData, companySector.value, zoneOverrides.value)
            const out = {}
            for (const p of sp.points || []) {
                const meta = QUADRANT_META[p.quadrant]
                if (!meta) continue
                // legend + interp surfaced for the strategic-role pill
                // tooltip on the Prio cards (PrioritizationOpportunities).
                out[p.zone] = {
                    icon: meta.icon,
                    label: meta.label,
                    color: meta.color,
                    legend: meta.legend,
                    interp: meta.interp,
                }
            }
            return out
        })

        // Zones-overview side panel: group zones by strategic role so the space
        // beside the quadrant is a readable, actionable key (Stars / Cold Spots …).
        // QUADRANT_META labels are keys (QUADRANT_ORDER below); only the shown text is translated.
        const ROLE_KEY = {
            'Stars': 'stars',
            'High Runners': 'highRunners',
            'Spotlight Candidates': 'spotlight',
            'Cold Spots': 'coldSpots',
        }
        // PATTERN_META chips are English data; the role card shows them in the portal language.
        const PATTERN_CHIP_KEY = {
            'Engagement gap': 'engagementGap',
            'Conversion gap': 'conversionGap',
            'Reach gap': 'reachGap',
            'Mid-tier': 'midTier',
            'Performing well': 'performingWell',
        }
        const QUADRANT_ORDER = ['Stars', 'High Runners', 'Spotlight Candidates', 'Cold Spots']
        const zoneRolesGrouped = computed(() => {
            const pri = {}
            for (const p of topPriorities.value || []) pri[p.zone] = p
            const by = {}
            for (const [zone, m] of Object.entries(strategicByZone.value || {})) {
                const label = m.label || 'Other'
                const rk = ROLE_KEY[label]
                if (!by[label]) by[label] = {
                    label,
                    displayLabel: rk ? t(`explore.roles.${rk}.label`) : label,
                    color: m.color, icon: m.icon,
                    meaning: rk ? t(`explore.roles.${rk}.meaning`) : '',
                    zones: [],
                }
                const p = pri[zone] || {}
                const eur = Math.round(p.potentialImpact || 0)
                const meta = PATTERN_META[p.patternKind] || PATTERN_META.neutral
                by[label].zones.push({
                    name: zone,
                    visitors: Math.round(p.visitors || 0),
                    engPct: Math.round((p.engagementRate || 0) * 100),
                    convPct: Math.round((p.conversionRate || 0) * 100),
                    eurText: eur > 0 ? (isEn() ? `€${eur.toLocaleString()}` : formatEuro(eur)) : '',
                    patternChip: PATTERN_CHIP_KEY[meta.chip] ? t(`explore.pattern.${PATTERN_CHIP_KEY[meta.chip]}`) : meta.chip,
                    patternIcon: meta.icon,
                    patternColor: meta.color,
                    patternBg: meta.bg,
                })
            }
            return QUADRANT_ORDER.filter((l) => by[l]).map((l) => by[l])
        })
        // Clicking a role chip focuses that zone in the deep dive below.
        const zoneFocus = ref('')

        const estBadgeTooltip =
            'This zone\'s contribution to the chart is estimated from in/out crossings (no polygon measurement). See the zone\'s detail page for the underlying estimate.'

        // Phase 2 — per-zone-type sets of zones that contributed their dwell
        // via Mode C (Little's Law) estimation rather than polygon measurement.
        // The Data-tab bar lists use these to add an "(est.)" badge so users
        // can see at a glance which rows are estimates.
        const estimatedDwellZones = computed(() => {
            const d = props.fixedWidgetsData || {}
            const toSet = (arr) => new Set(Array.isArray(arr) ? arr : [])
            return {
                product: toSet(d.product_zones_with_estimated_dwell),
                demo:    toSet(d.demo_zones_with_estimated_dwell),
                display: toSet(d.display_zones_with_estimated_dwell),
                info:    toSet(d.info_zones_with_estimated_dwell),
                service: toSet(d.service_zones_with_estimated_dwell),
                pickup:  toSet(d.pick_up_zones_with_estimated_dwell),
            }
        })

        // Per-zone Little's-Law confidence ('high' | 'medium') so each row in
        // the Estimated Avg Dwell / Estimated Total Time charts can show its
        // own confidence chip. Empty dict when backend hasn't emitted yet.
        const estDwellConfidence = computed(() => {
            const d = props.fixedWidgetsData || {}
            const toObj = (v) => (v && typeof v === 'object') ? v : {}
            return {
                product: toObj(d.product_zones_dwell_confidence),
                demo:    toObj(d.demo_zones_dwell_confidence),
                display: toObj(d.display_zones_dwell_confidence),
                info:    toObj(d.info_zones_dwell_confidence),
                service: toObj(d.service_zones_dwell_confidence),
                pickup:  toObj(d.pick_up_zones_dwell_confidence),
            }
        })

        // Per-zone in/out detection bias (Phase 3). Emitted alongside the
        // confidence map. Surfaced as a separate "BIAS X%" chip and as a
        // tooltip on the confidence chip so users can see whether MEDIUM
        // confidence is driven by sample size or by a noisy tripwire.
        const estDwellBias = computed(() => {
            const d = props.fixedWidgetsData || {}
            const toObj = (v) => (v && typeof v === 'object') ? v : {}
            return {
                product: toObj(d.product_zones_dwell_bias_pct),
                demo:    toObj(d.demo_zones_dwell_bias_pct),
                display: toObj(d.display_zones_dwell_bias_pct),
                info:    toObj(d.info_zones_dwell_bias_pct),
                service: toObj(d.service_zones_dwell_bias_pct),
                pickup:  toObj(d.pick_up_zones_dwell_bias_pct),
            }
        })

        // Render config for a bias chip given a raw percentage (0-100 scale).
        // Returns null below 5 % (irrelevant), a 'mild' chip 5-10 %, a 'high'
        // chip ≥ 10 %. Bias > 20 % is impossible here because the backend
        // rejects the estimate at that threshold — the chip never renders.
        const biasChipForValue = (bias) => {
            const b = Number(bias ?? NaN)
            if (!Number.isFinite(b) || b < 5) return null
            const tier = b >= 10 ? 'high-bias' : 'mild-bias'
            return {
                label: t('explore.bias.chip', { pct: isEn() ? b.toFixed(0) : formatNumber(b) }),
                cls: ['conf-chip', `conf-${tier}`],
                title: b >= 10
                    ? `Tripwire detection bias ${b.toFixed(1)}%. The Brownian Bridge correction is compensating, but consider re-aiming the camera — the underlying signal is noisy.`
                    : `Tripwire detection bias ${b.toFixed(1)}%. Within acceptable range; minor calibration drift.`,
            }
        }
        const biasChipFor = (zoneType, zoneName) => {
            if (!zoneName) return null
            return biasChipForValue(estDwellBias.value?.[zoneType]?.[zoneName])
        }
        // Tooltip enricher for the confidence chip — adds bias context so a
        // MEDIUM-confidence chip self-explains when bias is the cause.
        const confTitleFor = (zoneType, zoneName, conf) => {
            if (!conf) return ''
            const bias = Number(estDwellBias.value?.[zoneType]?.[zoneName] ?? NaN)
            const base = `Confidence: ${conf}.`
            if (!Number.isFinite(bias)) return base
            if (bias < 5) return `${base} Tripwire bias ${bias.toFixed(1)}% — well within accuracy target.`
            return `${base} Tripwire bias ${bias.toFixed(1)}% — see the bias chip for guidance.`
        }

        /* ============================================================
         * Factory: per-zone-type polygon-vs-tripwire (Mode C) computeds.
         * Produces:
         *   - polygon-only Total Time dict + barlist rows + max scale
         *   - Mode C estimated visitors/avgDwell/totalTime arrays + max scales
         *   - polygon-derived best KPI cards (Top Avg Duration, Top Visitor Count)
         *   - top Mode C estimated avg dwell KPI
         *   - hasEstimated flag for v-if gating
         * Used by Product/Demo/Display (inline) and Info/Service/PickUp
         * (via _buildDwellZoneTypeComputeds).
         * ============================================================ */
        function _makeEstComputeds({
            typeKey,                 // 'product' | 'demo' | 'display' | 'info' | 'service' | 'pickup'
            polygonNamesRef,         // ComputedRef<Set<string>>
            visitorsCountsRef,       // Ref<{zone: count}>
            totalDurationRef,        // Ref<{zone: sec}>
            polygonAvgRowsRef,       // ComputedRef<[{zone, avgSec}]>
            polygonVisitorsRowsRef,  // ComputedRef<[{zone, visitors}]>
        }) {
            const totalDurationPerZonePoly = computed(() => {
                const src = totalDurationRef.value || {}
                const poly = polygonNamesRef.value
                if (!poly || !poly.size) return src
                return Object.fromEntries(
                    Object.entries(src).filter(([z]) => poly.has(z))
                )
            })
            const totalDurationRowsPoly = computed(() =>
                Object.entries(totalDurationPerZonePoly.value || {})
                    .map(([zone, totalSec]) => ({ zone, totalSec: Number(totalSec || 0) }))
                    .filter(r => r.totalSec > 0)
            )
            const maxTotalDurationPoly = computed(() =>
                Math.max(1, ...totalDurationRowsPoly.value.map(x => x.totalSec))
            )

            const estSet = computed(() => estimatedDwellZones.value?.[typeKey] || new Set())

            const estVisitorsByZone = computed(() => {
                const est = estSet.value
                if (!est.size) return []
                return [...est]
                    .map(z => ({ zone: z, visitors: Number(visitorsCountsRef.value?.[z] ?? 0) }))
                    .filter(r => r.visitors > 0)
            })
            const estAvgDwellByZone = computed(() => {
                const est = estSet.value
                if (!est.size) return []
                return [...est]
                    .map(z => {
                        const total = Number(totalDurationRef.value?.[z] ?? 0)
                        const vis = Number(visitorsCountsRef.value?.[z] ?? 0)
                        return { zone: z, avgSec: safeDiv(total, vis) }
                    })
                    .filter(r => r.avgSec > 0)
            })
            const estTotalTimeByZone = computed(() => {
                const est = estSet.value
                if (!est.size) return []
                return [...est]
                    .map(z => ({ zone: z, totalSec: Number(totalDurationRef.value?.[z] ?? 0) }))
                    .filter(r => r.totalSec > 0)
            })
            const maxEstVisitors  = computed(() => Math.max(1, ...estVisitorsByZone.value.map(x => x.visitors)))
            const maxEstAvgSec    = computed(() => Math.max(1, ...estAvgDwellByZone.value.map(x => x.avgSec)))
            const maxEstTotalTime = computed(() => Math.max(1, ...estTotalTimeByZone.value.map(x => x.totalSec)))
            const hasEstimated    = computed(() =>
                estVisitorsByZone.value.length > 0 || estAvgDwellByZone.value.length > 0
            )

            const bestByAvgDurationPoly = computed(() => {
                const rows = polygonAvgRowsRef.value
                if (!rows?.length) return null
                const top = [...rows].sort((a, b) => b.avgSec - a.avgSec || a.zone.localeCompare(b.zone))[0]
                return { zone_name: top.zone, avg_duration: top.avgSec }
            })
            const bestByVisitorsPoly = computed(() => {
                const rows = polygonVisitorsRowsRef.value
                if (!rows?.length) return null
                const top = [...rows].sort((a, b) => b.visitors - a.visitors || a.zone.localeCompare(b.zone))[0]
                return { zone_name: top.zone, visitor_count: top.visitors }
            })
            const bestByEstAvgDwell = computed(() => {
                const rows = estAvgDwellByZone.value
                if (!rows.length) return null
                const top = [...rows].sort((a, b) => b.avgSec - a.avgSec || a.zone.localeCompare(b.zone))[0]
                return { zone_name: top.zone, avg_duration: top.avgSec }
            })

            return {
                totalDurationPerZonePoly, totalDurationRowsPoly, maxTotalDurationPoly,
                estVisitorsByZone, estAvgDwellByZone, estTotalTimeByZone,
                maxEstVisitors, maxEstAvgSec, maxEstTotalTime, hasEstimated,
                bestByAvgDurationPoly, bestByVisitorsPoly, bestByEstAvgDwell,
            }
        }

        // The duration selector uses sentinel IDs for the named ranges (this
        // week / this month / last week / last month) — the raw value is not
        // a day count. Translate it to the actual number of days in the
        // resolved period so labels like "over X days" stay truthful.
        const daysForDuration = (d) => {
            const now = new Date()
            const daysInMonth = (year, monthIdx) => new Date(year, monthIdx + 1, 0).getDate()
            if (d === 1000) {
                // This week — Monday through today
                const dow = (now.getDay() + 6) % 7 // Mon=0 ... Sun=6
                return dow + 1
            }
            if (d === 2000) {
                // This month — 1st through today
                return now.getDate()
            }
            if (d === 3000) {
                // Last week — always 7 days
                return 7
            }
            if (d === 4000) {
                // Last month — calendar length of the previous month
                const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1
                const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
                return daysInMonth(lastMonthYear, lastMonth)
            }
            return Number(d) || 0
        }
        // Live picker → days: drives display copy ("over X days"). Follows the picker
        // instantly.
        const effectiveDurationDays = computed(() => daysForDuration(selectedDuration.value))

        // The service-response rollup is stored per slot, so a period change needs a
        // refetch rather than a client-side filter.
        watch(serviceResponseSlot, () => {
            loadServiceResponse(); loadCheckoutTransactions(); loadCounterUnattended()
        })
        // Data-aligned days: derived from the slot the CURRENT data was fetched for
        // (stamped by Home as _selectedDuration). Used for the €/mo scale so it can
        // never lead the still-stale data on a slot change → no €-flash.
        const dataPeriodDays = computed(() =>
            daysForDuration(props.fixedWidgetsData?._selectedDuration ?? 30)
        )

        // % lift of the best day in this period vs the daily average — a
        // motivating "you've already done this once" comparison.
        const bestDayLiftPct = computed(() => {
            const avg = Number(averageValue.value) || 0
            const peak = Number(highestTotalDayCount.value) || 0
            if (avg <= 0 || peak <= 0) return null
            return Math.round(((peak - avg) / avg) * 100)
        })

        const zoneStats = computed(() => {
            const zones = allZones.value || []
            // hasAnyTripwire is shape-tolerant: it returns true for any of the
            // three storage shapes (current {id, points} objects, bare-pair
            // groups, legacy single flat pair). Without this, a migrated
            // single-tripwire zone (whose stored value is now a 1-element list
            // of objects) would incorrectly count as "no tripwire" here.
            const hasTrip = z => hasAnyTripwire(z?.DetectionZoneTripwirePoints)
            const hasPoly = z => {
                const pts = _parsePoly(z?.DetectionZonePointArray)
                return Array.isArray(pts) && pts.length >= 3
            }
            const byType = (typeIds) => {
                const list = zones.filter(z => typeIds.includes(z.DetectionZoneTypeId))
                return {
                    polygon: list.filter(hasPoly).length,
                    tripwire: list.filter(hasTrip).length
                }
            }
            return {
                entrance: byType([1, 2, 3]),  // entrance + exit + combined EE
                waiting: byType([4]),
                interaction: byType([5]),
                product: byType([6]),
                demo: byType([7]),
                display: byType([8]),
                info: byType([9]),
                service: byType([10]),
                pickup: byType([11])
            }
        })

        /* ==========================================================
         * Zone-type navigator items
         * ======================================================== */
        // Explore is sub-tabbed: the zone-type pills switch which single section
        // shows (default Store), replacing the old collapsible accordions. Each pill
        // carries its zone/tripwire counts from zoneStats; pills only appear for
        // present zone types.
        const exploreSubTab = ref('sec-store-visitors')
        const zoneNavItems = computed(() => {
            const s = zoneStats.value
            return [
                { id: 'sec-store-visitors',       title: t('explore.section.storeVisitors'),       icon: 'bi-shop',                accent: '#36910d', tripwire: false, present: true, count: s.entrance },
                { id: 'sec-checkout-waiting',     title: t('explore.section.checkoutWaiting'),     icon: 'bi-hourglass-split',     accent: '#AD3935', tripwire: false, present: isCheckoutWaitingZonePresent(), count: s.waiting },
                { id: 'sec-checkout-interaction', title: t('explore.section.checkoutInteraction'), icon: 'bi-credit-card-2-front', accent: '#D3A741', tripwire: false, present: isCheckoutInteractionZonePresent(), count: s.interaction },
                { id: 'sec-product',              title: t('explore.section.product'),        icon: 'bi-tag',                 accent: '#004F6E', tripwire: hasTripwireProductZones.value, present: showProductSection.value, count: s.product },
                { id: 'sec-demo',                 title: t('explore.section.demo'),           icon: 'bi-easel',               accent: '#62B5D9', tripwire: hasTripwireDemoZones.value, present: showDemoSection.value, count: s.demo },
                { id: 'sec-display',              title: t('explore.section.display'),        icon: 'bi-image',               accent: '#110D93', tripwire: hasTripwireDisplayZones.value, present: showDisplaySection.value, count: s.display },
                { id: 'sec-info',                 title: t('explore.section.info'),           icon: 'bi-info-circle',         accent: '#0D9488', tripwire: hasTripwireInfoZones.value, present: showInfoSection.value, count: s.info },
                { id: 'sec-service',              title: t('explore.section.service'),        icon: 'bi-headset',             accent: '#D97706', tripwire: hasTripwireServiceZones.value, present: showServiceSection.value, count: s.service },
                { id: 'sec-pickup',               title: t('explore.section.pickup'),        icon: 'bi-bag-check',           accent: '#7E22CE', tripwire: hasTripwirePickUpZones.value, present: showPickUpSection.value, count: s.pickup },
                // Weather is a data view (not gated), but only appears for stores with a
                // configured weather location. Placed LAST.
                { id: 'sec-weather',              title: t('explore.section.weather'),              icon: 'bi-cloud-sun',           accent: '#1f9d9d', tripwire: false, present: weatherEnabled.value, count: null },
            ]
        })
        const activeZoneItem = computed(() => zoneNavItems.value.find(i => i.id === exploreSubTab.value) || null)

        // Today hero → tab navigation (and remember the focused zone for the
        // Intelligence redesign to read). When the hero requests
        // `scroll: 'bottom'` (the "+N more" chip), scroll the page to the
        // bottom of the deep-dive after the tab swap settles — that's where
        // the per-zone action library lives.
        const heroFocusedZone = ref(null)
        // Default the Zones focus to the top zone so the funnel AND the active role
        // card agree on first load (the grid that used to seed selection is retired).
        watch(topPriorities, (list) => {
            if (!zoneFocus.value && !heroFocusedZone.value && list && list.length) {
                zoneFocus.value = list[0].zone
            }
        }, { immediate: true })

        // Refine the docked Ask Allen's context per active tab (+ focused zone on
        // Zones), so its suggested prompts match what's on screen. These are just
        // opener chips — Allen still answers anything you type.
        watch([activeTab, zoneFocus, heroFocusedZone], ([tab, zf, hf]) => {
            if (tab === 'intelligence-layer') setAllenContext('zones', zf || hf || '')
            else if (tab === 'actions-layer') setAllenContext('actions')
            else setAllenContext('explore')
        }, { immediate: true })
        function onHeroNavigate({ tab, zone, scroll } = {}) {
            if (zone) heroFocusedZone.value = zone
            if (tab && tab !== activeTab.value) activeTab.value = tab
            if (scroll === 'bottom') {
                nextTick(() => {
                    // Two RAFs: one for tab swap, one for the deep-dive to
                    // render its picker + chart cells at the new height.
                    requestAnimationFrame(() => requestAnimationFrame(() => {
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    }))
                })
            }
        }

        /* ==========================================================
        * Trend & time formatting (computed)
        * ======================================================== */
        const formattedHighestTotalHour = computed(() => {
            if (!highestTotalHour.value) return { formattedDate: "", formattedTime: "" }

            const date = new Date(highestTotalHour.value)

            if (isNaN(date.getTime())) {
                console.error("Invalid Date Format:", highestTotalHour.value)
                return { formattedDate: "Invalid Date", formattedTime: "Invalid Time" }
            }

            // Extract date components
            const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
            const day = date.getDate()
            const month = date.toLocaleDateString('en-GB', { month: 'short' })

            // Dutch: "za 17 okt"
            const formattedDate = isEn() ? `${weekday}, ${day} ${month}`
                : `${formatWeekday(date, { short: true })} ${formatDate(date, { style: 'short' })}`
            
            // Extract time and compute the next hour
            const startHour = date.getHours()
            
            const endHour = (startHour + 1) % 24 // Ensure it stays within 24 hours

            const formattedTime = isEn() ? `${startHour}h-${endHour}h` : hourWindow(startHour, endHour)

            return {
                formattedDate,
                formattedTime
            }
        })

        const formattedHighestTotalDay = computed(() => {
    
            if (!highestTotalDay.value) return { formattedDate: ""}

            const date = new Date(highestTotalDay.value)

            if (isNaN(date.getTime())) {
                console.error("Invalid Date Format:", highestTotalDay.value)
                return { formattedDate: "Invalid Date"}
            }

            // Extract date components
            const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
            const day = date.getDate()
            const month = date.toLocaleDateString('en-GB', { month: 'short' })

            const formattedDate = isEn() ? `${weekday}, ${day} ${month}`
                : `${formatWeekday(date, { short: true })} ${formatDate(date, { style: 'short' })}`

            return {
                formattedDate
            }
        })

        const formattedBusiestHour = computed(() => {
            if (busiestHour.value === undefined || busiestHour.value === null) {
                return ""
            }

            const startHour = parseInt(busiestHour.value, 10)
            const endHour = (startHour + 1) % 24 // Calculate the end hour, wrapping around at 24

            return isEn() ? `${startHour}h-${endHour}h` : hourWindow(startHour, endHour)
        })

        // Dutch hour window, 24-hour clock: "15:00–16:00".
        function hourWindow(start, end) {
            const hh = (h) => `${String(h).padStart(2, '0')}:00`
            return `${hh(start)}–${hh(end)}`
        }

        // "Monday's Average Visitors" / "Gem. bezoekers op maandag". currentWeekdayString
        // stays the English day (0 = Monday); Dutch reads the day name from Intl.
        const weekdayAverageLabel = computed(() => {
            if (isEn()) return `${currentWeekdayString.value}'s Average Visitors`
            const i = currentWeekday.value
            if (!(i >= 0 && i <= 6)) return t('explore.kpi.weekdayAvgSame')
            // 1 January 2024 was a Monday.
            return t('explore.kpi.weekdayAvg', { day: formatWeekday(new Date(2024, 0, 1 + i)) })
        })

        const currentWeekdayString = computed(() => {
            if (currentWeekday.value >= 0 && currentWeekday.value <= 6) {
                const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                return daysOfWeek[currentWeekday.value]
            } else {
                return 'Same Weekday'
            }
        })

        const diffTodayAboveSameWeekdayAverage = computed(() => {
            let diff = averageValueSameWeekday.value - todaysCurrentCount.value - todaysCurrentCombinedCountStoreIn.value

            if (diff < 0) {
                diff = -1 * diff;
                return `+${diff}`
            } else if (diff > 0) {
                return `-${diff}`
            } else {
                return `${diff}`
            }
        })

        const diffTodayAboveAverage = computed(() => {
            let diff = averageValue.value - todaysCurrentCount.value - todaysCurrentCombinedCountStoreIn.value

            if (diff < 0) {
                diff = -1 * diff;
                return `+${diff}`
            } else if (diff > 0) {
                return `-${diff}`
            } else {
                return `${diff}`
            }
        })

        // map trend to icon/text/color
        const trendStyleMap = {
            strong_up:   { icon:'bi-arrow-up-square',   label:'explore.trend.strongUp',   color:'#2a6041' },
            mod_up:      { icon:'bi-arrow-up-square',   label:'explore.trend.up',         color:'#36910d' },
            slight_up:   { icon:'bi-arrow-up-square',   label:'explore.trend.slightUp',   color:'#2ceaa3' },
            stable:      { icon:'bi-arrow-right-square',label:'explore.trend.stable',     color:'#3b3b3b' },
            slight_down: { icon:'bi-arrow-down-square', label:'explore.trend.slightDown', color:'#a7333f' },
            mod_down:    { icon:'bi-arrow-down-square', label:'explore.trend.down',       color:'#C21500' },
            strong_down: { icon:'bi-arrow-down-square', label:'explore.trend.strongDown', color:'#8b0000' },
        }

        // The store trend arrives as a sentence. `trend_detail` (structured) is
        // preferred when the payload carries it — string-matching prose is how
        // this tile silently went blank the moment the backend wording changed.
        function parseTrendString(trendStr, detail) {
            if (!trendStr || typeof trendStr !== 'string') return null

            const lower = trendStr.toLowerCase()
            const significant = detail ? detail.is_significant !== false : null

            // % across the selected period. Suppressed when there is no
            // established trend, so the tile shows a dash rather than a figure
            // that reads as a finding — matching the zone-level widget.
            const m = trendStr.match(/([+-]?\d+(\.\d+)?)\s*%/)
            let pct = detail ? (detail.total_change_pct ?? null)
                             : (m ? Number(m[1]) : null)

            // 1) bucket by prefix phrase. Current vocabulary first; the older
            //    %-per-day wording still appears in exports taken before the
            //    trend estimator was rebuilt.
            let bucket = null
            const noTrend = lower.startsWith('no clear trend')
                || lower.startsWith('leaning ')
                || lower.startsWith('not enough days')
                || lower.startsWith('no meaningful trend')
            if (noTrend || significant === false) {
                bucket = 'stable'
                pct = null
            } else if (lower.startsWith('clear increase')) {
                bucket = Math.abs(pct ?? 0) >= 25 ? 'strong_up'
                       : Math.abs(pct ?? 0) >= 10 ? 'mod_up' : 'slight_up'
            } else if (lower.startsWith('clear decline')) {
                bucket = Math.abs(pct ?? 0) >= 25 ? 'strong_down'
                       : Math.abs(pct ?? 0) >= 10 ? 'mod_down' : 'slight_down'
            }
            else if (lower.startsWith('strong increase')) bucket = 'strong_up'
            else if (lower.startsWith('moderate increase')) bucket = 'mod_up'
            else if (lower.startsWith('slight upward')) bucket = 'slight_up'
            else if (lower.startsWith('stable')) { bucket = 'stable'; pct = null }
            else if (lower.startsWith('slight decline')) bucket = 'slight_down'
            else if (lower.startsWith('moderate decline')) bucket = 'mod_down'
            else if (lower.startsWith('strong decrease')) bucket = 'strong_down'

            // 2) style + compose final text (keep server’s sentence)
            const style = bucket ? trendStyleMap[bucket] : null

            return {
                bucket,                   // e.g. "mod_up"
                pct,                      // null when no trend was established
                sentence: trendStr,       // original server string
                icon: style?.icon || 'bi-activity',
                color: style?.color || '#6b7a86',
                short: bucket === 'stable' ? '—' : (style?.label ? t(style.label) : '—')
            }
        }

        const trendInfo = computed(() => parseTrendString(trend.value, trendDetail.value))

        /* ==========================================================
        * Methods: window + modal + background job
        * ======================================================== */

        const updateWindowWidth = () => {
            windowWidth.value = window.innerWidth;
        }

        // Map the active dashboard tab -> the grounded summary the button shows.
        const _tabForInsight = () => ({
            'data-foundation-layer': 'data',
            'intelligence-layer': 'intelligence',
        }[activeTab.value] || 'data')

        const toggleAllensInsights = async () => {
            showAllensInsights.value = !showAllensInsights.value
            if (showAllensInsights.value) {
                await fetchStoreAnalytics()
            }
            else if (currentFetchController) {
                currentFetchController.abort()
                jobStatus.value = null
            }
        }

        // Grounded, tab-aware: read the nightly-precomputed summary for the active
        // tab (instant; no enqueue/poll). Re-fetched when the tab changes while open.
        const fetchStoreAnalytics = async () => {
            storeAnalytics.value = ""
            jobStatus.value = "pending"
            if (currentFetchController) currentFetchController.abort()
            const controller = new AbortController()
            currentFetchController = controller
            const requestId = ++latestRequestId
            try {
                const resp = await getAllenInsight({
                    level: 'store',
                    tab: _tabForInsight(),
                    signal: controller.signal,
                })
                if (controller.signal.aborted || requestId !== latestRequestId) return
                if (resp.status === "complete") {
                    storeAnalytics.value = resp.result
                    jobStatus.value = "complete"
                } else if (resp.status === "unavailable") {
                    storeAnalytics.value = "<p>No insights available yet for this view. Allen's summaries are prepared overnight.</p>"
                    jobStatus.value = "complete"
                } else if (resp.status === "error") {
                    jobStatus.value = "error"
                    console.error("Allen insight error:", resp.error)
                }
            }
            catch (err) {
                if (err.name !== 'AbortError') console.error("Unexpected error in fetchStoreAnalytics:", err)
            }
        }

        /* ==========================================================
        * Methods: user interactions (duration / OH mode)
        * ======================================================== */
        const _setAllLoading = (val) => {
            isLoadingAverageSameWeekday.value =
            isLoadingAverage.value =
            isLoadingHighestHour.value =
            isLoadingHighestDay.value =
            isLoadingBusiestHour.value =
            isLoadingTrend.value =
            isLoadingbestProductZoneInCounts.value =
            isLoadingbestProductZoneInAvgDuration.value =
            isLoadingworstProductZoneInCounts.value =
            isLoadingbestDemoZoneInCounts.value =
            isLoadingbestDemoZoneInAvgDuration.value =
            isLoadingworstDemoZoneInCounts.value =
            isLoadingbestDisplayZoneInCounts.value =
            isLoadingbestDisplayZoneInAvgDuration.value =
            isLoadingworstDisplayZoneInCounts.value =
            isLoadingaverageWaitingTime.value =
            isLoadingaverageTimeInZoneWaiting.value =
            isLoadingmaxWaitingDuration.value =
            isLoadingmaxWaitingZoneName.value =
            isLoadingmostTypicalWaitingTime.value =
            isLoadingRealWaitRate.value =
            isLoadingaverageInteractionTime.value =
            isLoadingaverageTimeInZoneInteraction.value =
            isLoadingmaxInteractionDuration.value =
            isLoadingmaxInteractionZoneName.value =
            isLoadingmostTypicalInteractionTime.value =
            isLoadingRealTransactionRate.value =
            isLoadingfastestCheckoutZone.value =
            isLoadingslowestCheckoutZone.value =
            isLoadingfastestInteractionZone.value =
            isLoadingslowestInteractionZone.value =
            isLoadingmostUsedCheckout.value =
            isLoadingleastUsedCheckout.value =
            val
        }

        const handleUpdateDuration = async (newDuration) => {
            _setAllLoading(true)
            storeAnalytics.value = ''

            selectedDuration.value = newDuration
            emit('selectedDuration', newDuration)

            heatmapData.value = await getHeatmapByDuration(
                selectedDuration.value,
                selectedOpeningHoursMode.value
            )
            const entrance = await getEntranceChartDataByDuration(
                selectedDuration.value,
                selectedOpeningHoursMode.value
            )
            entranceChartData.value = entrance.entrance_chart_data

            const pdd = await getTopPDDChartData(selectedDuration.value, selectedOpeningHoursMode.value)
            bestProductAvgDur.value = pdd.pdd_chart_data.bestProductAvgDur ?? null
            bestProductCount.value = pdd.pdd_chart_data.bestProductCount ?? null
            bestDemoAvgDur.value = pdd.pdd_chart_data.bestDemoAvgDur ?? null
            bestDemoCount.value = pdd.pdd_chart_data.bestDemoCount ?? null
            bestDisplayAvgDur.value = pdd.pdd_chart_data.bestDisplayAvgDur ?? null
            bestDisplayCount.value = pdd.pdd_chart_data.bestDisplayCount ?? null
        }

        const handleUpdateOpeningHoursMode = async (newMode) => {
            _setAllLoading(true)
            storeAnalytics.value = ''

            selectedOpeningHoursMode.value = newMode
            emit('selectedOpeningHoursMode', newMode)

            heatmapData.value = await getHeatmapByDuration(
                selectedDuration.value,
                selectedOpeningHoursMode.value
            )
            const entrance = await getEntranceChartDataByDuration(
                selectedDuration.value,
                selectedOpeningHoursMode.value
            )
            entranceChartData.value = entrance.entrance_chart_data

            // Both of these are keyed by opening-hours mode in Redis, so they go stale
            // when the mode changes. checkout transactions was already missing here and
            // has been showing the previous mode's figures since it shipped.
            loadCheckoutTransactions()
            loadCounterUnattended()

            const pdd = await getTopPDDChartData(selectedDuration.value, selectedOpeningHoursMode.value)
            bestProductAvgDur.value = pdd.pdd_chart_data.bestProductAvgDur ?? null
            bestProductCount.value = pdd.pdd_chart_data.bestProductCount ?? null
            bestDemoAvgDur.value = pdd.pdd_chart_data.bestDemoAvgDur ?? null
            bestDemoCount.value = pdd.pdd_chart_data.bestDemoCount ?? null
            bestDisplayAvgDur.value = pdd.pdd_chart_data.bestDisplayAvgDur ?? null
            bestDisplayCount.value = pdd.pdd_chart_data.bestDisplayCount ?? null
        }

        const isCardEmpty = (...values) => {
            // Check if all values in the card are null, 0, empty string, or empty object
            return values.every(
                (val) =>
                    val === null ||
                    val === "null" ||
                    val === 0 ||
                    val === "0" ||
                    val === "" ||
                    (typeof val === "object" && Object.keys(val).length === 0)
            )
        }

        watch(activeTab, () => {
            // If Allen's Insights is open, re-fetch for the newly active tab.
            if (showAllensInsights.value) {
                fetchStoreAnalytics()
            }
        })

        /* ==========================================================
        * Lifecycle
        * ======================================================== */
        onMounted(async () => {
            isMounted.value = true

            loadWeatherInsight()   // fire-and-forget; surfaces self-hide until it resolves
            loadServiceResponse()  // same: the panels render nothing until it resolves
            loadCheckoutTransactions()
            loadCounterUnattended()

            // Deep-link from Today's gauge tap-in: ?tab=… opens that tab here.
            const _qtab = route.query && route.query.tab
            const _validTabs = new Set(['data-foundation-layer', 'intelligence-layer', 'actions-layer'])
            if (_qtab && _validTabs.has(_qtab)) {
                activeTab.value = _qtab
            }
            // Deep-link into a specific Explore sub-tab (e.g. a gauge tap: SERVE →
            // Checkout Waiting, DEPLOY → Checkout Interaction).
            const _qsub = route.query && route.query.sub
            if (_qsub && zoneNavItems.value.some(i => i.id === _qsub)) {
                exploreSubTab.value = _qsub
            }
            // Deep-link from Today's "Act on this →" or a notification: land on this
            // zone's card. A link carrying the zone's key (`zk`) finds it under its
            // current name, also when the link was made before a rename.
            const _q = route.query || {}
            if (_q.zone) focusZone.value = String(_q.zone)
            if (_q.zk) {
                const zoneDir = useZoneDirectoryStore()
                zoneDir.ensureLoaded(userStore.currentCompany?.CompanyId ?? null)
                    .then(() => {
                        const z = zoneFromLink(_q, zoneDir.nameOf)
                        if (z) focusZone.value = z
                    })
                    .catch(() => { /* keep the name from the link */ })
            }

            window.addEventListener('resize', updateWindowWidth)

            await nextTick()

            // Check opening hours config
            const isEmpty = await openingHoursStore.fetchOpeningHours()
            if (isEmpty) {
                selectedOpeningHoursMode.value = 'all'
                disabledOpeningHourModes.value = [
                'only_regular_opening_hours',
                'regular_and_irregular_opening_hours'
                ]
            }

            await detectionZoneStore.fetchDetectionZones()
            allZones.value = detectionZoneStore.detectionzones
            uniqueDetectionZoneTypeIds.value = [...new Set(detectionZoneStore.detectionzones.map(zone => zone.DetectionZoneTypeId))]
            productZoneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 6).length
            waitingZoneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 4).length
            interactionLaneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 5).length
            cashierLaneCount.value     = allZones.value.filter(z => z.DetectionZoneTypeId === 999).length
            // Info / Service / Pick Up zones — counted for the Service gauge footer.
            infoZoneCount.value    = allZones.value.filter(z => z.DetectionZoneTypeId === 9).length
            serviceZoneCount.value = allZones.value.filter(z => z.DetectionZoneTypeId === 10).length
            pickUpZoneCount.value  = allZones.value.filter(z => z.DetectionZoneTypeId === 11).length

            heatmapData.value = await getHeatmapByDuration(selectedDuration.value, selectedOpeningHoursMode.value)
            let entrance_chart_result = await getEntranceChartDataByDuration(selectedDuration.value, selectedOpeningHoursMode.value)
            entranceChartData.value = entrance_chart_result.entrance_chart_data
            let pddChartData = await getTopPDDChartData(selectedDuration.value, selectedOpeningHoursMode.value)
            bestProductAvgDur.value = pddChartData.pdd_chart_data.bestProductAvgDur ?? null
            bestProductCount.value = pddChartData.pdd_chart_data.bestProductCount ?? null
            bestDemoAvgDur.value = pddChartData.pdd_chart_data.bestDemoAvgDur ?? null
            bestDemoCount.value = pddChartData.pdd_chart_data.bestDemoCount ?? null
            bestDisplayAvgDur.value = pddChartData.pdd_chart_data.bestDisplayAvgDur ?? null
            bestDisplayCount.value = pddChartData.pdd_chart_data.bestDisplayCount?? null
        })

        onBeforeUnmount(() => {
            storeAnalytics.value = ""
            window.removeEventListener('resize', updateWindowWidth);
        })

        onUnmounted(() => {
            isMounted.value = false

            if (currentFetchController && !currentFetchController.signal.aborted) {
                currentFetchController.abort()
            }
        })

        /* ==========================================================
        * Watchers — map backend payload → UI state
        * ======================================================== */
        // store-level
        watch(() => props.fixedWidgetsData.current_weekday, (newValue) => {
            if (newValue !== undefined) {
                currentWeekday.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_same_weekday, (newValue) => {
            if (newValue !== undefined) {
                averageValueSameWeekday.value = Math.round(newValue)
                isLoadingAverageSameWeekday.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_visitors, (newValue) => {
            if (newValue !== undefined) {
                averageValue.value = Math.round(newValue)
                isLoadingAverage.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_hour, (newValue) => {
            if (newValue !== undefined) {
                highestTotalHour.value = newValue
                isLoadingHighestHour.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_hour_count, (newValue) => {
            if (newValue !== undefined) {
                highestTotalHourCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_day, (newValue) => {
            if (newValue !== undefined) {
                highestTotalDay.value = newValue
                isLoadingHighestDay.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.highest_total_day_count, (newValue) => {
            if (newValue !== undefined) {
                highestTotalDayCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.busiest_hour, (newValue) => {
            if (newValue !== undefined) {
                busiestHour.value = `${newValue}`
                isLoadingBusiestHour.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_on_busiest_hour, (newValue) => {
            if (newValue !== undefined) {
                if (Math.round(newValue) < 1) {
                    highestAverageHourlyCount.value = '#';
                } else {
                    highestAverageHourlyCount.value = Math.round(newValue);
                }
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.trend_detail, (newValue) => {
            trendDetail.value = newValue ?? null
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.trend, (newValue) => {
            if (newValue !== undefined) {
                trend.value = newValue
                isLoadingTrend.value = false
            }
        }, { deep: true, immediate: true })


        watch(() => props.fixedWidgetsData.store_dwell_estimate_seconds, (v) => {
            storeDwellEstimateSeconds.value = (v == null) ? null : Number(v)
        }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.store_dwell_confidence, (v) => {
            storeDwellConfidence.value = v || null
        }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.store_dwell_bias_pct, (v) => {
            storeDwellBiasPct.value = (v == null) ? null : Number(v)
        }, { deep: true, immediate: true })

        // product
        watch(() => props.fixedWidgetsData.best_product_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                bestProductZoneByVisitors.value = newValue
                isLoadingbestProductZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.best_product_zone_by_avg_duration, (newValue) => {
            if (newValue !== undefined) {
                bestProductZoneByAvgDuration.value = newValue
                isLoadingbestProductZoneInAvgDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.worst_product_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                worstProductZoneByVisitors.value = newValue
                isLoadingworstProductZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.total_duration_per_product_zone, (newValue) => {
            if (newValue !== undefined) {
                totalDurationPerProductZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_product_zone, (newValue) => {
            if (newValue !== undefined) {
                percentageByDurationRangeInProductZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_zone_visitors_counts, (newValue) => {
            if (newValue !== undefined) {
                productZoneVisitorsCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_zone_clear_lingerers_counts, (newValue) => {
            if (newValue !== undefined) {
                productZoneClearLingerersCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_hotcold_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                productHotcoldByVisitors.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_hotcold_by_clear, (newValue) => {
            if (newValue !== undefined) {
                productHotcoldByClearLingerers.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.product_zone_funnels, (newValue) => {
            if (newValue !== undefined) {
                productZoneFunnels.value = newValue
            }
        }, { deep: true, immediate: true })

        // demo
        watch(() => props.fixedWidgetsData.best_demo_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                bestDemoZoneByVisitors.value = newValue
                isLoadingbestDemoZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.best_demo_zone_by_avg_duration, (newValue) => {
            if (newValue !== undefined) {
                bestDemoZoneByAvgDuration.value = newValue
                isLoadingbestDemoZoneInAvgDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.worst_demo_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                worstDemoZoneByVisitors.value = newValue
                isLoadingworstDemoZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.total_duration_per_demo_zone, (newValue) => {
            if (newValue !== undefined) {
                totalDurationPerDemoZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_demo_zone, (newValue) => {
            if (newValue !== undefined) {
                percentageByDurationRangeInDemoZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_zone_visitors_counts, (newValue) => {
            if (newValue !== undefined) {
                demoZoneVisitorsCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_zone_clear_lingerers_counts, (newValue) => {
            if (newValue !== undefined) {
                demoZoneClearLingerersCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_hotcold_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                demoHotcoldByVisitors.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_hotcold_by_clear, (newValue) => {
            if (newValue !== undefined) {
                demoHotcoldByClearLingerers.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.demo_zone_funnels, (newValue) => {
            if (newValue !== undefined) {
                demoZoneFunnels.value = newValue
            }
        }, { deep: true, immediate: true })

        // display
        watch(() => props.fixedWidgetsData.best_display_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                bestDisplayZoneByVisitors.value = newValue
                isLoadingbestDisplayZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.best_display_zone_by_avg_duration, (newValue) => {
            if (newValue !== undefined) {
                bestDisplayZoneByAvgDuration.value = newValue
                isLoadingbestDisplayZoneInAvgDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.worst_display_zone_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                worstDisplayZoneByVisitors.value = newValue
                isLoadingworstDisplayZoneInCounts.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.total_duration_per_display_zone, (newValue) => {
            if (newValue !== undefined) {
                totalDurationPerDisplayZone.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_display_zone, (newValue) => {
            if (newValue !== undefined) {
                percentageByDurationRangeInDisplayZone.value = newValue
            }
        }, { deep: true, immediate: true })
        
        watch(() => props.fixedWidgetsData.display_zone_visitors_counts, (newValue) => {
            if (newValue !== undefined) {
                displayZoneVisitorsCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_zone_clear_lingerers_counts, (newValue) => {
            if (newValue !== undefined) {
                displayZoneClearLingerersCounts.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_hotcold_by_visitors, (newValue) => {
            if (newValue !== undefined) {
                displayHotcoldByVisitors.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_hotcold_by_clear, (newValue) => {
            if (newValue !== undefined) {
                displayHotcoldByClearLingerers.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.display_zone_funnels, (newValue) => {
            if (newValue !== undefined) {
                displayZoneFunnels.value = newValue
            }
        }, { deep: true, immediate: true })

        // ---- Tripwire crossings per dwell zone type (Phase 8.3.c) ----
        // Helper-style: each block of 5 watchers = one zone type.
        watch(() => props.fixedWidgetsData.best_product_zone_by_crossings, (v) => { if (v !== undefined) bestProductZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.lowest_product_zone_by_crossings, (v) => { if (v !== undefined) lowestProductZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.product_zone_crossings_counts, (v) => { if (v !== undefined) productZoneCrossingsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.product_zones_with_tripwire_total, (v) => { if (v !== undefined) productZonesWithTripwireTotal.value = v }, { immediate: true })
        watch(() => props.fixedWidgetsData.product_zones_with_polygon_total,  (v) => { if (v !== undefined) productZonesWithPolygonTotal.value  = v }, { immediate: true })

        watch(() => props.fixedWidgetsData.best_demo_zone_by_crossings, (v) => { if (v !== undefined) bestDemoZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.lowest_demo_zone_by_crossings, (v) => { if (v !== undefined) lowestDemoZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.demo_zone_crossings_counts, (v) => { if (v !== undefined) demoZoneCrossingsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.demo_zones_with_tripwire_total, (v) => { if (v !== undefined) demoZonesWithTripwireTotal.value = v }, { immediate: true })
        watch(() => props.fixedWidgetsData.demo_zones_with_polygon_total,  (v) => { if (v !== undefined) demoZonesWithPolygonTotal.value  = v }, { immediate: true })

        watch(() => props.fixedWidgetsData.best_display_zone_by_crossings, (v) => { if (v !== undefined) bestDisplayZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.lowest_display_zone_by_crossings, (v) => { if (v !== undefined) lowestDisplayZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.display_zone_crossings_counts, (v) => { if (v !== undefined) displayZoneCrossingsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.display_zones_with_tripwire_total, (v) => { if (v !== undefined) displayZonesWithTripwireTotal.value = v }, { immediate: true })
        watch(() => props.fixedWidgetsData.display_zones_with_polygon_total,  (v) => { if (v !== undefined) displayZonesWithPolygonTotal.value  = v }, { immediate: true })

        // ---- Info / Service / Pick Up zones (Phase 8.3.d) — polygon + tripwire ----
        // Info polygon
        watch(() => props.fixedWidgetsData.best_info_zone_by_visitors, (v) => { if (v !== undefined) bestInfoZoneByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.best_info_zone_by_avg_duration, (v) => { if (v !== undefined) bestInfoZoneByAvgDuration.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.worst_info_zone_by_visitors, (v) => { if (v !== undefined) worstInfoZoneByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.total_duration_per_info_zone, (v) => { if (v !== undefined) totalDurationPerInfoZone.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_info_zone, (v) => { if (v !== undefined) percentageByDurationRangeInInfoZone.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.info_zone_visitors_counts, (v) => { if (v !== undefined) infoZoneVisitorsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.info_zone_clear_lingerers_counts, (v) => { if (v !== undefined) infoZoneClearLingerersCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.info_hotcold_by_visitors, (v) => { if (v !== undefined) infoHotcoldByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.info_hotcold_by_clear, (v) => { if (v !== undefined) infoHotcoldByClearLingerers.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.info_zone_funnels, (v) => { if (v !== undefined) infoZoneFunnels.value = v }, { deep: true, immediate: true })
        // Info tripwire
        watch(() => props.fixedWidgetsData.best_info_zone_by_crossings, (v) => { if (v !== undefined) bestInfoZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.lowest_info_zone_by_crossings, (v) => { if (v !== undefined) lowestInfoZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.info_zone_crossings_counts, (v) => { if (v !== undefined) infoZoneCrossingsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.info_zones_with_tripwire_total, (v) => { if (v !== undefined) infoZonesWithTripwireTotal.value = v }, { immediate: true })
        watch(() => props.fixedWidgetsData.info_zones_with_polygon_total,  (v) => { if (v !== undefined) infoZonesWithPolygonTotal.value  = v }, { immediate: true })

        // Service polygon
        watch(() => props.fixedWidgetsData.best_service_zone_by_visitors, (v) => { if (v !== undefined) bestServiceZoneByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.best_service_zone_by_avg_duration, (v) => { if (v !== undefined) bestServiceZoneByAvgDuration.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.worst_service_zone_by_visitors, (v) => { if (v !== undefined) worstServiceZoneByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.total_duration_per_service_zone, (v) => { if (v !== undefined) totalDurationPerServiceZone.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_service_zone, (v) => { if (v !== undefined) percentageByDurationRangeInServiceZone.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.service_zone_visitors_counts, (v) => { if (v !== undefined) serviceZoneVisitorsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.service_zone_clear_lingerers_counts, (v) => { if (v !== undefined) serviceZoneClearLingerersCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.service_hotcold_by_visitors, (v) => { if (v !== undefined) serviceHotcoldByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.service_hotcold_by_clear, (v) => { if (v !== undefined) serviceHotcoldByClearLingerers.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.service_zone_funnels, (v) => { if (v !== undefined) serviceZoneFunnels.value = v }, { deep: true, immediate: true })
        // Service tripwire
        watch(() => props.fixedWidgetsData.best_service_zone_by_crossings, (v) => { if (v !== undefined) bestServiceZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.lowest_service_zone_by_crossings, (v) => { if (v !== undefined) lowestServiceZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.service_zone_crossings_counts, (v) => { if (v !== undefined) serviceZoneCrossingsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.service_zones_with_tripwire_total, (v) => { if (v !== undefined) serviceZonesWithTripwireTotal.value = v }, { immediate: true })
        watch(() => props.fixedWidgetsData.service_zones_with_polygon_total,  (v) => { if (v !== undefined) serviceZonesWithPolygonTotal.value  = v }, { immediate: true })

        // Pick Up polygon
        watch(() => props.fixedWidgetsData.best_pick_up_zone_by_visitors, (v) => { if (v !== undefined) bestPickUpZoneByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.best_pick_up_zone_by_avg_duration, (v) => { if (v !== undefined) bestPickUpZoneByAvgDuration.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.worst_pick_up_zone_by_visitors, (v) => { if (v !== undefined) worstPickUpZoneByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.total_duration_per_pick_up_zone, (v) => { if (v !== undefined) totalDurationPerPickUpZone.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.percentage_by_duration_range_in_pick_up_zone, (v) => { if (v !== undefined) percentageByDurationRangeInPickUpZone.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_zone_visitors_counts, (v) => { if (v !== undefined) pickUpZoneVisitorsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_zone_clear_lingerers_counts, (v) => { if (v !== undefined) pickUpZoneClearLingerersCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_hotcold_by_visitors, (v) => { if (v !== undefined) pickUpHotcoldByVisitors.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_hotcold_by_clear, (v) => { if (v !== undefined) pickUpHotcoldByClearLingerers.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_zone_funnels, (v) => { if (v !== undefined) pickUpZoneFunnels.value = v }, { deep: true, immediate: true })
        // Pick Up tripwire
        watch(() => props.fixedWidgetsData.best_pick_up_zone_by_crossings, (v) => { if (v !== undefined) bestPickUpZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.lowest_pick_up_zone_by_crossings, (v) => { if (v !== undefined) lowestPickUpZoneByCrossings.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_zone_crossings_counts, (v) => { if (v !== undefined) pickUpZoneCrossingsCounts.value = v }, { deep: true, immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_zones_with_tripwire_total, (v) => { if (v !== undefined) pickUpZonesWithTripwireTotal.value = v }, { immediate: true })
        watch(() => props.fixedWidgetsData.pick_up_zones_with_polygon_total,  (v) => { if (v !== undefined) pickUpZonesWithPolygonTotal.value  = v }, { immediate: true })

        // waiting / interaction
        watch(() => props.fixedWidgetsData.average_waiting_time, (newValue) => {
            if (newValue !== undefined) {
                averageWaitingTime.value = newValue
                isLoadingaverageWaitingTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_time_in_zone_waiting, (newValue) => {
            if (newValue !== undefined) {
                averageTimeInZoneWaiting.value = newValue
                isLoadingaverageTimeInZoneWaiting.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_waiting_duration, (newValue) => {
            if (newValue !== undefined) {
                maxWaitingDuration.value = newValue
                isLoadingmaxWaitingDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_waiting_zone, (newValue) => {
            if (newValue !== undefined) {
                maxWaitingZoneName.value = newValue
                isLoadingmaxWaitingZoneName.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.most_typical_waiting_time, (newValue) => {
            if (newValue !== undefined) {
                // Backend ships { value, count, low, high }. We only display
                // value + count; low/high are intentionally unused.
                if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                    mostTypicalWaitingTime.value = newValue.value
                    amountMostTypicalWaitingTime.value = newValue.count
                }
                else {
                    mostTypicalWaitingTime.value = null
                    amountMostTypicalWaitingTime.value = null
                }
                isLoadingmostTypicalWaitingTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_wait_rate, (newValue) => {
            if (newValue !== undefined) {
                realWaitRate.value = Number(newValue) || 0
                isLoadingRealWaitRate.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_wait_count, (newValue) => {
            if (newValue !== undefined) {
                realWaitCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.checkout_queue, (newValue) => {
            if (newValue !== undefined) {
                checkoutQueue.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_interaction_time, (newValue) => {
            if (newValue !== undefined) {
                averageInteractionTime.value = newValue
                isLoadingaverageInteractionTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.average_time_in_zone_interaction, (newValue) => {
            if (newValue !== undefined) {
                averageTimeInZoneInteraction.value = newValue
                isLoadingaverageTimeInZoneInteraction.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_interaction_duration, (newValue) => {
            if (newValue !== undefined) {
                maxInteractionDuration.value = newValue
                isLoadingmaxInteractionDuration.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.max_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                maxInteractionZoneName.value = newValue
                isLoadingmaxInteractionZoneName.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.most_typical_interaction_time, (newValue) => {
            if (newValue !== undefined) {
                if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                    mostTypicalInteractionTime.value = newValue.value
                    amountMostTypicalInteractionTime.value = newValue.count
                }
                else {
                    mostTypicalInteractionTime.value = null
                    amountMostTypicalInteractionTime.value = null
                }
                isLoadingmostTypicalInteractionTime.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_transaction_rate, (newValue) => {
            if (newValue !== undefined) {
                realTransactionRate.value = Number(newValue) || 0
                isLoadingRealTransactionRate.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.real_transaction_count, (newValue) => {
            if (newValue !== undefined) {
                realTransactionCount.value = newValue
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.fastest_checkout_zone, (newValue) => {
            if (newValue !== undefined) {
                fastestCheckoutZone.value = newValue
                isLoadingfastestCheckoutZone.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.slowest_checkout_zone, (newValue) => {
            if (newValue !== undefined) {
                slowestCheckoutZone.value = newValue
                isLoadingslowestCheckoutZone.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.fastest_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                fastestInteractionZone.value = newValue
                isLoadingfastestInteractionZone.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.slowest_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                slowestInteractionZone.value = newValue
                isLoadingslowestInteractionZone.value = false
            }
        }, { deep: true, immediate: true })


        watch(() => props.fixedWidgetsData.most_used_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                mostUsedCheckout.value = newValue
                isLoadingmostUsedCheckout.value = false
            }
        }, { deep: true, immediate: true })

        watch(() => props.fixedWidgetsData.least_used_interaction_zone, (newValue) => {
            if (newValue !== undefined) {
                leastUsedCheckout.value = newValue
                isLoadingleastUsedCheckout.value = false
            }
        }, { deep: true, immediate: true })

        return {
            trendText,
            movedIntoTypes, NOTICES, ZONE_TYPE_NAMES, entranceTypeChange, changesToday, settingChanged, thresholdChangedIn,
            serviceResponseByType,
            checkoutTransactions,
            counterUnattended,
            countersByService,
            checkoutUnattendedRate,
            serviceResponsePeriodLabel,
            // core
            activeTab,
            focusZone,
            // weather insight
            weatherSignature,
            weatherZones,
            weatherEnabled,
            storeName,
            canEditGuidance,
            guidanceRecommendations,
            selectedDuration,
            dateRangeMode,
            selectedOpeningHoursMode,
            disabledOpeningHourModes,
            handleUpdateDuration,
            handleUpdateOpeningHoursMode,

            // controls / modal
            storeAnalytics,
            showAllensInsights,
            toggleAllensInsights,

            // charts & data
            heatmapData,
            entranceChartData,


            // formatting & helpers
            fmt,
            fmtPct,
            pctRaw,
            confidenceLabel,
            eurPerVisitorText,
            weekdayAverageLabel,
            asMoney,
            fmtSec,
            formattedDurationTime,
            isCardEmpty,
            summarizeList,

            // store-level computed
            currentWeekdayString,
            todaysCurrentCount,
            todaysCurrentCombinedCountStoreIn,
            todaysCurrentCombinedCountStoreOut,
            todaysCurrentExit,
            averageVisitDuration,
            storeDwellEstimateSeconds,
            storeDwellConfidence,
            storeDwellBiasPct,
            averageValueSameWeekday,
            diffTodayAboveSameWeekdayAverage,
            averageValue,
            diffTodayAboveAverage,
            highestTotalHour,
            highestTotalHourCount,
            highestTotalDay,
            highestTotalDayCount,
            busiestHour,
            highestAverageHourlyCount,
            trend,
            trendInfo,
            formattedHighestTotalHour,
            formattedHighestTotalDay,
            formattedBusiestHour,

            // PDD datasets
            bestProductZoneByVisitors,
            bestProductZoneByAvgDuration,
            worstProductZoneByVisitors,
            totalDurationPerProductZone,
            percentageByDurationRangeInProductZone,
            productZoneVisitorsCounts,
            productZoneClearLingerersCounts,
            productHotcoldByVisitors,
            productHotcoldByClearLingerers,
            productZoneFunnels,
            productZoneVisitorsCountsFiltered,
            productZoneClearLingerersCountsFiltered,
            productHotcoldByVisitorsFiltered,
            productHotcoldByClearLingerersFiltered,
            productZoneFunnelsFiltered,
            storeTotalVisitors,
            storeDataIntegrity,

            bestDemoZoneByVisitors,
            bestDemoZoneByAvgDuration,
            worstDemoZoneByVisitors,
            totalDurationPerDemoZone,
            percentageByDurationRangeInDemoZone,

            bestDisplayZoneByVisitors,
            bestDisplayZoneByAvgDuration,
            worstDisplayZoneByVisitors,
            totalDurationPerDisplayZone,
            percentageByDurationRangeInDisplayZone,

            // PDD “top” charts
            bestProductAvgDur,
            bestProductCount,
            bestDemoAvgDur,
            bestDemoCount,
            bestDisplayAvgDur,
            bestDisplayCount,

            // PDD funnels / lists / coverage
            productZoneNames,
            visitorsByProductZone,
            avgDwellByProductZone,
            funnelsByProductZone,
            topClearLingerersProductZone,
            topConversionProductZone,
            topSalesPotentialProductZone,
            zoneCoverageTextProductZone,
            maxVisitorsProductZone,
            maxAvgSecProductZone,
            productZoneFunnelRows,
            highestEngagementZoneProductZone,
            bestVisitorsFromRowsProductZone,
            worstVisitorsFromRowsProductZone,
            zeroVisitorZonesProductZone,

            demoZoneNames,
            visitorsByDemoZone,
            avgDwellByDemoZone,
            funnelsByDemoZone,
            topClearLingerersDemoZone,
            topConversionDemoZone,
            topSalesPotentialDemoZone,
            zoneCoverageTextDemoZone,
            maxVisitorsDemoZone,
            maxAvgSecDemoZone,
            demoZoneFunnelRows,
            highestEngagementZoneDemoZone,
            bestVisitorsFromRowsDemoZone,
            worstVisitorsFromRowsDemoZone,
            zeroVisitorZonesDemoZone,

            displayZoneNames,
            visitorsByDisplayZone,
            avgDwellByDisplayZone,
            funnelsByDisplayZone,
            topClearLingerersDisplayZone,
            topConversionDisplayZone,
            topSalesPotentialDisplayZone,
            zoneCoverageTextDisplayZone,
            maxVisitorsDisplayZone,
            maxAvgSecDisplayZone,
            displayZoneFunnelRows,
            highestEngagementZoneDisplayZone,
            bestVisitorsFromRowsDisplayZone,
            worstVisitorsFromRowsDisplayZone,
            zeroVisitorZonesDisplayZone,
            estVisitorsByDisplayZone,
            estAvgDwellByDisplayZone,
            estTotalTimeByDisplayZone,
            maxEstVisitorsDisplayZone,
            maxEstAvgSecDisplayZone,
            maxEstTotalTimeDisplayZone,
            hasEstimatedDisplayZones,
            bestDisplayZoneByAvgDurationPoly,
            bestDisplayZoneByVisitorsPoly,
            bestDisplayZoneByEstAvgDwell,
            totalDurationPerDisplayZonePoly,
            totalDurationRowsDisplayZone,
            maxTotalDurationDisplayZone,
            estDwellConfidence,
            tripwireDwellMap,
            hasMeasurableProductZones,
            estDwellBias,
            biasChipFor,
            biasChipForValue,
            confTitleFor,

            // Mode C tripwire-side bindings (Product / Demo / Info / Service / PickUp)
            estVisitorsByProductZone, estAvgDwellByProductZone, estTotalTimeByProductZone,
            maxEstVisitorsProductZone, maxEstAvgSecProductZone, maxEstTotalTimeProductZone,
            hasEstimatedProductZones,
            bestProductZoneByAvgDurationPoly, bestProductZoneByVisitorsPoly, bestProductZoneByEstAvgDwell,
            totalDurationPerProductZonePoly, totalDurationRowsProductZone, maxTotalDurationProductZone,

            estVisitorsByDemoZone, estAvgDwellByDemoZone, estTotalTimeByDemoZone,
            maxEstVisitorsDemoZone, maxEstAvgSecDemoZone, maxEstTotalTimeDemoZone,
            hasEstimatedDemoZones,
            bestDemoZoneByAvgDurationPoly, bestDemoZoneByVisitorsPoly, bestDemoZoneByEstAvgDwell,
            totalDurationPerDemoZonePoly, totalDurationRowsDemoZone, maxTotalDurationDemoZone,

            estVisitorsByInfoZone, estAvgDwellByInfoZone, estTotalTimeByInfoZone,
            maxEstVisitorsInfoZone, maxEstAvgSecInfoZone, maxEstTotalTimeInfoZone,
            hasEstimatedInfoZones,
            bestInfoZoneByAvgDurationPoly, bestInfoZoneByVisitorsPoly, bestInfoZoneByEstAvgDwell,
            totalDurationPerInfoZonePoly, totalDurationRowsInfoZone, maxTotalDurationInfoZone,

            estVisitorsByServiceZone, estAvgDwellByServiceZone, estTotalTimeByServiceZone,
            maxEstVisitorsServiceZone, maxEstAvgSecServiceZone, maxEstTotalTimeServiceZone,
            hasEstimatedServiceZones,
            bestServiceZoneByAvgDurationPoly, bestServiceZoneByVisitorsPoly, bestServiceZoneByEstAvgDwell,
            totalDurationPerServiceZonePoly, totalDurationRowsServiceZone, maxTotalDurationServiceZone,

            estVisitorsByPickUpZone, estAvgDwellByPickUpZone, estTotalTimeByPickUpZone,
            maxEstVisitorsPickUpZone, maxEstAvgSecPickUpZone, maxEstTotalTimePickUpZone,
            hasEstimatedPickUpZones,
            bestPickUpZoneByAvgDurationPoly, bestPickUpZoneByVisitorsPoly, bestPickUpZoneByEstAvgDwell,
            totalDurationPerPickUpZonePoly, totalDurationRowsPickUpZone, maxTotalDurationPickUpZone,

            // tripwire crossings per dwell type (Phase 8.3.c)
            bestProductZoneByCrossings,
            lowestProductZoneByCrossings,
            crossingsByProductZone,
            maxCrossingsProductZone,
            zeroCrossingZonesProductZone,
            crossingCoverageTextProductZone,
            hasPolygonProductZones,
            hasTripwireProductZones,
            showProductSection,

            bestDemoZoneByCrossings,
            lowestDemoZoneByCrossings,
            crossingsByDemoZone,
            maxCrossingsDemoZone,
            zeroCrossingZonesDemoZone,
            crossingCoverageTextDemoZone,
            hasPolygonDemoZones,
            hasTripwireDemoZones,
            showDemoSection,

            bestDisplayZoneByCrossings,
            lowestDisplayZoneByCrossings,
            crossingsByDisplayZone,
            maxCrossingsDisplayZone,
            zeroCrossingZonesDisplayZone,
            crossingCoverageTextDisplayZone,
            hasPolygonDisplayZones,
            hasTripwireDisplayZones,
            showDisplaySection,

            // Phase 8.3.d — Info / Service / Pick Up
            isInfoZonePresent,
            isServiceZonePresent,
            isPickUpZonePresent,

            bestInfoZoneByVisitors, bestInfoZoneByAvgDuration,
            totalDurationPerInfoZone, percentageByDurationRangeInInfoZone,
            visitorsByInfoZone, avgDwellByInfoZone, maxVisitorsInfoZone, maxAvgSecInfoZone,
            topClearLingerersInfoZone, highestEngagementZoneInfoZone,
            zoneCoverageTextInfoZone, infoZoneFunnelRows,
            worstVisitorsFromRowsInfoZone, zeroVisitorZonesInfoZone,
            bestInfoZoneByCrossings, lowestInfoZoneByCrossings,
            crossingsByInfoZone, maxCrossingsInfoZone,
            zeroCrossingZonesInfoZone, crossingCoverageTextInfoZone,
            infoZonesWithTripwireTotal,
            hasPolygonInfoZones, hasTripwireInfoZones, showInfoSection,

            bestServiceZoneByVisitors, bestServiceZoneByAvgDuration,
            totalDurationPerServiceZone, percentageByDurationRangeInServiceZone,
            visitorsByServiceZone, avgDwellByServiceZone, maxVisitorsServiceZone, maxAvgSecServiceZone,
            topClearLingerersServiceZone, highestEngagementZoneServiceZone,
            zoneCoverageTextServiceZone, serviceZoneFunnelRows,
            worstVisitorsFromRowsServiceZone, zeroVisitorZonesServiceZone,
            bestServiceZoneByCrossings, lowestServiceZoneByCrossings,
            crossingsByServiceZone, maxCrossingsServiceZone,
            zeroCrossingZonesServiceZone, crossingCoverageTextServiceZone,
            serviceZonesWithTripwireTotal,
            hasPolygonServiceZones, hasTripwireServiceZones, showServiceSection,

            bestPickUpZoneByVisitors, bestPickUpZoneByAvgDuration,
            totalDurationPerPickUpZone, percentageByDurationRangeInPickUpZone,
            visitorsByPickUpZone, avgDwellByPickUpZone, maxVisitorsPickUpZone, maxAvgSecPickUpZone,
            topClearLingerersPickUpZone, highestEngagementZonePickUpZone,
            zoneCoverageTextPickUpZone, pickUpZoneFunnelRows,
            worstVisitorsFromRowsPickUpZone, zeroVisitorZonesPickUpZone,
            bestPickUpZoneByCrossings, lowestPickUpZoneByCrossings,
            crossingsByPickUpZone, maxCrossingsPickUpZone,
            zeroCrossingZonesPickUpZone, crossingCoverageTextPickUpZone,
            pickUpZonesWithTripwireTotal,
            hasPolygonPickUpZones, hasTripwirePickUpZones, showPickUpSection,

            // Explore sub-tabs
            zoneNavItems,
            exploreSubTab,
            activeZoneItem,
            zoneStats,
            topPriorities,
            actionsPriorities,
            exploreEngagedPct,
            exploreEurPerVisitor,
            zoneRolesGrouped,
            zoneFocus,
            companySector,
            zoneOverrides,
            companyStaffCost,
            strategicByZone,
            bestDayLiftPct,
            effectiveDurationDays,
            estimatedDwellZones,
            estBadgeTooltip,
            heroFocusedZone,
            onHeroNavigate,

            // waiting / interaction
            averageWaitingTime,
            averageTimeInZoneWaiting,
            maxWaitingDuration,
            maxWaitingZoneName,
            mostTypicalWaitingTime,
            amountMostTypicalWaitingTime,
            realWaitRate,
            realWaitCount,
            checkoutQueue,
            queuePct,
            queueVerdict,
            averageInteractionTime,
            averageTimeInZoneInteraction,
            maxInteractionDuration,
            maxInteractionZoneName,
            mostTypicalInteractionTime,
            amountMostTypicalInteractionTime,
            realTransactionRate,
            realTransactionCount,
            fastestCheckoutZone,
            slowestCheckoutZone,
            fastestInteractionZone,
            slowestInteractionZone,
            mostUsedCheckout,
            leastUsedCheckout,

            // loading flags
            isLoadingAverageSameWeekday,
            isLoadingAverage,
            isLoadingHighestHour,
            isLoadingHighestDay,
            isLoadingBusiestHour,
            isLoadingTrend,
            isLoadingbestProductZoneInCounts,
            isLoadingbestProductZoneInAvgDuration,
            isLoadingworstProductZoneInCounts,
            isLoadingbestDemoZoneInCounts,
            isLoadingbestDemoZoneInAvgDuration,
            isLoadingworstDemoZoneInCounts,
            isLoadingbestDisplayZoneInCounts,
            isLoadingbestDisplayZoneInAvgDuration,
            isLoadingworstDisplayZoneInCounts,
            isLoadingaverageWaitingTime,
            isLoadingaverageTimeInZoneWaiting,
            isLoadingmaxWaitingDuration,
            isLoadingmaxWaitingZoneName,
            isLoadingmostTypicalWaitingTime,
            isLoadingRealWaitRate,
            isLoadingaverageInteractionTime,
            isLoadingaverageTimeInZoneInteraction,
            isLoadingmaxInteractionDuration,
            isLoadingmaxInteractionZoneName,
            isLoadingmostTypicalInteractionTime,
            isLoadingRealTransactionRate,
            isLoadingfastestCheckoutZone,
            isLoadingslowestCheckoutZone,
            isLoadingfastestInteractionZone,
            isLoadingslowestInteractionZone,
            isLoadingmostUsedCheckout,
            isLoadingleastUsedCheckout,
            
            // presence flags
            isEntranceZonePresent,
            isExitZonePresent,
            isCombinedEntranceExitZonePresent,
            isCheckoutWaitingZonePresent,
            isCheckoutInteractionZonePresent,
            isProductZonePresent,
            isDemoZonePresent,
            isDisplayZonePresent,

            // lane counts
            productZoneCount,
            waitingZoneCount,
            interactionLaneCount,
            cashierLaneCount,
            serviceZoneCount,
            infoZoneCount,
            pickUpZoneCount,
        }
    }
}

</script>

<style scoped>
/* ============================================================
 SECTION SUMMARY (collapsed-section meta line)
============================================================ */
.sum-pill{
  display:inline-flex; align-items:center; gap:4px;
  padding:2px 8px;
  border-radius:999px;
  font-size:.7rem; font-weight:700; letter-spacing:.3px;
  text-transform:uppercase;
  line-height:1;
  white-space:nowrap;   /* keep "3 zones" on one line */
  flex:0 0 auto;        /* don't shrink — whole pill wraps as a unit instead */
}
.sum-pill i{ font-size:.72rem }

.sum-zones{
  color: var(--accent, #6b7a86);
  background: color-mix(in srgb, var(--accent, #6b7a86) 10%, #fff);
  border: 1px solid color-mix(in srgb, var(--accent, #6b7a86) 35%, #e6edf3);
}

.sum-tripwire{
  color: var(--accent, #6b7a86);
  background: color-mix(in srgb, var(--accent, #6b7a86) 10%, #fff);
  border: 1px dashed color-mix(in srgb, var(--accent, #6b7a86) 55%, #fff);
}

/* ============================================================
 SECTION EMPTY STATE (e.g. no entrance configured)
============================================================ */
.section-empty-state{
  display:flex;
  align-items:center;
  gap:14px;
  margin-top:16px;
  padding:18px 20px;
  background:#fff;
  border:1px dashed #d3dbe1;
  border-radius:12px;
  color:#425461;
}
.section-empty-state .empty-ico{
  font-size:1.6rem;
  color:#36910d;
  opacity:.85;
  flex-shrink:0;
}
.section-empty-state .empty-text{
  display:flex; flex-direction:column; gap:2px;
  line-height:1.35;
}
.section-empty-state .empty-text strong{ color:#1a2a36; font-weight:700 }
.section-empty-state .empty-text span{ color:#6b7a86; font-size:.9rem }

/* keep nav above the sticky tab/control headers without overlapping the page */
.data-foundation-pane{ position: relative; }

/* ===== Explore "at a glance" strip (Data → Explore, IA reorg) ===== */
/* Small per-sub-tab title above the charts (section headers are hidden). */
.explore-subtitle {
  display: flex; align-items: center; gap: 8px;
  margin: 12px 0 14px; font-size: 1.05rem; font-weight: 700; color: #2b3a46;
}
.explore-subtitle i { font-size: 1.1rem; }
.explore-glance {
  display: inline-flex;
  align-items: stretch;
  gap: 4px;
  margin: 2px 0 22px;
  padding: 12px 12px;
  background: #f7f9fc;
  border: 1px solid #e3ebf5;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(29, 43, 58, 0.05);
  max-width: 100%;
  flex-wrap: wrap;
}
.eg-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 20px;
  min-width: 92px;
}
.eg-tile + .eg-tile { border-left: 1px solid #e3ebf5; }
.eg-val {
  font-size: 20px;
  font-weight: 650;
  color: #3a4a57;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.eg-lbl {
  font-size: 11px;
  font-weight: 600;
  color: #7a8895;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
/* Explore zone-type headers: the global .section-head h5 is 1.6rem — too heavy
   for this dense reference list. Lighter here; other Section uses keep the size. */
.data-foundation-pane :deep(.section-head h5) {
  font-size: 1.18rem;
  font-weight: 600;
}

/* "vs your daily average" lift text on the Top Day card */
.best-day-lift{
  color: #2f6f3a;
  font-weight: 600;
  white-space: nowrap;
}

/* "(est.)" badge on store-level bar-list rows whose value came from Mode C
   (Little's Law) estimation rather than polygon measurement. Cursor goes to
   help so the title tooltip is discoverable. */
.est-badge{
  display: inline-block;
  margin-left: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  color: #9a6b00;
  letter-spacing: 0.2px;
  cursor: help;
}

/* Per-row confidence chip for Mode C dwell estimates. Mirrors the per-zone
   confidence emitted by the backend (high / medium). */
.conf-chip{
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.conf-chip.conf-high   { color: #15803d; background: #dcfce7; border-color: #86efac; }
.conf-chip.conf-medium { color: #b45309; background: #fef3c7; border-color: #fcd34d; }
/* Entrance data-outage badge on the Total Visitors card (partial-capture day). */
.integrity-badge{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 999px;
  padding: 1px 7px;
  cursor: help;
}
/* Bias chips (Phase 3). Visually distinct from confidence chips so a row
   carrying both stays scannable — the bias chip uses an unfilled, outlined
   treatment with red ink for high bias, slate ink for mild bias. */
.conf-chip.conf-mild-bias { color: #92400e; background: #fffbeb; border-color: #fde68a; }
.conf-chip.conf-high-bias { color: #b91c1c; background: #fee2e2; border-color: #fca5a5; }

/* ============================================================
 TREND CARD CONTENT
============================================================ */
.trend-short {
  font-weight: 700;
  font-size: 1.0rem;
  line-height: 1.1;
}
.trend-sentence {
  display: block;
  color: #6b7a86;
  font-size: .9rem;
  line-height: 1.25;
}

/* ============================================================
 BAR-LIST (Visitors/Average dwell lists)
============================================================ */
/* Single-grid layout so the label column auto-sizes to the widest label in
   the chart — single-zone charts get a tight label; charts with longer zone
   names or with the est conf-chip naturally take more label space without
   leaving whitespace in other charts. */
.barlist { display:grid; grid-template-columns: max-content 1fr auto; gap:10px; align-items:center; }
.barlist-row { display:contents; }
.barlist-label { color:#425461; white-space:nowrap; }
.barlist-row.with-chip .barlist-label { display:inline-flex; align-items:center; gap:6px; }
.barlist-row.with-chip .barlist-label .conf-chip { flex-shrink: 0; }
.barlist-bar { height:10px; background:#eef3f7; border-radius:999px; overflow:hidden; }
.barlist-fill { height:100%; background:#004F6E; border-radius:999px; opacity:.85; }
.barlist-value { font-variant-numeric: tabular-nums; color:#2b3a44; }

/* ============================================================
 FUNNEL TABLE
============================================================ */
.funnelz-table { display: grid; gap: 8px; }
.funnelz-row {
  display: grid;
  grid-template-columns: 1fr .6fr .8fr .9fr .9fr;
  align-items: center;
  gap: 10px;
  padding: .45rem .65rem;
  border: 1px solid #e6edf3;
  border-radius: 10px;
  background: #fff;
}
.funnelz-head { font-weight: 600; background: #f7fafc; }
.zone-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.num { text-align: right; font-variant-numeric: tabular-nums; }
.num small { color: #6b7a86; margin-left: 4px; }
.funnelz-row .merge-3 { grid-column: 3 / span 3; text-align: center; }
.funnelz-row .insufficient-msg { color: #6b7a86; font-style: italic; }

/* ============================================================
 STATE / UTILITY
============================================================ */
.card-disabled { opacity: 0.5; pointer-events: none; background-color: #ffffff; }
.group-disabled { border: 2px dashed #dcdcdc; background-color: #ffffff; opacity: 0.7; }

/* ============================================================
 MODAL (Allen’s insights)
============================================================ */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 2100;   /* above the nav sidebar (1100) so the modal isn't covered */
}
.modal-window {
  background: white; border-radius: 8px;
  /* no sidebar offset — the overlay centers it over the full viewport; min-width
     is fluid so it never exceeds a narrow screen (was margin-left:300px + min-width:400px). */
  min-width: min(400px, 92vw); max-width: 92vw; max-height: 90vh;
  overflow: auto; padding: 1rem; position: relative;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

/* ============================================================
 LOADER (overlay)
============================================================ */
.loader-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background-color: rgba(255, 255, 255, 0.8); z-index: 1000;
}
.loader {
  width: 20px; margin-top: 20px; padding: 2px; aspect-ratio: 1; border-radius: 50%;
  background: #B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m); mask: var(--_m);
  -webkit-mask-composite: source-out; mask-composite: subtract;
  animation: spinner 1s infinite linear;
}
@keyframes spinner { to { transform: rotate(1turn) } }

/* ============================================================
 RESPONSIVE (kept only where still referenced)
============================================================ */
@media (max-width: 1600px) {
  .charts-grid { grid-template-columns: 1fr; }
  .grid-container-product-data { grid-template-columns: 1fr; }
}
@media (max-width: 1500px) {
  .grid-container-product-data { grid-template-columns: 1fr; }
  .card-stack { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-right: 0; }
}
@media (max-width: 1400px) {
  .chart-container { flex-direction: column; gap: 1rem; }
  .chart-wrapper { flex: 1 1 100%; max-width: 100%; }
}
@media (max-width: 900px){
  .funnelz-row { grid-template-columns: 1.2fr .6fr .8fr .9fr .9fr; }
}
/* (.modal-window no longer offset — centered over the viewport at all widths) */
@media (max-width: 600px) {
  .grid-container-product-data { grid-template-columns: 1fr; }
  .card-stack { display: grid; grid-template-columns: 1fr; gap: 10px; margin-right: 0; }
}

/* Intelligence-tab two-chart row — StrategicPositioning + PatternMatrix
   side-by-side. Each chart component carries its OWN card shell
   (.sp-shell / .pm-shell), so this wrapper is just a flex layout —
   no duplicate background/border/padding. Below the wrap threshold
   (~976px row width) the two charts stack.
   margin-bottom: 24px matches the gap below ProductZoneDeepDive's
   top KPI strip ("€ per visitor (realized)" / "% of visitors
   engaged") so the visual rhythm between sections is consistent. */
.intel-charts-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 24px;
}
/* Zones overview: quadrant map (left) + role key (right) fills the space. */
.zones-overview {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(240px, 1fr);
  gap: 22px;
  align-items: start;
  margin-bottom: 24px;
}
.zov-chart { min-width: 0; }
.zov-roles {
  background: #fbfcfe; border: 1px solid #e8eef7; border-radius: 14px;
  padding: 14px 16px; display: flex; flex-direction: column; gap: 14px;
}
.zov-roles-title { font-size: 13px; font-weight: 800; color: #2b3a46; letter-spacing: 0.2px; }
.role-group { display: flex; flex-direction: column; gap: 5px; }
.role-head { font-size: 12.5px; font-weight: 800; display: inline-flex; align-items: center; gap: 6px; }
.role-ico { font-size: 13px; }
.role-count {
  margin-left: 4px; font-size: 11px; font-weight: 700; color: #6b7a87;
  background: #eef2f7; border-radius: 999px; padding: 0 7px; line-height: 1.5;
}
.role-meaning { font-size: 11.5px; color: #6b7a87; line-height: 1.4; }
.role-zones { display: flex; flex-direction: column; gap: 6px; margin-top: 3px; }
.role-zonerow {
  cursor: pointer; text-align: left; width: 100%;
  background: #fff; border: 1px solid #e8eef7; border-left: 3px solid #cbd6e6;
  border-radius: 8px; padding: 6px 10px;
  display: flex; flex-direction: column; gap: 2px;
  transition: background .15s ease, box-shadow .15s ease;
}
.role-zonerow:hover { background: #f4f8fd; box-shadow: 0 2px 6px rgba(13,38,76,.08); }
.role-zonerow:hover .rz-caret { color: #7a8895; transform: translateX(1px); }
/* Active = the zone whose funnel is showing below. */
.role-zonerow.active { background: #eef4fb; box-shadow: 0 2px 8px rgba(13,38,76,.10); }
.role-zonerow.active .rz-caret { color: #4F7EB3; }
.rz-top { display: flex; align-items: center; gap: 8px; }
.rz-name { font-size: 12.5px; font-weight: 700; color: #2b3a46; line-height: 1.3; flex: 1 1 auto; min-width: 0; }
.rz-eur { font-size: 12px; font-weight: 700; color: #2c6f58; font-variant-numeric: tabular-nums; flex: 0 0 auto; }
/* Chevron — the only affordance that the row opens a funnel (no extra copy). */
.rz-caret { font-size: 11px; color: #c4ccd6; flex: 0 0 auto; transition: color .15s ease, transform .15s ease; }
/* Funnel pattern pill — the diagnosis the retired grid cards used to carry. */
.rz-pattern { display: inline-flex; align-items: center; gap: 3px; align-self: flex-start; font-size: 10.5px; font-weight: 600; padding: 1px 7px; border-radius: 999px; line-height: 1.5; margin: 1px 0; }
.rz-stat { font-size: 11px; color: #7a8895; }
@media (max-width: 900px) {
  .zones-overview { grid-template-columns: 1fr; }
}
.intel-chart-block {
  flex: 1 1 480px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.intel-chart-block > * {
  width: 100%;
  /* fill the (stretched) block so both cards are the SAME height even when one
     chart's legend wraps to more lines / its caption wraps differently. */
  height: 100%;
  /* charts' own shells use margin-bottom: 24px; suppress here so the
     intel-charts-row's own margin-bottom is the single source of
     truth. */
  margin-bottom: 0;
}

/* Stack the two scatter charts below 1500px. The grow-to-fill stacking from a
   480px basis left the block width ambiguous during the wrap, so the chart
   didn't always resize into it (looked clipped). An explicit 100% basis gives
   a clean full width to resize into, and stacks at the width you want. */
@media (max-width: 1500px){
  .intel-chart-block { flex-basis: 100%; }
}
</style>